import React, { useState, useEffect, useCallback, useRef } from 'react';
import { comidasParaCompartir } from '../lib/comidasParaCompartir';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { suplementosPorComida } from '../lib/suplementosDelDia';
import { estadoDelDia as juzgarElDia, loQueFaltaAlDia as loQueFaltaDelDia } from '../lib/estadoDelDia';
import { pasoDeCantidad } from '../lib/pasoDeCantidad';
import { useOnboarding } from '../context/OnboardingContext';
import { leer as leerLocal, escribir as escribirLocal, borrar as borrarLocal } from '../lib/almacenLocal';
import { entraTalCual, unidadesEnteras } from '../lib/favoritaTalCual';
import { excesos, textoExceso, margenDe, MARGEN, NOMBRE_MACRO, MARGEN_QUE_SE_DICE, desajusteEnPalabras, desajusteEnPalabrasRedondo } from '../lib/exceso';
import { comidasARecuadrar } from '../lib/recuadreAlCruzar';
import { num1 } from '../lib/numeros';
import { cantidadConNombre } from '../lib/cantidadConNombre';
import { escalarMacros } from '../lib/escalarMacros';
import { cantidadConTope, AVISO_TOPE_GRASA_POST } from '../lib/topeGrasaPost';
import { tipoPorDefecto } from '../lib/tipoDelDia';
import { leerCantidad, avisoRazonable, avisoDelTope, AVISO_NO_ES_NUMERO, AVISO_NEGATIVO } from '../lib/cantidades';
import { useConfirm } from '../components/ui/confirm';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { hoyLocal } from '../lib/horaEspana';
import {
    Copy, FileDown, SlidersHorizontal, Star, Check, AlertCircle, Settings, UserCheck, X
} from 'lucide-react';
import { estaCerrado, cerrarAviso, reabrirAviso, huellaDelObjetivo } from '../lib/avisosCerrados';
import PreferencesSetup, { PREFERENCE_CATEGORIES } from '../components/nutrition/PreferencesSetup';
import NutritionIntro from '../components/nutrition/NutritionIntro';
import PrimeraDieta from '../components/nutrition/PrimeraDieta';
import BuildMealModal from '../components/nutrition/BuildMealModal';
import RepeatMealModal from '../components/nutrition/RepeatMealModal';
import CopyDietModal from '../components/nutrition/CopyDietModal';
import FavoritesModal from '../components/nutrition/FavoritesModal';
import FavoritasDeComida from '../components/nutrition/FavoritasDeComida';
import DayHeader from '../components/nutrition/DayHeader';
import MealCard, { MealSelectorItem, MealTab } from '../components/nutrition/MealCard';
import { VISTAS as VISTAS_DE_COMIDAS, VistaComidasSelector, leerVista, guardarVista } from '../components/nutrition/VistaComidas';
import MenuDeLaPantalla from '../components/nutrition/MenuDeLaPantalla';
import { resumenDeLaConfig } from '../components/nutrition/ConfigSection';
import LibraryMenusModal from '../components/nutrition/LibraryMenusModal';
import DietCalendar from '../components/nutrition/DietCalendar';
import DiaVacio from '../components/nutrition/DiaVacio';
import ExtrasDelDia from '../components/nutrition/ExtrasDelDia';
import { cabeceras, actuandoComoId } from '../lib/cabeceras';
import PostSugeridos from '../components/nutrition/PostSugeridos';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// LOS DOS AVISOS DE BIENVENIDA DE NUTRICION, APAGADOS (Francisco, 11-08-2026).
//
// Al entrar por primera vez salian encadenados «Tu primera dieta» (PrimeraDieta) y el tutorial
// de la pantalla (NutritionIntro), y tapaban justo lo que la persona venia a hacer. Es lo
// mismo que ya senalo Jesus el 11-08 con la captura de Nutricion: la unica que consiguio hacer
// fue la de la bienvenida, porque no dejaba ver nada detras.
//
// Un solo interruptor, y el codigo se queda entero: volver a encenderlo es poner true.
// Ojo, no confundir con el gate de PREFERENCIAS (showPreferencesSetup), que es otra cosa y
// sigue vivo: ese recoge los gustos, que el sugeridor usa de verdad.
const BIENVENIDA_NUTRICION = false;

// Peri options: intra_post/solo_post (Calma) + solo_intra/sin_peri (custom). Normalize stored
// values, defaulting unknown to intra_post.
const PERI_VALUES = ['intra_post', 'solo_post', 'solo_intra', 'sin_peri'];
const normPeri = (v) => (PERI_VALUES.includes(v) ? v : 'intra_post');
// El momento del entreno solo puede ser 0-3 (en ayunas / tras C1 / tras C2 / tras C3), pero
// 5.400 dias migrados de Calma traen un 5: el backend ya lo corrige a 1 al calcular, y aqui
// se hace lo mismo al LEER, porque con un 5 el peri se pintaba al final del dia, el resumen
// salia con un hueco («4 comidas ·  · intra + post») y el selector de horario en blanco.
const normMomento = (v) => (Number.isInteger(v) && v >= 0 && v <= 3 ? v : 1);

// Cuándo se guardó el día, en corto. Aparte del `formatDate` de la pantalla porque aquel dice
// «Hoy» para la fecha de hoy, y «te lo montó Francisco el Hoy» no se puede leer.
const fechaDeEdicion = (iso) => {
    const d = new Date(iso);
    return isNaN(d) ? '' : d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

// Aquí había otra copia del logo, con las letras en blanco fijo. No la pintaba nadie: esta
// pantalla no lleva logo. Fuera (14-08-2026).

// Food emojis for categories
const FOOD_EMOJIS = {
    '2': '🥩', '3': '🐟', '1': '🥚', '5': '🥛', '4': '💪',
    '7': '🌾', '8': '🍞', '21': '🍚', '22': '🍝', '9': '🥔',
    '10': '🫘', '11': '🍎', '13': '🥦', '17': '🫒', '17.2': '🥜',
    '16': '🥫', '24': '🥤', 'default': '🍽️'
};

const getFoodEmoji = (categorias) => {
    if (!categorias) return FOOD_EMOJIS.default;
    const mainCat = categorias.split(' | ')[0]?.split('.')[0];
    return FOOD_EMOJIS[mainCat] || FOOD_EMOJIS.default;
};


// Categories for Build Meal Modal - Step 1 (Proteínas) - Con prefixes para foods-sorted
const PROTEIN_CATEGORIES = [
    { id: 'huevos', label: 'Huevos', emoji: '🥚', prefixes: ['1.2'] },
    { id: 'claras', label: 'Claras', emoji: '🍳', prefixes: ['1.1'] },
    { id: 'embutidos', label: 'Embutidos', emoji: '🥓', prefixes: ['2.1'] },
    { id: 'aves', label: 'Aves', emoji: '🍗', prefixes: ['2.2'] },
    { id: 'vacuno', label: 'Vacuno', emoji: '🥩', prefixes: ['2.3'] },
    { id: 'cerdo', label: 'Cerdo', emoji: '🐷', prefixes: ['2.4'] },
    { id: 'otras_carnes', label: 'Otras carnes', emoji: '🍖', prefixes: ['2.5', '2.6', '2.7', '40', '45'] },
    { id: 'pescados', label: 'Pescados', emoji: '🐟', prefixes: ['3'] },
    { id: 'lacteos', label: 'Lácteos', emoji: '🧀', prefixes: ['5'] },
    { id: 'proteina_polvo', label: 'Proteína', emoji: '🥤', prefixes: ['4'] },
    { id: 'legumbres', label: 'Legumbres', emoji: '🫘', prefixes: ['10'] },
    { id: 'vegetal', label: 'Proteína vegetal', emoji: '🌱', prefixes: ['28', '6'] },
];

// Categories for Build Meal Modal - Step 2 (Acompañamientos)
const SIDE_CATEGORIES = [
    { id: 'arroces', label: 'Arroces', emoji: '🍚', prefixes: ['21'] },
    { id: 'panes', label: 'Panes', emoji: '🍞', prefixes: ['8'] },
    { id: 'cereales', label: 'Cereales', emoji: '🌾', prefixes: ['7'] },
    { id: 'pasta', label: 'Pasta', emoji: '🍝', prefixes: ['22'] },
    { id: 'tuberculos', label: 'Tubérculos', emoji: '🥔', prefixes: ['9'] },
    { id: 'fruta', label: 'Fruta', emoji: '🍎', prefixes: ['11'] },
    { id: 'verduras', label: 'Verduras', emoji: '🥬', prefixes: ['13'] },
    { id: 'legumbres', label: 'Legumbres', emoji: '🫘', prefixes: ['10'] },
    { id: 'lacteos', label: 'Lácteos', emoji: '🧀', prefixes: ['5'] },
    { id: 'bebidas', label: 'Bebidas', emoji: '🥤', prefixes: ['19', '24'] },
    { id: 'comida_prep', label: 'Comida prep.', emoji: '🍕', prefixes: ['32', '39', '49', '50', '51', '53'] },
    { id: 'dulces', label: 'Dulces', emoji: '🍫', prefixes: ['31', '34', '35', '36', '37', '43', '44', '47'] },
    { id: 'salsas', label: 'Salsas', emoji: '🥫', prefixes: ['16'] },
    { id: 'grasas', label: 'Grasas', emoji: '🫒', prefixes: ['17', '42'] },
    { id: 'sopas', label: 'Sopas', emoji: '🍲', prefixes: ['48'] },
];

// INTRA categories - only aminoacids and isotonic
const INTRA_CATEGORIES = [
    { id: 'aminoacidos', label: 'Aminoácidos', emoji: '⚡', prefixes: ['41'] },
    { id: 'isotonicas', label: 'Isotónicas', emoji: '💧', prefixes: ['18.1'] },
];

// POST Step 1 categories - protein powders
const POST_PROTEIN_CATEGORIES = [
    { id: 'whey', label: 'Whey', emoji: '💪', prefixes: ['4.1'] },
    { id: 'caseina', label: 'Caseína', emoji: '🥛', prefixes: ['4.2'] },
    { id: 'vegetal', label: 'Vegetal', emoji: '🌱', prefixes: ['4.3'] },
    { id: 'batido', label: 'Batido', emoji: '🥤', prefixes: ['5.4'] },
];

// POST Step 2 categories - fast carbs
const POST_CARB_CATEGORIES = [
    { id: 'fruta', label: 'Fruta', emoji: '🍎', prefixes: ['11'] },
    { id: 'crema_arroz', label: 'C. Arroz', emoji: '🍚', prefixes: ['21.3'] },
    { id: 'cereales', label: 'Cereales', emoji: '🌾', prefixes: ['7.1'] },
    { id: 'bebida', label: 'Bebida', emoji: '🥤', prefixes: ['24'] },
];

// La fecha de hoy con la que viajan las dietas (AAAA-MM-DD): EL RELOJ DEL CLIENTE
// (bloque F, 23-08, decisión de Francisco). Estuvo clavada a España para cuadrar con el
// servidor, pero eso le abría a un cliente en América el día de mañana. Ahora el servidor
// acepta la fecha del cliente en todos los caminos del día vivido (checkins, entreno,
// semana, montar-día), así que la cuenta única es la del navegador; España queda para
// los plazos y ventanas, que ya van con su «(h España)». Para quien vive aquí no cambia
// nada. OJO: nunca `toISOString()` -- ese es el día UTC, la trampa de siempre.
const hoyISO = () => hoyLocal();

// LO QUE FALTA O SOBRA, DICHO CON PALABRAS: se mudo a `lib/exceso` el 13-09-2026 para poder
// probarla (fichas 28 y 34 del parte de Gonzalo). Lo usan los dos «Cuadrar» -- el de una
// comida y el del día entero -- y el cartel de arriba de Nutrición, y ahí está escrito por
// qué el cartel de arriba va redondo y los de dentro de la comida con décima.

const NutritionPage = () => {
    // `user` hace falta para separar por cliente lo que se guarda en el navegador (punto
    // 4.7): la copia local del día se guardaba solo con la fecha, así que en un ordenador
    // compartido el siguiente que entrara se encontraba la dieta del anterior.
    const { token, user, logout, profile, refreshProfile } = useAuth();
    // En una referencia: `logout` es una función nueva en cada pintado del contexto, y puesta
    // en las dependencias de `api` haría repetir todos los efectos que dependen de él.
    const logoutRef = useRef(logout);
    logoutRef.current = logout;
    const uid = user?.id;
    const navigate = useNavigate();
    const { notify } = useOnboarding();
    // Para preguntar antes de hacer algo que no se puede deshacer (copiar sobre un día que
    // ya tiene dieta, borrar una favorita). El confirm del navegador bloquea la pestaña.
    const { confirm, elegir } = useConfirm();

    // Preferences state - for checking if user has configured preferences
    const [showPreferencesSetup, setShowPreferencesSetup] = useState(false);
    const [userPreferences, setUserPreferences] = useState([]);
    const [avoidedCategories, setAvoidedCategories] = useState([]);
    const [avoidedKeywords, setAvoidedKeywords] = useState([]);
    const [preferencesLoading, setPreferencesLoading] = useState(true);
    // La configuracion del dia (comidas / horario / peri) va plegada: se resume en una
    // linea de texto y solo se despliega cuando de verdad se quiere cambiar algo.
    const [configExpanded, setConfigExpanded] = useState(false);
    // La tuerca de «Comidas del día»: dentro va cómo ver las comidas.
    const [ajustesVistaAbierto, setAjustesVistaAbierto] = useState(false);

    // ── EL «DESHACER», DENTRO DE LA COMIDA (Jesús, 6-09-2026) ────────────────
    //
    // Su frase: «cuando aparece el botón de deshacer en las notificaciones no se puede
    // tocar; podríamos ponerlo en la pantalla de la comida donde hace la modificación, como
    // quitar o algo». Tenía razón en lo que describe: el aviso de abajo a la derecha se va
    // solo a los 8 o 9 segundos, y en el teléfono ese rato se le va en leerlo, así que el
    // botón estaba pero no llegaba a tiempo. Un deshacer que no se puede pulsar no es un
    // deshacer.
    //
    // Así que todo lo que se puede deshacer en una comida -- cuadrarla, vaciarla, quitar un
    // ingrediente escribiendo 0, y «Cuadrar el día», que deja el suyo en cada comida que haya
    // movido -- deja su vuelta atrás EN LA PROPIA TARJETA, sin prisa. Los avisos siguen
    // saliendo (dicen lo que ha pasado), pero ya no llevan botón: uno que no se puede tocar
    // es peor que ninguno.
    //
    // SE CAE SOLO EN CUANTO LA COMIDA CAMBIA, y eso es lo que lo hace seguro. Sin límite de
    // tiempo, un deshacer eterno acaba devolviendo unas cantidades de hace media hora y se
    // lleva por delante lo que el cliente haya puesto después. Por eso se guarda también la
    // huella de cómo quedó la comida: si lo que hay en pantalla ya no es eso, el enlace
    // desaparece y no hay nada que pisar.
    const [deshacerComida, setDeshacerComida] = useState({});

    // Los alimentos y sus cantidades, en una línea. Es la huella que dice si la comida sigue
    // como la dejó el cambio. `huellaDeLaComida`, más abajo, mira solo los ids y sirve para
    // otra cosa (la elección de de dónde bajar).
    const huellaConCantidades = (comida) => (comida?.alimentos || [])
        .map((a) => `${a.alimento_id}:${a.cantidad_g}`).join('|');

    // Y LAS DE TODAS LAS COMIDAS, para el «Deshacer» de «Cuadrar el día». Mismo criterio que
    // la de una comida: si lo que hay en pantalla ya no es lo que dejó el cuadre, el enlace se
    // cae. (`huellaDelDia`, más abajo, es otra cosa: la del guardado, que mira el día entero
    // y no solo las cantidades.)
    const cantidadesDelDia = (comidas) => getMealOrder()
        .map((k) => `${k}=${huellaConCantidades(comidas?.[k])}`).join('||');

    // EL «DESHACER» DEL DÍA, DE UNA SOLA VEZ (Francisco, 16-09): «si el cambio es global,
    // poder deshacer todos juntos, y no comida por comida».
    //
    // Esto REVIERTE la solución del 6-09, que repartía un «Deshacer» por cada tarjeta tocada.
    // Aquella nació de una queja buena de Jesús -- el botón del aviso flotante no se podía
    // tocar -- y resolvió eso, pero dejó el gesto del día descosido en seis: para volver
    // atrás de un cuadre que tocó cuatro comidas había que entrar en las cuatro. Un gesto,
    // una vuelta atrás. El de UNA comida sigue donde estaba, en su tarjeta.
    //
    // `{ antes, huella, cuantas }`: el día tal y como estaba, la huella de cómo quedó y
    // cuántas comidas se movieron, que es lo que se escribe en el enlace.
    const [deshacerElDia, setDeshacerElDia] = useState(null);

    // Como quiere ver las comidas del dia (lista y detalle, pestañas o todo seguido).
    // Se recuerda de un dia para otro; ver components/nutrition/VistaComidas.jsx.
    const [vistaComidas, setVistaComidas] = useState(leerVista);
    const cambiarVistaComidas = useCallback((v) => { guardarVista(v); setVistaComidas(v); }, []);

    // Macros del metodo o de la etiqueta. SOLO cambia lo que se enseña: el conteo, el
    // reparto y el estado de cada comida siguen saliendo de calculateMealMacros.
    // MÉTODO / REALES SE RETIRÓ (punto 112 del artifact del 25-08: «fuera, con Método /
    // Reales»). Era un conmutador informativo que cambiaba SOLO lo que ponía la lista de
    // ingredientes -- los totales, los objetivos y el estado de cada comida siempre han
    // salido de `macros_efectivos` --, y le costaba a la pantalla tres líneas: el propio
    // conmutador en dos sitios y un aviso explicando que lo que se ve no es lo que cuenta.
    // Ahora la lista enseña siempre los del método, que es lo que la app cuenta.

    // Intro guiado de primera visita. POR CLIENTE, no por dispositivo (punto 4.7): si un
    // cliente lo cierra en el ordenador de casa, el siguiente que entre ahí no debería
    // perderse el tutorial por algo que hizo otro.
    const [showIntro, setShowIntro] = useState(false);
    useEffect(() => {
        // APAGADO (Francisco, 11-08-2026): ver BIENVENIDA_NUTRICION arriba.
        if (!BIENVENIDA_NUTRICION) return;
        if (uid) setShowIntro(leerLocal('nutrition-intro-seen', uid) !== '1');
    }, [uid]);
    const dismissIntro = useCallback(() => {
        escribirLocal('nutrition-intro-seen', uid, '1');
        setShowIntro(false);
    }, [uid]);

    // Paso 4 del doc: la primera vez que viene a por su dieta se le piden los gustos (que es
    // cuando sirven de algo) y se le enseña como esta repartido su dia. Va antes que el tutorial:
    // primero se configura lo suyo, y despues se le explica la pantalla.
    const [primeraDieta, setPrimeraDieta] = useState(false);
    useEffect(() => {
        // APAGADO (Francisco, 11-08-2026): ver BIENVENIDA_NUTRICION arriba.
        if (!BIENVENIDA_NUTRICION) return;
        if (uid) setPrimeraDieta(leerLocal('primera-dieta-hecha', uid) !== '1');
    }, [uid]);
    const cerrarPrimeraDieta = useCallback(() => {
        escribirLocal('primera-dieta-hecha', uid, '1');
        setPrimeraDieta(false);
    }, [uid]);

    // Date & Config state
    //
    // La fecha arranca DE LA URL, no de hoy (QA 15-08). Con hoy de arranque habia una
    // carrera: el efecto que refleja la fecha en la URL corria con el valor inicial antes
    // de que el efecto que lee ?date= aterrizara su setState, reescribia ?date= a hoy, y
    // con el doble montaje de StrictMode la segunda pasada releia la URL ya machacada.
    // Resultado: ?date=2026-08-16 abria el 15, y recargar en un dia futuro lo perdia.
    const [currentDate, setCurrentDate] = useState(() => {
        const pedida = new URLSearchParams(window.location.search).get('date');
        return (pedida && /^\d{4}-\d{2}-\d{2}$/.test(pedida)) ? pedida : hoyISO();
    });
    const [tipoDia, setTipoDia] = useState('entrenamiento');
    const [numComidas, setNumComidas] = useState(4);
    const [momentoEntreno, setMomentoEntreno] = useState(1);
    const [opcionPeri, setOpcionPeri] = useState('intra_post');
    // La opción del peri de SU FICHA (la de la configuración, no la de hoy): con ella se sabe
    // si hoy ha dicho «No quiero intra» y hay que dejarle la tarjeta gris con «Ponerlo».
    const [opcionPeriFicha, setOpcionPeriFicha] = useState(null);
    // «¿Dejamos este intraentreno como favorito?» / «¿Lo dejamos así a partir de ahora?»
    // (Jesús, «El intra, paso a paso», 27-09). null o {tipo: 'nuevo'|'cambio', alimentos, antes}.
    const [preguntaPeriFav, setPreguntaPeriFav] = useState(null);
    const [postSugeridosAbierto, setPostSugeridosAbierto] = useState(false);

    // Favorites state (alimentos favoritos; UI oculta via FOOD_FAVORITES_UI, se conserva la logica)
    const [favorites, setFavorites] = useState(new Set());

    // Data state
    const [distribution, setDistribution] = useState(null);
    // Motivo por el que no hay reparto (p.ej. "No tienes macros asignados"): sin él,
    // los objetivos por comida se pintaban a 0 sin explicación.
    const [distribError, setDistribError] = useState(null);
    const [distribTargetsOverlay, setDistribTargetsOverlay] = useState(null);
    // La calibración del día la hace el backend; si esa llamada falla, los macros que quedan
    // en pantalla son los del conteo por alimento suelto, SIN el acumulado del día. Antes se
    // fallaba en silencio y esos números se guardaban y salían en el PDF como buenos.
    const [calibracionFallida, setCalibracionFallida] = useState(false);
    // Sube al pulsar "Reintentar": el efecto de calibración se dispara con la firma del día,
    // que al reintentar es la misma, así que hace falta algo que sí cambie.
    const [calibracionIntento, setCalibracionIntento] = useState(0);
    // Lo que lleva el día de cada familia calibrada y en qué tramo va. Sale de la misma
    // llamada de calibración (que ya lo devolvía y se tiraba), y es lo que se le enseña al
    // cliente: «frutos secos hoy: 15 de 20 g». Jesús, 13-08: que se vea desde el primer
    // gramo, no cuando ya ha cruzado.
    const [acumFamilias, setAcumFamilias] = useState(null);
    // El último tramo por el que se avisó, para no repetir el aviso en cada tecla. Es un
    // ref y no un estado a propósito: cambiarlo no tiene que repintar nada.
    const tramoAvisado = useRef({ cereal_pan: null, fruto_seco: null });
    // Un solo recuadre automático por familia y día (doc 57, F3): el refit puede devolver
    // el acumulado al tramo anterior y sin este candado la app entraría en un vaivén de
    // recuadres. La clave lleva la fecha, así que se limpia sola al cambiar de día.
    const recuadresHechos = useRef(new Set());
    // Calma comidaConMacrosVolcadas: the meal key that absorbs the day's remaining macros.
    // When set, every OTHER meal is locked (target = its served = cuadrada). null = no volcado.
    const [volcadoMeal, setVolcadoMeal] = useState(null);
    const [mealsData, setMealsData] = useState({});

    // Cambiar de día se lleva los «Deshacer» de las comidas: son de ESE día, y al volver
    // devolverían cantidades de otro. Va aquí y no arriba con su estado porque `currentDate`
    // se declara en medio, y leerlo antes revienta.
    useEffect(() => { setDeshacerComida({}); }, [currentDate]);
    // TODAS CERRADAS AL ENTRAR, EN EL TELÉFONO. Aquí se abría la Comida 1 sola, cada vez que
    // se recargaba o se volvía a la pantalla, y con ella se desplegaban su modo de cálculo,
    // su fila de macros y sus tres botones: unos 600 px antes de llegar a la Comida 2. Lo
    // primero que tiene que ver el cliente es el día entero, no una comida cualquiera
    // abierta por él. En escritorio se queda como estaba, con la primera abierta.
    const [expandedMeals, setExpandedMeals] = useState(
        () => (typeof window !== 'undefined' && window.innerWidth < 1024 ? {} : { C1: true }));
    const [selectedMeal, setSelectedMeal] = useState('C1');
    const [loading, setLoading] = useState(true);
    
    // Modal states
    const [menuOptionsModal, setMenuOptionsModal] = useState({ open: false, mealKey: null });
    const [copyModalOpen, setCopyModalOpen] = useState(false);
    const [favoritesModalOpen, setFavoritesModalOpen] = useState(false);
    const [dietFavorites, setDietFavorites] = useState([]);
    // Favoritas de UNA comida (25-08). Van aparte de las de día: son otra cosa y se
    // piden al servidor con ?ambito=comida.
    const [favComidaModal, setFavComidaModal] = useState({ open: false, mealKey: null });
    const [comidaFavoritas, setComidaFavoritas] = useState([]);
    // COMPARTIR UNA COMIDA (26-09): las firmas de lo que ya compartió, la pregunta de si
    // autoriza (sale la primera vez que pulsa) y el menú del ranking que viene a ponerse en
    // una comida (`?menu=<id>`, el «Usar en una comida» de su ficha).
    const [compartidas, setCompartidas] = useState(() => new Set());
    const [menuParaUsar, setMenuParaUsar] = useState(null);
    const [copyDate, setCopyDate] = useState('');
    const [buildMealModal, setBuildMealModal] = useState({ open: false, mealKey: null });
    const [repeatMealModal, setRepeatMealModal] = useState({ open: false, mealKey: null });
    const [recentDiets, setRecentDiets] = useState([]);
    const [selectedDietForRepeat, setSelectedDietForRepeat] = useState(null);
    const [editingQuantity, setEditingQuantity] = useState({ mealKey: null, foodIndex: null });
    
    // Search state
    
    // Menu options
    
    // Summary expanded state
    const [summaryExpanded, setSummaryExpanded] = useState(false);
    
    // ¿NADIE HA DICHO SI ESTE DÍA ES DE ENTRENO? (punto 4.17)
    //
    // La app abre todos los días en «Entreno», y eso no es un valor por defecto cualquiera:
    // en el cliente que miró Jesús son 60 g de hidratos y 45 de perientreno de más un domingo.
    //
    // Y no es un despiste de alguno. Medido en producción el 09-08 sobre las 14.027 dietas
    // guardadas: **14.025 dicen «entrenamiento» y 2 dicen «descanso»**. Prácticamente nadie
    // lo marca nunca, así que casi todo el mundo come de día de entreno todos los días.
    //
    // Tampoco se puede deducir: `training_days` lo tienen 4 clientes de 174 y además es un
    // número (cuántos días, no cuáles), `nivel1.dias_entreno` lo tiene 1, y clientes activos
    // con rutina hay 0. No existe el dato en ninguna parte.
    //
    // Lo que sí se puede arreglar hoy es que el supuesto deje de ser invisible: si el día no
    // lo ha marcado nadie, se dice y se le pide que elija. Cuál debe ser la regla automática
    // es una decisión de Jesús, y con 2 de 14.027 delante se toma mejor.
    const [diaSinMarcar, setDiaSinMarcar] = useState(false);
    // Y su ref, para el detector de tipo de día: esa petición vuelve cuando le llega el
    // turno y necesita el valor de AHORA, no el que había cuando se lanzó.
    const diaSinMarcarRef = useRef(false);
    diaSinMarcarRef.current = diaSinMarcar;

    // EL DÍA VACÍO (doc 21-08, tarea 6.1). Cuando el día no tiene ninguna comida con
    // alimentos, en lugar de la parrilla sale una pregunta con tres salidas (DiaVacio).
    // «Crear el día» guarda aquí LA FECHA para la que se pidió la parrilla: así al
    // cambiar de día la pregunta vuelve, y si el cliente vacía un día que estaba
    // montando no se le echa de la parrilla a mitad de faena.
    const [diaEnCreacion, setDiaEnCreacion] = useState(null);
    // La lista de días recientes es la misma que usa el modal de repetir comida
    // (recentDiets, /diets/recent); esto solo marca que la está cargando el día vacío.
    const [cargandoRecientes, setCargandoRecientes] = useState(false);

    // Calendar state
    const [calendarOpen, setCalendarOpen] = useState(false);
    
    // PDF export state
    const [exportingPdf, setExportingPdf] = useState(false);

    // API helper
    //
    // LAS CABECERAS SALEN DE `lib/cabeceras`, NO DE AQUÍ (punto 2 del 17-08). Esto ponía el
    // token a mano y se dejaba `X-Actuar-Como`, así que cuando el entrenador entraba en la
    // calculadora de un cliente TODA esta pantalla trabajaba como el entrenador: el reparto
    // devolvía los macros del admin, la dieta que se cargaba era la del admin y lo que se
    // guardaba se guardaba encima de su propio día. Con el cartel naranja de «Estás en la
    // cuenta de Cliente Demo» arriba.
    const api = useCallback(async (endpoint, options = {}) => {
        const res = await fetch(`${API_URL}${endpoint}`, {
            ...options,
            headers: cabeceras(token, {
                'Content-Type': 'application/json',
                ...options.headers
            })
        });
        if (!res.ok) {
            const error = await res.json().catch(() => ({ detail: 'Error de red' }));
            // LA SESIÓN CADUCADA, COMO EN EL RESTO DE LA APP (23-09). Nutrición llama al servidor
            // por su cuenta y no pasaba por el manejo general de `AuthContext`: con el token
            // caducado (dura 24 h) cada «Guardar» devolvía 401 y la pantalla decía «Error
            // guardando preferencias», sin más. Le pasó a Jesús cuatro veces seguidas. Ahora lo
            // dice y lleva a entrar otra vez, igual que las demás pantallas.
            if (res.status === 401 && !endpoint.includes('/auth/')) {
                toast.error('Tu sesión ha expirado. Vuelve a iniciar sesión.', { id: 'session-expired' });
                logoutRef.current();
            }
            const fallo = new Error(error.detail || 'Error');
            fallo.status = res.status;   // para que quien llama no repita el aviso de la sesión
            throw fallo;
        }
        return res.json();
    }, [token]);
    
    // Check user preferences on load
    useEffect(() => {
        const checkPreferences = async () => {
            try {
                const res = await api('/api/user/preferences');
                if (!res.has_preferences) {
                    setShowPreferencesSetup(true);
                } else {
                    setUserPreferences(res.food_preferences);
                    setAvoidedCategories(res.avoided_categories || []);
                    setAvoidedKeywords(res.avoided_keywords || []);
                }
            } catch (err) {
                console.error('Error checking preferences:', err);
            } finally {
                setPreferencesLoading(false);
            }
        };
        checkPreferences();
    }, [api]);

    // LAS PREFERENCIAS, ANTES QUE EL RECORRIDO (Jesús, reunión del 25-09, cuenta de Nacho
    // Santandreu: «me salta de repente las preferencias como la segunda pantalla del recorrido
    // y no me deja»). Sin preferencias, esta página se cambia entera por «Configura tus
    // preferencias», y el recorrido preguntaba y buscaba sus paradas por encima. Aquí se dice
    // en qué punto están (`cargando` | `pendientes` | `listas`) y el recorrido espera a `listas`
    // (context/OnboardingContext). Al guardarlas, pasa a `listas` y el recorrido arranca solo.
    useEffect(() => {
        const estado = preferencesLoading ? 'cargando' : (showPreferencesSetup ? 'pendientes' : 'listas');
        document.body.dataset.preferencias = estado;
        window.dispatchEvent(new Event('preferencias-cambian'));
    }, [preferencesLoading, showPreferencesSetup]);
    useEffect(() => () => {
        delete document.body.dataset.preferencias;
        window.dispatchEvent(new Event('preferencias-cambian'));
    }, []);

    // Load favorites on mount
    useEffect(() => {
        const loadFavorites = async () => {
            try {
                const res = await api('/api/favorites');
                setFavorites(new Set((res.favorites || []).map(String)));
            } catch (e) { /* ignore */ }
        };
        loadFavorites();
    }, [api]);

    const toggleFavorite = async (foodId) => {
        const fid = Number(foodId);
        const isFav = favorites.has(String(foodId));
        try {
            if (isFav) {
                await api(`/api/favorites/${fid}`, { method: 'DELETE' });
                setFavorites(prev => { const s = new Set(prev); s.delete(String(foodId)); return s; });
            } else {
                await api(`/api/favorites/${fid}`, { method: 'POST' });
                setFavorites(prev => new Set(prev).add(String(foodId)));
            }
        } catch (e) { /* ignore */ }
    };

    // Handle preferences saved
    const handlePreferencesSaved = (preferences, avoidedCats, avoidedKws) => {
        setUserPreferences(preferences);
        setAvoidedCategories(avoidedCats || []);
        setAvoidedKeywords(avoidedKws || []);
        setShowPreferencesSetup(false);
    };

    // El tipo de día que propone su rutina: solo para el día que nadie ha configurado.
    //
    // Y SOLO PARA ÉSE (1-09-2026). Esto se lo ponía a todos, incluidos los días que ya
    // estaban guardados con su tipo, así que la rutina pisaba una decisión tomada: abrir un
    // martes de agosto que se montó como entreno lo pasaba a descanso porque hoy la rutina
    // dice que los martes se descansa, y el día quedaba cambiado por haberlo mirado. La
    // regla ya estaba escrita en `loadDiet` («Este día ya lo configuró alguien: su tipo es
    // una decisión, no un supuesto»); lo que faltaba era respetarla aquí.
    useEffect(() => {
        // DESPUÉS DE CARGAR, NO A LA VEZ. Corría en paralelo a la carga del día, así que
        // cuando volvía no se sabía todavía si ese día estaba configurado o no.
        if (loading) return;
        const detectDayType = async () => {
            try {
                const routine = await api('/api/routines/current');
                if (routine && routine.days) {
                    const dateObj = new Date(currentDate + 'T12:00:00');
                    const dayName = dateObj.toLocaleDateString('es-ES', { weekday: 'long' }).toLowerCase();
                    const dayData = routine.days.find(d => d.day.toLowerCase() === dayName);
                    // `diaSinMarcarRef` y no el estado: esta petición vuelve cuando quiere, y
                    // para entonces el día ya ha cargado y el valor del cierre estaría viejo.
                    if (dayData && diaSinMarcarRef.current) {
                        setTipoDia(dayData.is_rest ? 'descanso' : 'entrenamiento');
                    }
                }
            } catch (err) {
                // No routine assigned, keep default
            }
        };
        detectDayType();
    }, [currentDate, loading]); // eslint-disable-line

    // Export diet to PDF
    const exportPdf = async () => {
        setExportingPdf(true);
        try {
            // El PDF se genera en el servidor a partir de la dieta GUARDADA, no de lo que hay en
            // pantalla. Sin esto, montar el dia y pulsar PDF sin salir antes de la pantalla daba
            // siempre "No hay dieta guardada para este dia" (404).
            await flushGuardado();
            // LAS CABECERAS, DE `lib/cabeceras` (punto 2 del 17-08). Esta llamada se montaba
            // el `Authorization` a mano y se dejaba `X-Actuar-Como`, así que el entrenador
            // que entraba en la calculadora de un cliente y pulsaba PDF se descargaba SU
            // PROPIO día con el nombre del día del cliente. Es el mismo fallo que se cerró
            // el 17-08 con dos sitios que se quedaron fuera; este era uno.
            const res = await fetch(`${API_URL}/api/diets/${currentDate}/pdf`, {
                headers: cabeceras(token),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Error generando PDF');
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `dieta_jg12_${currentDate}.pdf`;
            // EL ENLACE, EN EL DOCUMENTO, Y LA URL SE SUELTA DESPUÉS (1-09-2026).
            //
            // `revokeObjectURL` iba en la misma vuelta que el `click()`, o sea que se le
            // quitaban los datos al navegador mientras todavía los estaba leyendo: el aviso
            // decía «PDF descargado» y en Descargas quedaba un `.crdownload` de 6.768 bytes
            // que no se cerraba nunca. El contenido llegaba entero desde el servidor; lo que
            // no llegaba a existir era el fichero.
            //
            // Se suelta en el siguiente turno del navegador, cuando la descarga ya ha
            // arrancado. Y el enlace se mete en el documento antes de pulsarlo, que es lo
            // que hace que la descarga funcione igual en todos los navegadores.
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
            // «PDF» TAMPOCO SE DICE AQUI (ficha 05 del 5-09). El boton ya se llama «Ver
            // dieta»; dejar la palabra en el aviso era cambiarle el rotulo y seguir
            // diciendosela un segundo despues. Lo que le importa al cliente es que tiene su
            // dieta, no en que formato viaja.
            toast.success('Tu dieta, descargada');
        } catch (err) {
            // El mensaje de la excepción va a la consola, no a la cara del cliente.
            console.error('[PDF de la dieta]', err);
            toast.error('No hemos podido preparar tu dieta. Inténtalo de nuevo.');
        }
        setExportingPdf(false);
    };

    // Load distribution - accepts optional overrides to avoid stale closure on init
    //: Con qué configuración se pidió el reparto la última vez. Quien lo pide lo apunta, y
    //: el efecto de más abajo solo vuelve a pedirlo cuando lo de ahora no es esto.
    const configPedidaRef = useRef(null);
    const loadDistribution = useCallback(async (overrides = {}) => {
        const pedida = {
            tipo_dia: overrides.tipoDia ?? tipoDia,
            num_comidas: overrides.numComidas ?? numComidas,
            momento_entreno: overrides.momentoEntreno ?? momentoEntreno,
            opcion_peri: overrides.opcionPeri ?? opcionPeri,
        };
        configPedidaRef.current = JSON.stringify([currentDate, pedida]);
        try {
            const result = await api('/api/calculator/distribute', {
                method: 'POST',
                body: JSON.stringify({
                    fecha: currentDate, // date-versioned macros: backend resolves the version effective on this date
                    ...pedida,
                    single_meal: pedida.num_comidas === 1, // el cliente manda
                })
            });
            setDistribution(result);
            setDistribError(null);
        } catch (err) {
            // Sin reparto TODOS los objetivos por comida quedan a 0 ("todo sobra" y el
            // autoajuste peta): en vez de silenciarlo, se guarda para avisar en pantalla.
            console.error('Error loading distribution:', err);
            setDistribError(err.message || 'Error de red');
        }
    }, [api, tipoDia, numComidas, momentoEntreno, opcionPeri, currentDate]);

    // QUIÉN MONTÓ ESTE DÍA (punto 4.11). El servidor lo guarda con la dieta desde que el
    // entrenador puede entrar en la calculadora de un cliente, y no se enseñaba en ninguna
    // pantalla. Jesús lo pidió con estas palabras: «si el entrenador le monta una dieta el
    // martes y el cliente la cambia el miércoles, los dos tienen que poder verlo».
    //
    // Al cliente solo se le dice cuando lo montó SU ENTRENADOR: que un día suyo lo montara él
    // no es una noticia, y una línea en todos los días acabaría siendo invisible justo el día
    // que dice algo.
    const [loMontoSuCoach, setLoMontoSuCoach] = useState(null);

    // La versión del día con la que trabaja esta pestaña (ver `loadDiet`).
    const versionDiaRef = useRef(null);
    const loadDietRef = useRef(null);

    // Extras del día (bloque 6.3): lo comido fuera del plan. NO CUENTAN EN NINGUNA PARTE
    // desde el punto 28 del doc del 24-08: van en su lista, aparte, y no mueven ni el
    // «Llevas» del Inicio ni la Dieta. Aquí solo se pintan y se editan.
    const [extrasDia, setExtrasDia] = useState([]);

    // Load saved diet - returns { targets, config } where config has the diet's day values
    const loadDiet = useCallback(async (date) => {
        try {
            const diet = await api(`/api/diets/${date}`);
            // CON QUÉ VERSIÓN DEL DÍA EMPEZAMOS. Viaja en cada guardado para que el
            // servidor sepa si esta pantalla va con una copia vieja: con el mismo día
            // abierto en dos sitios, el segundo en guardar devolvía su versión de antes y
            // borraba lo del otro sin que nadie se enterara (16-08-2026).
            versionDiaRef.current = diet?.updated_at || null;
            // Los Extras del día viajan con el documento (bloque 6.3 del doc 21-08):
            // se refrescan aquí para que el bloque de debajo de las comidas diga la verdad
            // también al cambiar de fecha o recargar tras un guardado.
            setExtrasDia(diet.extras || []);
            if (diet.exists) {
                const dietConfig = {
                    tipoDia: diet.tipo_dia || 'entrenamiento',
                    numComidas: diet.num_comidas || 4,
                    momentoEntreno: normMomento(diet.momento_entreno ?? 1),  // ?? not || so 0 (en ayunas) persiste
                    opcionPeri: normPeri(diet.opcion_peri),
                };
                setTipoDia(dietConfig.tipoDia);
                setNumComidas(dietConfig.numComidas);
                setMomentoEntreno(dietConfig.momentoEntreno);
                setOpcionPeri(dietConfig.opcionPeri);

                const updatedMeals = {};
                for (const [mealKey, mealData] of Object.entries(diet.comidas || {})) {
                    if (mealData.alimentos && mealData.alimentos.length > 0) {
                        const updatedFoods = await Promise.all(
                            mealData.alimentos.map(async (food) => {
                                if (food.macros_efectivos && food.macros_efectivos.P !== undefined) {
                                    return food;
                                }
                                try {
                                    const result = await api('/api/calculator/macros-efectivos', {
                                        method: 'POST',
                                        body: JSON.stringify({
                                            alimento_id: food.alimento_id,
                                            cantidad_g: food.cantidad_g,
                                            es_vegano: false
                                        })
                                    });
                                    return { ...food, macros_efectivos: result.efectivos, macros_brutos: result.brutos, que_cuenta: result.que_cuenta };
                                } catch { return food; }
                            })
                        );
                        updatedMeals[mealKey] = { ...mealData, alimentos: updatedFoods };
                    } else {
                        updatedMeals[mealKey] = mealData;
                    }
                }
                setMealsData(updatedMeals);
                setVolcadoMeal(diet.comida_volcada || null);
                setLoMontoSuCoach(diet.editado_como === 'entrenador'
                    ? { por: diet.editado_por, cuando: diet.updated_at } : null);
                // Este día ya lo configuró alguien: su tipo es una decisión, no un supuesto.
                setDiaSinMarcar(false);
                console.log('[loadDiet] distribution_targets:', diet.distribution_targets);
                return { targets: diet.distribution_targets || null, config: dietConfig, ok: true, comidas: updatedMeals };
            } else {
                setMealsData({});
                setVolcadoMeal(null);
                setLoMontoSuCoach(null);
                // NADIE HA DICHO SI ESTE DÍA ENTRENA (punto 4.17). Se sigue diciendo -- el
                // selector se queda con su aro --, pero ya no se abre siempre en «Entreno»:
                // el sábado y el domingo abren en descanso (Francisco, 3-09).
                //
                // Y EL TIPO LO DICE EL SERVIDOR (5-09, ficha 61 del parte de Gonzalo). Aquí
                // se calculaba con `tipoPorDefecto`, que solo sabe del fin de semana, así que
                // un martes de descanso POR RUTINA abría en entreno; y encima Inicio pedía su
                // reparto con otro tipo, de forma que el mismo sábado vacío salía «Descanso»
                // en el selector y con los números de entreno. `tipo_dia_previsto` es la
                // única fuente: manda la rutina y, si no la hay, la regla del finde. La regla
                // local se queda de red por si el servidor es viejo.
                const previsto = diet.tipo_dia_previsto || tipoPorDefecto(date);
                setTipoDia(previsto);
                setDiaSinMarcar(true);
                return { targets: null, config: null, ok: true, comidas: {}, tipoPrevisto: previsto };
            }
        } catch (err) {
            console.error('Error loading diet:', err);
            setMealsData({});
            // ok:false -> the load FAILED (not "no diet"); auto-save must not treat the empty
            // in-memory state as authoritative and delete a diet that may exist on the server.
            return { targets: null, config: null, ok: false };
        }
    }, [api]);

    // ── Auto-guardado (Calma autoGuardadoEnFecha) ────────────────────────────
    // Calma auto-saves the diet you are LEAVING when the date changes and on page unmount
    // (no per-keystroke save). An empty day is deleted (borrarDieta). autoSaveRef holds the
    // latest savable snapshot; loadedDateRef guards against saving/deleting a date whose
    // diet never loaded (or failed to load) - preventing accidental deletion on a race.
    const autoSaveRef = useRef({});
    const loadedDateRef = useRef(null);
    // Estado del guardado que se muestra al usuario. Antes el auto-guardado era mudo: si fallaba,
    // seguias montando el dia creyendo que estaba a salvo y lo perdias al recargar.
    const [guardadoEstado, setGuardadoEstado] = useState('idle'); // idle | guardando | guardado | error
    // Un dia que llego con alimentos y se queda vacio SI se borra (el usuario los quito). Un dia
    // que nunca los tuvo no borra nada: asi un fallo de carga no puede convertirse en un DELETE.
    const teniaAlimentosRef = useRef(false);

    const [cargaFallida, setCargaFallida] = useState(false);
    const [reintentoCarga, setReintentoCarga] = useState(0);

    // ── Copia local del dia (red de seguridad) ───────────────────────────────
    // El dia se copia en el navegador en cuanto lo tocas. Si el servidor no responde, ni la
    // pantalla se queda vacia ni se pierde nada: se recupera de aqui y se sube solo cuando el
    // servidor vuelve. La copia se borra en cuanto el guardado remoto confirma.
    // LA COPIA VA CON EL NOMBRE DEL CLIENTE DENTRO (punto 4.7). Antes la clave era solo la
    // fecha, así que en un ordenador compartido el segundo que entrara se encontraba el día
    // del primero -- y al guardarlo, se lo metía en su propia dieta. Sin cliente no se
    // guarda nada: mejor perder la red de seguridad que dejarla donde la vea otro.
    const claveLocal = (date) => `nutrition_dia_${date}`;

    const guardarCopiaLocal = (date, snap) => {
        if (!uid || !hayAlimentos(snap)) return;
        escribirLocal(claveLocal(date), uid, JSON.stringify(snap));
    };
    const leerCopiaLocal = (date) => {
        const s = leerLocal(claveLocal(date), uid);
        try { return s ? JSON.parse(s) : null; } catch (e) { return null; }
    };
    const borrarCopiaLocal = (date) => borrarLocal(claveLocal(date), uid);

    const hayAlimentos = (snap) =>
        Object.values(snap?.comidas || {}).some(m => (m?.alimentos || []).length > 0);

    // ── MIRAR UN DÍA NO PUEDE CAMBIARLO (1-09-2026) ─────────────────────────────────────
    //
    // Los tres guardados de esta pantalla -- el de 1,5 s, el de salir del día y el de cerrar
    // la pestaña -- escribían SIEMPRE, hubiera tocado algo el cliente o no. Con eso, abrir un
    // día de agosto para mirarlo lo reescribía con la fecha de hoy y, lo que de verdad duele,
    // le recalculaba `is_cuadrado` CON LOS MACROS DE AHORA: un día que se cuadró en su
    // momento dejaba de estar cuadrado por el simple hecho de consultarlo, y de ese campo
    // salen el verde del calendario y la cuenta de «cuadraste N días». Repasar el historial
    // iba borrando el historial que se repasaba.
    //
    // La defensa es una huella de cómo estaba el día al cargarlo. Si lo que hay en pantalla
    // sigue siendo eso, no se guarda nada.
    //
    // LA HUELLA SOLO LLEVA LO QUE EL CLIENTE DECIDE. `macros_snapshot`, `is_cuadrado` y
    // `distribution_targets` NO entran a propósito: los tres salen de los macros de hoy, así
    // que con ellos dentro cualquier día viejo saldría «cambiado» siempre y esto no serviría
    // de nada, que es justo el fallo que viene a cerrar.
    const huellaDelDia = (snap) => JSON.stringify({
        comidas: snap?.comidas || {},
        tipo_dia: snap?.tipo_dia,
        num_comidas: snap?.num_comidas,
        momento_entreno: snap?.momento_entreno,
        opcion_peri: snap?.opcion_peri,
        comida_volcada: snap?.comida_volcada ?? null,
    });
    //: Cómo estaba el día cuando se cargó. `null` mientras no se sabe (cargando o cambiando
    //: de fecha), y en ese hueco no se guarda: es cuando la pantalla todavía se está montando.
    const huellaCargadaRef = useRef(null);
    //: Qué día está abierto AHORA. El guardado de despedida se llama con la fecha que se
    //: deja, que ya no es ésta, y su huella no puede pisar la del día al que se acaba de
    //: entrar.
    const currentDateRef = useRef(currentDate);
    currentDateRef.current = currentDate;

    const hayCambios = (snap) =>
        huellaCargadaRef.current !== null && huellaDelDia(snap) !== huellaCargadaRef.current;

    // Cuando se puede escribir en el servidor: con el dia cargado (caso normal) o, si la carga
    // fallo, en cuanto haya alimentos en pantalla. Perder lo que acabas de montar es mucho peor
    // que guardar un dia que quiza no cargo del todo; y borrar nunca se permite en ese estado.
    // Y ademas, que haya algo que guardar: ver la huella de arriba.
    const puedeGuardar = (snap, date) =>
        (loadedDateRef.current === date || hayAlimentos(snap)) && hayCambios(snap);

    const autoSaveDiet = useCallback(async (date, snap) => {
        if (!date || !snap) return;
        const hasFood = hayAlimentos(snap);
        // Lo primero, la copia local: es instantanea y no puede fallar por red. A partir de aqui,
        // pase lo que pase con el servidor, el dia ya no se pierde.
        guardarCopiaLocal(date, snap);
        setGuardadoEstado('guardando');
        try {
            if (hasFood) {
                const res = await api('/api/diets', {
                    method: 'POST',
                    body: JSON.stringify({ fecha: date, ...snap,
                                           base_updated_at: versionDiaRef.current }),
                });
                // La versión con la que seguimos trabajando es la que acaba de escribir el
                // servidor; sin esto, el siguiente autoguardado chocaría contra sí mismo.
                if (res?.updated_at) versionDiaRef.current = res.updated_at;
                if ((res?.conflictos || []).length) {
                    // Esa comida la tocaron en otro sitio después de que cargáramos: se ha
                    // respetado la de allí y aquí se recarga para no seguir con una copia
                    // vieja que la volvería a pisar en el siguiente guardado.
                    setGuardadoEstado('guardado');
                    await loadDietRef.current?.(date);
                    return true;
                }
                teniaAlimentosRef.current = true;
                // A partir de este guardado el día lleva su firma, no la de su entrenador: el
                // aviso tiene que irse o estaría diciendo que lo montó otro.
                setLoMontoSuCoach(null);
            } else if (teniaAlimentosRef.current) {
                await api(`/api/diets/${date}`, { method: 'DELETE' }).catch(() => {}); // 404 = nothing to delete
                teniaAlimentosRef.current = false;
            }
            borrarCopiaLocal(date);   // confirmado en el servidor: la copia ya no hace falta
            // Lo guardado pasa a ser «como estaba»: si no, el mismo contenido se volvería a
            // mandar en cada guardado de despedida hasta salir de la pantalla.
            if (date === currentDateRef.current) huellaCargadaRef.current = huellaDelDia(snap);
            setGuardadoEstado('guardado');
            return true;
        } catch (e) {
            setGuardadoEstado('error'); // la copia local sigue ahi y el reenvio la subira
            return false;
        }
    }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

    // Guardado mientras trabajas (no solo al salir de la pantalla). Sin esto, el dia vivia unicamente
    // en memoria: el PDF no encontraba nada que exportar y una recarga podia llegar antes que el
    // guardado de despedida y dejar la pantalla en blanco.
    const guardadoTimerRef = useRef(null);
    const flushGuardado = useCallback(async () => {
        if (guardadoTimerRef.current) {
            clearTimeout(guardadoTimerRef.current);
            guardadoTimerRef.current = null;
        }
        if (!puedeGuardar(autoSaveRef.current, currentDate)) return;
        await autoSaveDiet(currentDate, autoSaveRef.current);
    }, [autoSaveDiet, currentDate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reintentar a mano nunca puede costarte lo que tienes en pantalla: si hay comida montada,
    // primero se sube y solo despues se vuelve a pedir el dia al servidor. Si la subida falla,
    // no se recarga nada (recargar pisaria tu trabajo con lo que haya en el servidor).
    const reintentarCarga = useCallback(async () => {
        const snap = autoSaveRef.current;
        if (hayAlimentos(snap)) {
            const subido = await autoSaveDiet(currentDate, snap);
            if (!subido) {
                toast.error('Seguimos sin conexión. Tu día está guardado en este dispositivo');
                return;
            }
        }
        setReintentoCarga(n => n + 1);
    }, [autoSaveDiet, currentDate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reenvio automatico: mientras quede una copia local sin confirmar, se reintenta sola cada 8 s.
    // Asi un corte de red se arregla solo en cuanto vuelve, sin que tengas que hacer nada.
    useEffect(() => {
        if (guardadoEstado !== 'error' || !currentDate) return;
        const t = setInterval(() => {
            const pendiente = leerCopiaLocal(currentDate);
            if (!pendiente) { clearInterval(t); return; }
            autoSaveDiet(currentDate, autoSaveRef.current);
        }, 8000);
        return () => clearInterval(t);
    }, [guardadoEstado, currentDate, autoSaveDiet]); // eslint-disable-line react-hooks/exhaustive-deps

    // Al abrir, HOY. Antes se restauraba sin más la última fecha que se hubiera mirado, así
    // que quien echaba un vistazo al día de mañana y se salía, al volver se encontraba la
    // pantalla abierta en mañana; y quien entraba al día siguiente aterrizaba en la fecha de
    // ayer. La app tiene que abrir en el día de hoy (punto 22 del doc del 07-08).
    //
    // Lo único que se conserva es lo útil de aquello: si recargas la página en el mismo día
    // en el que estabas trabajando, vuelves al día que tenías abierto en vez de perderlo. Por
    // eso se guarda también CUÁNDO se guardó, y la fecha solo se restaura si se guardó hoy.
    // El futuro TAMBIÉN se restaura: quien está dejando montado el día de mañana y recarga
    // (o el chat lo manda a mañana) tiene que seguir en mañana, no rebotar a hoy con el chat
    // y la pestaña cada uno en un día (ronda 1 del 15-08). El punto 22 queda protegido por
    // el sello del día: al entrar MAÑANA, lo guardado ayer ya no restaura y se abre en hoy.
    // Y la fecha también va por cliente (punto 4.7): era la otra mitad de lo mismo. Sin el
    // id dentro, el cliente B abría Nutrición en el día que estaba mirando el cliente A, que
    // es justo por donde empezaba el problema.
    //
    // Y ANTES QUE NADA, ?date=AAAA-MM-DD SI VIENE EN LA URL (punto 4.14). No funcionaba, y
    // con 991 días de dietas migradas por cliente es la única forma de mandar un enlace a un
    // día concreto -- o de que el entrenador abra el que quiere mirar sin ir pasando flechas.
    // Manda sobre lo guardado: si alguien pide un día por la URL, es ese y no otro.
    useEffect(() => {
        const pedida = new URLSearchParams(window.location.search).get('date');
        if (pedida && /^\d{4}-\d{2}-\d{2}$/.test(pedida)) {
            setCurrentDate(pedida);
            return;
        }
        // Y SIN FECHA EN LA URL, HOY. SIEMPRE (Jesús, 11-09, punto 20).
        //
        // Aquí se restauraba el último día mirado si se había guardado hoy, y por eso
        // entrando a Nutrición se encontró plantado en el domingo 20 de septiembre. Su
        // regla: «Nutrición abre siempre en el día de hoy; para ir a otro, las flechas».
        //
        // ESTO REVIERTE LO DEL 15-08 (ronda 1, punto 22), que guardaba la fecha para que ir
        // al chat y volver no rebotase a hoy. Manda lo más reciente, y además aquel motivo
        // ya no se pierde: el chat, Inicio y Mi semana navegan con `?date=`, que sigue
        // ganando unas líneas más arriba.
        setCurrentDate(hoyISO());
    }, [uid]);

    // ?comida=Intra|Post|C1..C4: aterrizar CON ESA COMIDA elegida. Es el remate del
    // P32 del 23-08: pinchar el bloque del peri en Inicio te dejaba en la cocina con
    // la Comida 1 delante, como si el peri no fuera contigo.
    useEffect(() => {
        const comida = new URLSearchParams(window.location.search).get('comida');
        if (!comida || !/^(C[1-4]|Intra|Post)$/.test(comida)) return;
        setSelectedMeal(comida);
        setExpandedMeals(prev => ({ ...prev, [comida]: true }));

        // Y ATERRIZAR EN ELLA, NO ARRIBA DE LA PÁGINA (Jesús, 18-09). «Le doy a Comida 4 y
        // aun así me lleva a la parte de arriba de la página, de hecho ni la veo y tengo que
        // hacer scroll.» Con cuatro comidas, la última queda fuera de la pantalla, así que
        // pinchar «Comida 4» en Inicio te dejaba buscándola.
        //
        // SE ESPERA A QUE EXISTA. La tarjeta no está en la pantalla cuando esto corre: el día
        // se pide al servidor y llega después, así que un scroll aquí mismo no encontraría
        // nada. Se mira cada poco durante cinco segundos y, en cuanto aparece, se va a ella y
        // se deja de mirar. Si el día tarda más de eso o no tiene esa comida, no pasa nada:
        // se queda como estaba, arriba.
        //
        // Y HAY QUE COGER LA QUE SE VE. Esta pantalla pinta cada comida MAS DE UNA VEZ -- la
        // del telefono y la del escritorio conviven en el DOM, y la que no toca esta oculta --,
        // asi que el primer nodo con ese nombre puede medir cero y llevar el scroll a ninguna
        // parte. Se busca entre todos el que de verdad ocupa sitio.
        // Y HAY QUE COGER LA QUE SE VE. Esta pantalla pinta cada comida MAS DE UNA VEZ -- la
        // del telefono y la del escritorio conviven en el DOM, y la que no toca esta oculta --,
        // asi que el primer nodo con ese nombre puede medir cero y llevar el scroll a ninguna
        // parte. Se busca entre todos el que de verdad ocupa sitio.
        //
        // Y SE REAJUSTA HASTA QUE LA PANTALLA SE ASIENTA. Con un solo salto se aterrizaba
        // DENTRO de la comida, por los ingredientes: lo de arriba (la cabecera del dia, los
        // totales, las comidas anteriores) termina de cargar despues y empuja la tarjeta hacia
        // abajo, asi que el sitio al que se salto ya no es el suyo. Se sigue corrigiendo hasta
        // que deja de moverse dos veces seguidas, o hasta que se acaba el plazo.
        // EL MARGEN CUENTA LA BARRA DE ARRIBA. La app lleva una cabecera pegada al borde
        // superior, asi que dejar la comida a 12 px del top la mete DEBAJO de esa barra: se
        // aterrizaba en los ingredientes y el titulo «Comida 4» quedaba tapado. Se mide lo que
        // haya pegado arriba y se le suman los 12 px de aire.
        const loQueTapaArriba = () => {
            let alto = 0;
            document.querySelectorAll('header, [data-sticky-top]').forEach((el) => {
                const cs = getComputedStyle(el);
                if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
                const r = el.getBoundingClientRect();
                if (r.top <= 1 && r.height > alto) alto = r.height;
            });
            return alto;
        };
        const AIRE = 12;                   // un dedo de aire por encima de la cabecera
        let vigilante = null;
        const desistirA = Date.now() + 6000;
        let anterior = null;
        let quieta = 0;
        const buscarla = () => {
            const el = [...document.querySelectorAll(`[data-testid="meal-card-${comida}"]`)]
                .find((n) => n.getBoundingClientRect().height > 0);
            if (el) {
                const arriba = Math.max(0, window.scrollY + el.getBoundingClientRect().top
                                            - loQueTapaArriba() - AIRE);
                quieta = anterior !== null && Math.abs(arriba - anterior) < 2 ? quieta + 1 : 0;
                anterior = arriba;
                window.scrollTo({ top: arriba, behavior: 'auto' });
                if (quieta >= 2) { clearInterval(vigilante); return; }
            }
            if (Date.now() > desistirA) clearInterval(vigilante);
        };
        vigilante = setInterval(buscarla, 150);
        buscarla();
        return () => clearInterval(vigilante);
    }, []);

    // La fecha se refleja en la URL, para que copiar la barra de direcciones lleve al día
    // que estás mirando: se cambia sin recargar ni apilar historial, así que el botón de
    // atrás sigue haciendo lo suyo.
    //
    // YA NO SE GUARDA EN EL NAVEGADOR (Jesús, 11-09, punto 20): se guardaba para restaurarla
    // al entrar, y eso es justo lo que él no quiere. Sin nadie que la lea, escribirla solo
    // dejaba basura en el navegador de cada cliente.
    useEffect(() => {
        if (!currentDate || !uid) return;
        try {
            const url = new URL(window.location.href);
            if (url.searchParams.get('date') !== currentDate) {
                url.searchParams.set('date', currentDate);
                window.history.replaceState(null, '', url.toString());
            }
        } catch (e) { /* si el navegador no deja tocar la URL, no pasa nada */ }
    }, [currentDate, uid]);

    // Initial load
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            setDistribTargetsOverlay(null);
            // Cambiando de día no se sabe todavía cómo estaba el nuevo, así que hasta que
            // termine de cargar no se guarda nada (ver `hayCambios`). Sin esto, la primera
            // vuelta compararía contra la huella del día ANTERIOR y todo saldría «cambiado».
            huellaCargadaRef.current = null;

            // Load persisted diet config FIRST to avoid stale-closure distribution call
            let cfgOverrides = {};
            try {
                const cfg = await api('/api/user/diet-config');
                const me = normMomento(cfg.momento_entreno ?? 1);
                const nc = cfg.num_comidas ?? 4;
                const op = normPeri(cfg.opcion_peri);
                setMomentoEntreno(me);
                setNumComidas(nc);
                setOpcionPeri(op);
                setOpcionPeriFicha(op);
                cfgOverrides = { momentoEntreno: me, numComidas: nc, opcionPeri: op };
            } catch (e) {}

            // Reintento automatico antes de molestar al usuario: un microcorte de red no deberia
            // costarle un aviso ni, mucho menos, el dia. Tres intentos con esperas crecientes.
            let carga = await loadDiet(currentDate);
            for (let intento = 1; !carga.ok && intento <= 2; intento++) {
                await new Promise(r => setTimeout(r, intento * 1200));
                carga = await loadDiet(currentDate);
            }
            const { targets, config: dietConfig, ok, comidas: dietComidas, tipoPrevisto } = carga;
            if (targets) setDistribTargetsOverlay(targets);

            // If diet has its own config, use that (overrides profile defaults for this day)
            //
            // Y EN EL DÍA VACÍO, EL TIPO PREVISTO (5-09, ficha 61). Sin día guardado
            // `dietConfig` es null, así que aquí se pedía el reparto con `cfgOverrides`, que
            // no lleva tipo: `loadDistribution` caía en el `tipoDia` del cierre, que en la
            // primera vuelta todavía es «entrenamiento». Y como esta llamada sale DESPUÉS de
            // la que dispara el cambio de `tipoDia` (el efecto del reparto), era la última en
            // llegar y ganaba: el selector decía «Descanso» y los números eran los de
            // entreno. Con el tipo dentro de los overrides las tres llamadas piden lo mismo.
            const finalOverrides = dietConfig
                || (tipoPrevisto ? { ...cfgOverrides, tipoDia: tipoPrevisto } : cfgOverrides);
            await loadDistribution(finalOverrides);
            setLoading(false);
            // Only enable auto-save for this date once it has loaded successfully.
            if (ok) {
                loadedDateRef.current = currentDate;
                // Lo que traia el dia al abrirlo decide si un dia vacio puede borrarse (ver autoSaveDiet).
                teniaAlimentosRef.current = hayAlimentos({ comidas: dietComidas || {} });
                setGuardadoEstado('idle');
                setCargaFallida(false);
            } else {
                // La carga fallo (red, backend caido, sesion caducada). Antes esto dejaba la pantalla
                // muda PARA SIEMPRE en esa fecha: seguias montando el dia y no se guardaba por ningun
                // camino, sin un solo aviso. Ahora se avisa, y lo que montes se guarda igualmente
                // (ver puedeGuardar); lo unico que queda prohibido es BORRAR, porque el dia vacio que
                // se ve en pantalla no es de fiar.
                setCargaFallida(true);
                teniaAlimentosRef.current = false;
                // Sin esto, el "Guardado" del dia que acabas de dejar tapaba el aviso de esta fecha.
                setGuardadoEstado('idle');
                // Si el dia tenia copia local sin subir, se recupera: no ves la pantalla en blanco
                // ni pierdes lo que montaste la ultima vez que el servidor no respondio.
                const copia = leerCopiaLocal(currentDate);
                if (copia && hayAlimentos(copia)) {
                    setMealsData(copia.comidas || {});
                    if (copia.tipo_dia) setTipoDia(copia.tipo_dia);
                    if (copia.num_comidas) setNumComidas(copia.num_comidas);
                    if (copia.momento_entreno != null) setMomentoEntreno(normMomento(copia.momento_entreno));
                    if (copia.opcion_peri) setOpcionPeri(normPeri(copia.opcion_peri));
                    setVolcadoMeal(copia.comida_volcada || null);
                    setGuardadoEstado('error');   // hay algo pendiente de subir: arranca el reenvio
                    toast.info('Sin conexion con el servidor: hemos recuperado tu día de la copia local');
                }
            }
        };
        init();
    }, [currentDate, reintentoCarga]); // eslint-disable-line

    // Auto-save the date being LEFT (cleanup runs on date change and on unmount) - mirrors
    // Calma's `watch fecha` + `unmounted` -> autoGuardadoEnFecha. Guarded by loadedDateRef so
    // a not-yet-loaded date is never persisted/deleted. autoSaveDiet is kept in a ref so the
    // effect depends ONLY on currentDate (not on autoSaveDiet/api identity) - otherwise an
    // unstable `api` would re-fire the cleanup on every render and save constantly.
    const autoSaveDietRef = useRef(autoSaveDiet);
    autoSaveDietRef.current = autoSaveDiet;
    // Por el ref, igual que el autoguardado: la limpieza del efecto de abajo se monta con el
    // `currentDate` de entonces y necesita la versión de ahora de esta comprobación.
    const hayCambiosRef = useRef(hayCambios);
    hayCambiosRef.current = hayCambios;
    // `loadDiet` se declara más arriba pero el autoguardado necesita llamarla cuando otra
    // pantalla ha tocado el día; por el ref no hay que reordenar medio fichero.
    loadDietRef.current = loadDiet;
    useEffect(() => {
        const dateLeaving = currentDate;
        return () => {
            // Y AQUÍ TAMBIÉN SE MIRA SI HAY ALGO QUE GUARDAR (1-09). Este era el camino por el
            // que abrir un día viejo y pasar al siguiente lo reescribía entero: la condición
            // se cumplía siempre por `loadedDateRef`, tocara algo el cliente o no.
            if ((loadedDateRef.current === dateLeaving || hayAlimentos(autoSaveRef.current))
                    && hayCambiosRef.current(autoSaveRef.current)) {
                autoSaveDietRef.current(dateLeaving, autoSaveRef.current);
            }
        };
    }, [currentDate]); // eslint-disable-line react-hooks/exhaustive-deps

    // A browser REFRESH/close does NOT run React cleanup, so the unmount auto-save never fires
    // and the day looked "lost". Save synchronously via keepalive fetch (it survives unload and
    // carries the auth header). Only saves a non-empty day, and nunca borra.
    //
    // Tres eventos, no uno: `beforeunload` no es fiable en movil (iOS puede matar la pestana sin
    // dispararlo nunca), asi que se escucha tambien `pagehide` y el paso a segundo plano, que es
    // lo que si ocurre cuando cambias de aplicacion. Guardar de mas es inofensivo: es un upsert.
    useEffect(() => {
        const handler = () => {
            if (loading) return;
            const snap = autoSaveRef.current;
            if (!hayAlimentos(snap)) return;
            // Y SOLO SI HAY ALGO QUE GUARDAR (1-09). Este salta también al pasar la pestaña a
            // segundo plano, así que sin esto bastaba con cambiar de pestaña con un día viejo
            // abierto para reescribirlo.
            if (!hayCambios(snap)) return;
            try {
                const token = localStorage.getItem('token');
                fetch(`${process.env.REACT_APP_BACKEND_URL}/api/diets`, {
                    method: 'POST',
                    // Y LAS CABECERAS DE `lib/cabeceras`, QUE ESTE ESCRIBE (punto 2 del
                    // 17-08). Era el otro sitio que se quedó fuera de aquel arreglo, y el
                    // peor de los dos: el del PDF se lleva el día equivocado, pero este
                    // GUARDA. Salta en `beforeunload`, en `pagehide` y al pasar la pestaña a
                    // segundo plano, así que al entrenador que estaba en la calculadora de
                    // un cliente le bastaba con cambiar de pestaña para escribir la dieta
                    // del cliente EN SU PROPIA CUENTA, y encima de su día si lo tenía.
                    headers: cabeceras(token, { 'Content-Type': 'application/json' }),
                    // CON SU VERSIÓN DEL DÍA, COMO TODOS LOS DEMÁS (16-08-2026, en prod).
                    // Este guardado de despedida era el único que no la mandaba, y resultó
                    // ser justo el que más daño hace: al salir de la pantalla escribía la
                    // copia más vieja que hay, sin candado ninguno. Medido con la cuenta de
                    // Francisco: el asistente pasaba el día a 3 comidas, y bastaba con
                    // recargar Nutrición para que volvieran las 4 con la Comida 4 entera.
                    // Aquí no se puede atender la respuesta -- la pestaña se está yendo --,
                    // pero con la versión el servidor ya sabe qué parte suya está vieja.
                    body: JSON.stringify({ fecha: currentDate, ...snap,
                                           base_updated_at: versionDiaRef.current }),
                    keepalive: true,
                });
            } catch (e) { /* best effort */ }
        };
        const alOcultarse = () => { if (document.visibilityState === 'hidden') handler(); };
        window.addEventListener('beforeunload', handler);
        window.addEventListener('pagehide', handler);
        document.addEventListener('visibilitychange', alOcultarse);
        return () => {
            window.removeEventListener('beforeunload', handler);
            window.removeEventListener('pagehide', handler);
            document.removeEventListener('visibilitychange', alOcultarse);
        };
    }, [currentDate, loading]);

    // Guardado con retardo: cada cambio reinicia el contador y se guarda cuando paras de tocar.
    // 1,5 s es suficiente para no disparar una peticion por cada gramo que ajustas.
    useEffect(() => {
        if (loading) return;
        // LA PRIMERA VUELTA TRAS CARGAR NO GUARDA: apunta cómo estaba el día y se va. A
        // partir de aquí, lo que no se parezca a esto es que el cliente ha tocado algo.
        if (huellaCargadaRef.current === null) {
            huellaCargadaRef.current = huellaDelDia(autoSaveRef.current);
            return;
        }
        if (!puedeGuardar(autoSaveRef.current, currentDate)) return;
        if (guardadoTimerRef.current) clearTimeout(guardadoTimerRef.current);
        guardadoTimerRef.current = setTimeout(() => {
            autoSaveDiet(currentDate, autoSaveRef.current);
        }, 1500);
        return () => {
            if (guardadoTimerRef.current) clearTimeout(guardadoTimerRef.current);
        };
    }, [mealsData, tipoDia, numComidas, momentoEntreno, opcionPeri, volcadoMeal,
        currentDate, loading]); // eslint-disable-line react-hooks/exhaustive-deps

    // EL REPARTO SE VUELVE A PEDIR CUANDO CAMBIA LA CONFIGURACIÓN DEL DÍA.
    //
    // Y TAMBIÉN CUANDO TERMINA DE CARGAR (1-09-2026). Esto leía `loading` pero NO lo tenía
    // en las dependencias, así que si el tipo de día cambiaba mientras la pantalla cargaba,
    // el reparto de ese tipo no se pedía nunca: al soltarse `loading` este efecto ya no se
    // volvía a ejecutar. El cliente se encontraba un día de descanso pidiéndole los macros
    // de un día de entreno -- 100 g de proteína y 80 de hidratos de más, y 30 de grasa de
    // menos -- y no se corregía solo: había que tocar el selector Entreno/Descanso.
    //
    // Y NO SE PIDE DOS VECES: `init` ya pide el reparto con la configuración del día antes
    // de soltar `loading`, así que al entrar aquí casi siempre es el mismo y no hay nada que
    // volver a preguntar. Se compara con la última que se pidió, que es lo que distingue
    // «ha cambiado algo» de «acabo de cargar».
    useEffect(() => {
        if (loading) return;
        const ahora = JSON.stringify([currentDate, {
            tipo_dia: tipoDia, num_comidas: numComidas,
            momento_entreno: momentoEntreno, opcion_peri: opcionPeri,
        }]);
        if (configPedidaRef.current === ahora) return;
        loadDistribution();
    }, [tipoDia, numComidas, momentoEntreno, opcionPeri, loading, currentDate]); // eslint-disable-line

    // Al caer en el día vacío se cargan sus dos listas: cuántas dietas guardadas tiene
    // (para «Ver las N») y sus días recientes montados (para «Repetir un día»). Solo
    // cuando la pantalla de día vacío va a verse; cargar dos veces no rompe nada.
    useEffect(() => {
        if (loading || cargaFallida || diaEnCreacion === currentDate) return;
        const vacio = !Object.values(mealsData || {}).some(m => (m?.alimentos || []).length > 0);
        if (!vacio) return;
        loadDietFavorites();
        setCargandoRecientes(true);
        loadRecentDiets().finally(() => setCargandoRecientes(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
        // Y SE VUELVE A PEDIR AL CAMBIAR DE ENTRENO A DESCANSO. La llamada lleva
        // `tipo_destino`, que es lo que decide qué días se ofrecen y con qué etiqueta
        // («Encaja», «+12 G», «Descanso»), pero `tipoDia` no estaba entre las dependencias:
        // el cliente tocaba el interruptor y la lista se quedaba con la del tipo anterior,
        // juzgada contra unos macros que ya no eran los suyos.
    }, [loading, currentDate, mealsData, cargaFallida, diaEnCreacion, tipoDia]);

    // EL INTRA Y EL POST SE RELLENAN SOLOS AL CREAR UN DÍA DE ENTRENO (Jesús, artefacto «El
    // sugeridor del perientreno», 25-09; Francisco: al crear, con sus alimentos y cantidades).
    // «El intra y el post son las dos comidas que todo el mundo repite igual: el mismo MAP, la
    // misma ciclodextrina, la misma whey. Esto convierte cuatro toques por día en cero.»
    // Se copian del último día de entreno (`/diets/ultimo-peri`), solo los que el reparto de
    // hoy lleva y solo si están vacíos: NUNCA pisa uno ya montado. Una vez por fecha.
    const periCopiadoRef = useRef(null);
    useEffect(() => {
        if (diaEnCreacion !== currentDate || tipoDia === 'descanso') return;
        // SIN PERIENTRENO EN SU FICHA, NO SE COPIA NADA (26-09, el día de Jesús: su peri está a
        // 0 y le entraron 5 g de Hydro Peptides, 10 g de Gatorade y 100 g de leche de almendras).
        // Lo de abajo ya se lo saltaba cuando el reparto dice 0, pero si el reparto aún no ha
        // llegado al crear el día, el Intra y el Post todavía no se esconden y se copiaban.
        const periFicha = profile?.macros_periworkout;
        if (periFicha && !(Number(periFicha.protein ?? periFicha.proteinas ?? 0) > 0)
            && !(Number(periFicha.carbs ?? periFicha.hidratos ?? 0) > 0)) return;
        if (periCopiadoRef.current === currentDate) return;
        const hoy = getMealOrder().filter((k) => k === 'Intra' || k === 'Post');
        // SI EL REPARTO AÚN NO TRAE EL PERI, SE ESPERA (27-09). Al pasar el día a «Entreno»
        // esto corría antes de que el intra y el post entraran en la lista, se daba el día por
        // mirado y no se copiaba nada nunca. Ahora se vuelve a mirar cuando llegan.
        if (hoy.length === 0) return;
        periCopiadoRef.current = currentDate;
        const vacios = hoy.filter((k) => !(mealsData?.[k]?.alimentos || []).length);
        if (vacios.length === 0) return;
        // EL INTRA FAVORITO, «PUESTO SOLO» (Jesús, «El intra, paso a paso», 27-09): si lo dejó
        // puesto, sale ése -- con las cantidades a los macros de hoy: «si tus macros cambian,
        // lo ajustamos» -- y no el del último día.
        // Va en el mismo sitio que la copia del último día, DESPUÉS de la respuesta: puesto en el
        // acto, el propio «Crear el día» vaciaba las comidas justo detrás y se lo llevaba.
        // Y EL POST IGUAL (27-09: «lo mismo que en el intraentreno»).
        const favDe = { Intra: profile?.peri_favorito?.intra?.alimentos, Post: profile?.peri_favorito?.post?.alimentos };
        api(`/api/diets/ultimo-peri?antes_de=${currentDate}`).then((res) => {
            const conFav = ['Intra', 'Post'].filter((k) => favDe[k]?.length && vacios.includes(k));
            for (const k of conFav) res = { ...res, [k]: ajustarPeriFavorito(favDe[k], getMealTarget(k)) };
            const copiados = vacios.filter((k) => (res?.[k] || []).length > 0);
            if (copiados.length === 0) return;
            setMealsData((prev) => {
                const next = { ...prev };
                for (const k of copiados) {
                    if ((next[k]?.alimentos || []).length) continue;   // lo montó mientras tanto
                    next[k] = { ...(next[k] || {}), alimentos: res[k].map((a) => ({ ...a })),
                                ...(conFav.includes(k) ? { puesto_solo: true } : {}) };
                }
                return next;
            });
            if (copiados.every((k) => conFav.includes(k))) return;   // «Puesto solo» ya lo dice la tarjeta
            const quien = copiados.map((k) => (k === 'Intra' ? 'el intra' : 'el post')).join(' y ');
            toast(`Te hemos puesto ${quien} de tu último día de entreno. Si hoy quieres otra cosa, bórralo y monta lo que quieras.`,
                { id: 'peri-copiado' });
        }).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [diaEnCreacion, currentDate, tipoDia, opcionPeri, distribution]);

    // Objetivo por comida guardado (distribTargetsOverlay) OBSOLETO: se congela al crear la
    // dieta, pero si los macros asignados al cliente cambian después, la distribución
    // recalculada (`distribution`) ya no coincide. Sin esto, la comida seguía mostrando el
    // objetivo viejo ("cuadrada") mientras la cabecera del día usaba el nuevo ("te pasas") -
    // en comida única eso dejaba 225/240 en la comida y 180/180 en el día. Al detectar el
    // desfase descartamos el overlay: la comida pasa a usar el objetivo de HOY (coinciden) y
    // el autoguardado deja de re-persistir el valor viejo. Se ignora en modo volcado (sus
    // objetivos viven en volcadoMeal, no en el overlay).
    //
    // PERO NO SE JUZGA A MITAD DE LA CARGA (17-08-2026). Al abrir un día que el asistente
    // dejó en 3 comidas, esta pantalla arranca con las 4 del perfil: su distribución dice
    // 47,5 para la Comida 1, el objetivo guardado dice 63,3, la diferencia pasa de la
    // tolerancia y el overlay se tira ENTERO. Cuando llega la configuración del día ya no
    // hay overlay que usar, así que la comida sale con el número que calcula esta pantalla
    // (63,5) y el chat sigue diciendo el suyo (63,3): dos cifras para lo mismo, y el «faltan
    // 9,3 g» de al lado calculado sobre la otra. Mientras la distribución no sea la de ESTE
    // día -- misma cantidad de comidas que el objetivo guardado -- aquí no se decide nada.
    useEffect(() => {
        if (!distribTargetsOverlay || !distribution || volcadoMeal || loading) return;
        const enDistribucion = Object.keys(distribution.comidas || {}).length
            + Object.keys(distribution.periworkout || {}).length;
        if (enDistribucion !== Object.keys(distribTargetsOverlay).length) return;
        const TOL = 3; // holgura por redondeos; un cambio real de macros es >= 5 g
        const stale = Object.entries(distribTargetsOverlay).some(([k, t]) => {
            const f = distribution.comidas?.[k] || distribution.periworkout?.[k];
            if (!f) return true; // la comida ya no existe en la distribución de hoy
            return Math.abs((t.P || 0) - (f.P || 0)) > TOL ||
                   Math.abs((t.H || 0) - (f.H || 0)) > TOL ||
                   Math.abs((t.G || 0) - (f.G || 0)) > TOL;
        });
        if (stale) setDistribTargetsOverlay(null);
    }, [distribTargetsOverlay, distribution, volcadoMeal, loading]);

    // Wrappers for user-initiated config changes - persist to profile (cross-device)
    // En cuanto elige, el día deja de estar sin marcar: lo ha dicho él (punto 4.17).
    const handleSetTipoDia = (v) => { setTipoDia(v); setDiaSinMarcar(false); };
    const handleSetMomentoEntreno = (v) => {
        setMomentoEntreno(v);
        api('/api/user/diet-config', { method: 'PATCH', body: JSON.stringify({ momento_entreno: v }) }).catch(() => {});
    };
    const handleSetOpcionPeri = (v) => {
        setOpcionPeri(v);
        setOpcionPeriFicha(v);
        api('/api/user/diet-config', { method: 'PATCH', body: JSON.stringify({ opcion_peri: v }) }).catch(() => {});
    };
    const handleSetNumComidas = (v) => {
        setNumComidas(v);
        api('/api/user/diet-config', { method: 'PATCH', body: JSON.stringify({ num_comidas: v }) }).catch(() => {});
    };


    // Navigation
    const changeDate = (days) => {
        const d = new Date(currentDate + 'T12:00:00');
        d.setDate(d.getDate() + days);
        const n = d;
        setCurrentDate(`${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`);
    };

    // EL DÍA CON SU NOMBRE, TAMBIÉN HOY (maqueta de la parte 6): «Jueves, 27 de agosto» y no
    // «Hoy». En esta pantalla se anda de un día a otro con las flechas, y «Hoy» era lo único
    // que no decía QUÉ día es: al volver de mirar el sábado, la cabecera dejaba de dar la
    // referencia justo cuando hacía falta. El nombre entero, además, es como se dice en el
    // Inicio, así que las dos pantallas hablan igual.
    const formatDate = (dateStr) => {
        const [y, m, d] = dateStr.split('-').map(Number);
        const local = new Date(y, m - 1, d);
        // CON EL AÑO SI NO ES ESTE (QA 15-08). Se podía acabar montando la dieta en enero
        // de 2020 y la cabecera solo decía «Mié, 1 Ene»: nada en pantalla te sacaba del
        // error. El año solo aparece cuando hace falta, para no ensuciar el día a día.
        const esteAno = new Date().getFullYear();
        const texto = local.toLocaleDateString('es-ES', {
            weekday: 'long', day: 'numeric', month: 'long',
            ...(y !== esteAno ? { year: 'numeric' } : {}),
        });
        // «jueves, 27 de agosto» -> «Jueves, 27 de agosto». En español el día va en minúscula,
        // pero aquí abre la línea y se lee como un título.
        return texto.charAt(0).toUpperCase() + texto.slice(1);
    };


    // Meal order based on config
    // Calma esModoSinRepartoDeMacrosPorComidas (coach-set quiereRepartoDeComidas=false):
    // a single comida holds the whole day's macros; peri (intra/post) stays separate.
    // num_comidas=1 ES comida unica aunque la bandera no venga: un dia guardado por el
    // chat como bloque unico se pintaba con cuatro comidas, tres de ellas fantasma a
    // 0/0/0 (QA 15-08 ronda 3, B3-04).
    const singleMeal = distribution?.config?.single_meal === true || numComidas === 1;

    // Qué comidas tiene un día con ESA configuración. Se le pasa la configuración a
    // propósito: al aplicar una favorita hay que saber el reparto NUEVO antes de que el
    // estado lo haya recogido (setState no es inmediato), y con el de la pantalla se
    // calculaba el del día anterior.
    const ordenDeComidas = ({ numComidas: n, tipoDia: td, opcionPeri: op, momentoEntreno: me, unica }) => {
        const baseMeals = unica ? ['C1'] : (n === 3 ? ['C1', 'C2', 'C3'] : ['C1', 'C2', 'C3', 'C4']);
        if (td === 'descanso') return baseMeals;
        const periMeals = op === 'intra_post' ? ['Intra', 'Post'] :
                         op === 'solo_post' ? ['Post'] :
                         op === 'solo_intra' ? ['Intra'] : [];
        if (periMeals.length === 0) return baseMeals;
        const result = [...baseMeals];
        // single mode: peri after the one comida; otherwise spliced at the training moment.
        result.splice(unica ? baseMeals.length : me, 0, ...periMeals);
        return result;
    };

    // EL PERI SIN OBJETIVO NO SALE (Jesús, reunión del 25-09, en la cuenta de Paco López: el
    // intra y el post con «Objetivo · 0P · 0H» y «¿por qué sale esto?»). Un peri a cero es
    // decisión del coach: esa comida no existe para ese cliente. Solo se quita si además está
    // vacía, para no esconder lo que alguien ya montó; y mientras el reparto no ha llegado
    // (sin `distribution`) no se decide nada.
    const periSinObjetivo = (k) => {
        if (k !== 'Intra' && k !== 'Post') return false;
        const t = distribTargetsOverlay?.[k] || distribution?.periworkout?.[k];
        if (!distribution && !distribTargetsOverlay) return false;
        const sinNada = !((mealsData?.[k]?.alimentos) || []).length;
        return sinNada && !((t?.P || 0) > 0 || (t?.H || 0) > 0);
    };
    // SIN PERIENTRENO EN SU DIETA: los macros de perientreno de su ficha (los que pone el coach)
    // a cero. Entonces la configuración del día no ofrece elegir perientreno (ConfigSection,
    // `sinPeri`). Se mira la FICHA y no el reparto del día: quien sí tiene peri y elige «Sin
    // perientreno» un día también tiene ese reparto a cero, y se quedaría sin poder volver.
    const periDeFicha = profile?.macros_periworkout;
    const sinPeri = tipoDia === 'entrenamiento' && !!periDeFicha
        && !(Number(periDeFicha.protein ?? periDeFicha.proteinas ?? 0) > 0)
        && !(Number(periDeFicha.carbs ?? periDeFicha.hidratos ?? 0) > 0);
    const getMealOrder = () => ordenDeComidas({
        numComidas, tipoDia, opcionPeri, momentoEntreno, unica: singleMeal,
    }).filter((k) => !periSinObjetivo(k));

    // Mantener la comida seleccionada (vista master-detail) válida al cambiar la config
    useEffect(() => {
        const order = getMealOrder();
        if (!order.includes(selectedMeal)) setSelectedMeal(order[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numComidas, tipoDia, opcionPeri, momentoEntreno, singleMeal]);

    // Calculations
    const calculateMealMacros = (mealKey) => {
        const foods = mealsData[mealKey]?.alimentos || [];
        return foods.reduce((total, f) => ({
            P: total.P + (f.macros_efectivos?.P || 0),
            H: total.H + (f.macros_efectivos?.H || 0),
            G: total.G + (f.macros_efectivos?.G || 0)
        }), { P: 0, H: 0, G: 0 });
    };

    const calculateDayMacros = () => {
        return getMealOrder().reduce((total, key) => {
            const m = calculateMealMacros(key);
            return { P: total.P + m.P, H: total.H + m.H, G: total.G + m.G };
        }, { P: 0, H: 0, G: 0 });
    };

    /**
     * LO QUE SE GUARDA ES LO QUE SE VE (Francisco, 29-08-2026).
     *
     * `mealsData` se queda con TODAS las comidas que alguna vez tuvieron alimentos, también
     * las que el reparto de ahora ya no enseña. La pantalla pinta y suma por `getMealOrder()`,
     * así que el día se ve perfecto -- pero los cinco guardados mandaban `mealsData` entero.
     *
     * Lo reportó guardando una favorita: su día tenía 3 comidas más intra y post, y la
     * favorita salió con SEIS, con una «Comida 4» que él no había puesto. Era la suya de
     * antes de bajar de 4 a 3 comidas, invisible desde entonces. Reproducido: al pasar a 3, el
     * autoguardado seguía mandando `["C1","C4","Intra","Post","C2","C3"]` con `num_comidas: 3`.
     *
     * El mismo agujero tiene otra puerta: en un día de descanso el intra y el post no se
     * pintan, y si traían alimentos se guardaban igual. El backend ya lo resolvía al aplicar
     * una favorita (`descartar_sin_objetivo` en refit-diet, con este mismo motivo escrito:
     * «si quedaran alimentos ocultos, el autosave los persistiría»); faltaba aquí.
     */
    const comidasDelReparto = () => {
        const orden = getMealOrder();
        return Object.fromEntries(Object.entries(mealsData).filter(([k]) => orden.includes(k)));
    };


    // Guard: only honor the volcado if its meal still exists in the current layout (e.g. the
    // user dropped from 4 to 3 meals after volcando to C4 → ignore, don't lock everything).
    const activeVolcado = (volcadoMeal && getMealOrder().includes(volcadoMeal)) ? volcadoMeal : null;

    const getMealTarget = (mealKey) => {
        // Volcado (Calma comidaConMacrosVolcadas): the chosen meal absorbs the day's remaining
        // macros - but ONLY over the REGULAR comidas budget; peri (intra/post) is excluded from
        // the volcado (Calma's "Macros para las comidas" = 190/130/60 ≠ day total incl. peri).
        if (activeVolcado) {
            const isPeriMeal = mealKey === 'Intra' || mealKey === 'Post';
            if (mealKey === activeVolcado) {
                const regulars = getMealOrder().filter(k => !['Intra', 'Post'].includes(k));
                const dist = distribution?.comidas || {};
                const budget = regulars.reduce((acc, k) => {
                    const t = dist[k] || {};
                    return { P: acc.P + (t.P || 0), H: acc.H + (t.H || 0), G: acc.G + (t.G || 0) };
                }, { P: 0, H: 0, G: 0 });
                const otherServed = regulars.filter(k => k !== activeVolcado).reduce((acc, k) => {
                    const m = calculateMealMacros(k);
                    return { P: acc.P + m.P, H: acc.H + m.H, G: acc.G + m.G };
                }, { P: 0, H: 0, G: 0 });
                const r1 = (v) => Math.max(0, Math.round(v * 10) / 10);
                return { P: r1(budget.P - otherServed.P), H: r1(budget.H - otherServed.H), G: r1(budget.G - otherServed.G) };
            }
            // Peri keeps its normal peri target (just locked from editing); other regular meals
            // are locked to their served macros (cuadrada).
            if (isPeriMeal) return distribution?.periworkout?.[mealKey] || { P: 0, H: 0, G: 0 };
            return calculateMealMacros(mealKey);
        }

        // Volcado overlay takes absolute precedence
        if (distribTargetsOverlay?.[mealKey]) return distribTargetsOverlay[mealKey];

        if (!distribution) return { P: 0, H: 0, G: 0 };
        if (mealKey === 'Intra' || mealKey === 'Post') {
            return distribution.periworkout?.[mealKey] || { P: 0, H: 0, G: 0 };
        }
        return distribution.comidas?.[mealKey] || { P: 0, H: 0, G: 0 };
    };

    const getMealRemaining = (mealKey) => {
        const target = getMealTarget(mealKey);
        const served = calculateMealMacros(mealKey);
        return {
            P: Math.max(0, target.P - served.P),
            H: Math.max(0, target.H - served.H),
            G: Math.max(0, target.G - served.G)
        };
    };

    const getMealStatus = (mealKey) => {
        const target = getMealTarget(mealKey);
        const served = calculateMealMacros(mealKey);
        const foods = mealsData[mealKey]?.alimentos || [];
        if (foods.length === 0) return 'empty';
        // Calma margenValido = 4, INCLUSIVO desde el 3-09: «de 1 a 4, falte o sobre, es
        // válido» (punto 11.1, y la decisión de Francisco del margen plano en todo). El
        // número no se escribe aquí: vive en `lib/exceso` (`margenDe`, ya sin estrechado).
        const margin = MARGEN;
        const isPeriMeal = mealKey === 'Intra' || mealKey === 'Post';
        const mP = margenDe(target.P);
        const mH = margenDe(target.H);
        const mG = margenDe(target.G);
        // LOS TRES MACROS CON LA MISMA VARA (5-09, ficha 34). Aquí la proteína era «cubierta
        // basta» (`>= -mP`), así que una comida con 12,8 g de proteína de más salía
        // «cuadrada» mientras su fila de dentro decía «sobran 12,8» en naranja. El criterio
        // de pasarse vive en `lib/exceso`, que desde hoy cuenta la proteína.
        const pOk = Math.abs(target.P - served.P) <= mP;
        const hOk = Math.abs(target.H - served.H) <= mH;
        const gOk = isPeriMeal || Math.abs(target.G - served.G) <= mG;
        if (pOk && hOk && gOk) return 'cuadrada';
        if (excesos(served, target, { margen: margin, esPeri: isPeriMeal }).length) return 'sobra';
        return 'falta';
    };

    // LO QUE YA ESTÁ HECHO SE ABRE SOLO; LO QUE FALTA, NO.
    //
    // Jesús pedía que la fila dijera lo que hay dentro y no solo el estado: «lo que decide a
    // las nueve de la noche es qué comí, no en qué estado está la fila». Se probó metiendo los
    // alimentos en la propia fila y quedaban cortados, así que Francisco lo mandó quitar.
    // Esto lo resuelve por el otro lado: si la comida está cuadrada, se despliega y se leen
    // sus alimentos enteros, con sus cantidades y sin cortar nada.
    //
    // Solo al cargar el día, y una vez: después mandan los dedos del cliente. Si abre o cierra
    // algo, se respeta hasta que cambie de día. Por eso la marca lleva la fecha.
    const diaYaDesplegado = useRef(null);
    useEffect(() => {
        if (diaYaDesplegado.current === currentDate) return;
        if (!Object.keys(mealsData || {}).length) return;      // aún no ha llegado la dieta
        const hechas = getMealOrder().filter(k => getMealStatus(k) === 'cuadrada');
        diaYaDesplegado.current = currentDate;
        if (!hechas.length) return;
        setExpandedMeals(previo => ({ ...previo, ...Object.fromEntries(hechas.map(k => [k, true])) }));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- se dispara al cambiar el día
        // o al llegar sus datos; meter las funciones de cálculo lo relanzaría en cada render.
    }, [currentDate, mealsData]);

    // Familias que se calibran, con sus tramos y cómo se llaman para el cliente. Los
    // gramos son los de la spec (17-07) y los mismos que aplica el backend.
    const FAMILIAS_CALIBRADAS = {
        fruto_seco: { etiqueta: 'frutos secos', tramos: [20, 40], campo: 'acum_fs' },
        cereal_pan: { etiqueta: 'cereales y pan', tramos: [50, 100], campo: 'acum_cp' },
    };

    /** Guarda lo que lleva el día de cada familia y avisa UNA vez al cruzar cada tramo.
     *
     * Jesús, 13-08: «un aviso una sola vez al cruzar cada tramo, sin preguntar nada». Es
     * informativo: se dice lo que acaba de pasar (a partir de ahí su proteína cuenta) y se
     * sigue. El tramo avisado se recuerda para no repetirlo mientras el día siga ahí, y
     * baja solo si el cliente quita gramos y vuelve al tramo anterior. */
    const anotarCalibracion = (pcts, comidasCalibradas = null) => {
        if (!pcts) return;
        const una = Object.values(pcts)[0];       // el tramo es del día: todas las comidas igual
        if (!una) return;
        setAcumFamilias({
            fruto_seco: { gramos: una.acum_fs ?? 0, pct: una.pct_fs ?? 0 },
            cereal_pan: { gramos: una.acum_cp ?? 0, pct: una.pct_cp ?? 0 },
        });
        for (const [bloque, cfg] of Object.entries(FAMILIAS_CALIBRADAS)) {
            const g = (bloque === 'fruto_seco' ? una.acum_fs : una.acum_cp) ?? 0;
            const tramo = g > cfg.tramos[1] ? 2 : g > cfg.tramos[0] ? 1 : 0;
            const previo = tramoAvisado.current[bloque];
            // EL CARTEL SE VA SOLO, TIENE ASPA Y DEJA DE ESTAR CUANDO DEJA DE SER VERDAD
            // (Jesús, 15-08, fallo 37): «se quedó minutos en pantalla, siguió visible después
            // de borrar el alimento que lo provocó, y llegó a tapar el título de un panel».
            // El id fijo por familia es lo que permite retirarlo al bajar de tramo -- y de
            // paso evita que se apilen dos avisos de lo mismo.
            const idAviso = `umbral-${bloque}`;
            if (previo !== null && tramo > previo) {
                toast.info(tramo === 2
                    ? `Has pasado de ${cfg.tramos[1]} g de ${cfg.etiqueta} hoy: su proteína ya te cuenta entera`
                    : `Has pasado de ${cfg.tramos[0]} g de ${cfg.etiqueta} hoy: su proteína empieza a contarte a la mitad`,
                    { id: idAviso, duration: 7000, closeButton: true });
                // Y LAS COMIDAS YA MONTADAS SE RECUADRAN SOLAS (doc 57, F3): al cruzar el
                // tramo, la proteína de esa familia cambia de cuenta en TODAS las comidas
                // del día, también en las que ya estaban cuadradas. Antes se quedaban con
                // «sobran X g» sin que el cliente hubiera tocado nada. Solo las comidas en
                // modo Automático (Manual es «lo dejo como lo pongas») y solo una vez por
                // familia y día (ver recuadresHechos).
                const guard = `${bloque}:${currentDate}`;
                if (!recuadresHechos.current.has(guard)) {
                    // Y SOLO LAS QUE DEJAN DE ESTAR CUADRADAS (José Luis, 06-10: «tengo las
                    // cantidades cuadradas a mi gusto con el ok verde, doy guardar y modifica
                    // a su gusto las cantidades»). Se recuadraba toda comida con un alimento
                    // de la familia, también la que acababa de guardar a mano y que seguía
                    // dentro del margen con la calibración nueva: no había nada que ajustar
                    // y se le cambiaban las cantidades. La regla vive en lib/recuadreAlCruzar.
                    const afectadas = comidasARecuadrar({
                        orden: getMealOrder(), mealsData, bloque,
                        comidas: comidasCalibradas, objetivoDe: getMealTarget,
                    });
                    if (afectadas.length) {
                        recuadresHechos.current.add(guard);
                        afectadas.forEach(k => cuadrarComida(k, { silencioso: true }));
                        toast.info(afectadas.length === 1
                            ? `Hemos recuadrado una comida que ya tenías creada: con el cambio de ${cfg.etiqueta} había que ajustar sus cantidades.`
                            : `Hemos recuadrado ${afectadas.length} comidas que ya tenías creadas: con el cambio de ${cfg.etiqueta} había que ajustar sus cantidades.`,
                            { id: `recuadre-${bloque}`, duration: 8000, closeButton: true });
                    }
                }
            } else if (previo !== null && tramo < previo) {
                toast.dismiss(idAviso);
            }
            tramoAvisado.current[bloque] = tramo;
        }
    };

    // ── Calibración progresiva (proteína vegetal por TOTAL del DÍA) ─────────────
    // Spec 17-07-2026, con la corrección de Jesús del 13-08: tras CUALQUIER cambio de
    // composición (añadir, quitar, editar cantidades, aplicar menú, repetir, cuadrar...),
    // el backend recalcula los macros de TODO el día. El tramo lo decide el total del día
    // de cada familia, así que es el mismo para todas las comidas y editar una comida
    // puede cambiar cualquier otra. La firma solo mira ids+cantidades, así que cuando
    // vuelven los macros recalibrados la firma no cambia y no hay bucle.
    const calibracionSig = JSON.stringify(getMealOrder().map(k => [k,
        (mealsData[k]?.alimentos || []).map(a => [a.alimento_id ?? null, a.cantidad_g ?? 0])]));
    useEffect(() => {
        const order = getMealOrder();
        if (!order.some(k => (mealsData[k]?.alimentos || []).length > 0)) return;
        let cancelado = false;
        const pedirCalibracion = () => api('/api/calculator/calibrar-dia', {
            method: 'POST',
            body: JSON.stringify({
                meal_order: order,
                comidas: Object.fromEntries(order.map(k => [k,
                    (mealsData[k]?.alimentos || []).map(a => ({
                        alimento_id: a.alimento_id ?? null,
                        cantidad_g: a.cantidad_g ?? 0,
                    }))])),
            })
        });
        const timer = setTimeout(async () => {
            try {
                // Un reintento: la mayoría de los fallos aquí son un corte de red de un
                // segundo, y perder la calibración cambia los macros que se ven y se guardan.
                let res;
                try {
                    res = await pedirCalibracion();
                } catch {
                    if (cancelado) return;
                    res = await pedirCalibracion();
                }
                if (cancelado || !res?.comidas) return;
                setCalibracionFallida(false);
                anotarCalibracion(res.pcts, res.comidas);
                setMealsData(prev => {
                    const next = { ...prev };
                    for (const k of order) {
                        if (!prev[k]) continue;
                        const resp = res.comidas[k] || [];
                        next[k] = {
                            ...prev[k],
                            alimentos: (prev[k].alimentos || []).map((a, i) => {
                                const r = resp[i];
                                if (!r || !r.macros_efectivos) return a; // alimento desconocido: se conserva
                                return {
                                    ...a,
                                    macros_efectivos: r.macros_efectivos,
                                    macros_brutos: r.macros_brutos || a.macros_brutos,
                                    que_cuenta: r.que_cuenta || a.que_cuenta,
                                    // De qué familia calibrada es (o null): lo necesita el
                                    // contador de la línea para saber qué cuenta enseñar.
                                    bloque: r.bloque ?? null,
                                    // Y si su proteína llega al tercio, que es la puerta de
                                    // entrada de la calibración (punto 133): sin esto el
                                    // contador le prometía tramos a alimentos cuya proteína
                                    // no cuenta nunca.
                                    proteina_cuenta: r.proteina_cuenta ?? true,
                                    // Y si además CRECE con la cantidad del día. Los panes
                                    // proteicos pasan la puerta pero no tienen tramo: ya les
                                    // cuenta entera desde el primer gramo, y el contador les
                                    // decía «todavía no te cuenta».
                                    proteina_crece: r.proteina_crece ?? true,
                                };
                            }),
                        };
                    }
                    return next;
                });
            } catch (e) {
                // Se conservan los macros previos, pero SIN calibrar: hay que decirlo, porque
                // el día se guarda con estos números y son los que salen en el PDF.
                if (!cancelado) setCalibracionFallida(true);
            }
        }, 300);
        return () => { cancelado = true; clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calibracionSig, calibracionIntento]);

    // DE CUÁNTO EN CUÁNTO SE MUEVEN LOS GRAMOS con los botones − y +.
    //
    // Aquí había una tabla propia que decía «todo lo demás, ±1 g», y el servidor tiene la suya
    // y dice ±5. Así que el cliente subía de gramo en gramo y en cuanto la app tocaba esa
    // cantidad se la dejaba en un múltiplo de 5. En el intra se veía en todos los ingredientes
    // (Francisco, 27-08). La regla, con los números que lo destapan, en lib/pasoDeCantidad.
    const getQuantityIncrement = (food) =>
        pasoDeCantidad(food, esPorUnidad(food), pesoUnidad(food));

    // ¿Este alimento se cuenta por unidades (huevos, piezas de fruta, lonchas)?
    // Segun por donde haya entrado, el campo se llama de tres maneras distintas:
    // `unidades` (catalogo y handleAddFood), `por_unidad` (algunos menus) y `unidad`
    // ('unidades' | 'g', que es lo que se guarda en la dieta). Mirar solo uno dejaba
    // huevos moviendose de gramo en gramo.
    const esPorUnidad = (food) =>
        Boolean(food && (food.por_unidad ?? food.unidades ?? (food.unidad === 'unidades')));

    // Lo que pesa una unidad. Nunca 0: dividir por el peso es como se pasa a unidades.
    const pesoUnidad = (food) => (food?.peso_unidad || food?.racion || 100) || 100;

    // Cómo se escribe la cantidad: «2 ud» los de unidades, «120 g» los de peso. La
    // equivalencia de la unidad («2 ud (53 g)») la pone quien lo pinta, que es el que sabe
    // el sitio que tiene -- ver `IngredientRow` en MealCard.
    const formatFoodQuantity = (food) => {
        if (!food) return '0 g';
        // «Una cucharadita», «al gusto»: los del postentreno (lib/cantidadConNombre).
        const conNombre = cantidadConNombre(food);
        if (conNombre) return conNombre;
        const qty = food.cantidad_g || 0;
        const isPorUnidad = esPorUnidad(food);
        const unitWeight = pesoUnidad(food);
        if (isPorUnidad && unitWeight > 0) {
            return `${num1(Math.round((qty / unitWeight) * 2) / 2)} ud`;
        }
        // Los líquidos, en mililitros (Jesús, 27-09: «nadie pesa una bebida: se mira la botella»).
        return `${num1(Math.round(qty))} ${food.es_liquido ? 'ml' : 'g'}`;
    };

    // Food operations
    // Aquí vivía handleAddFood, que solo lo llamaba ese buscador. Lo que hacía -- resolver
    // las unidades del alimento y añadirlo a la comida -- lo hace hoy BuildMealModal.

    /**
     * Cambia la cantidad de un alimento y reajusta sus macros, sin llamar al servidor.
     *
     * Calma lo hace igual (K() = macros POST-REGLA x cantidad) y sin await, para que
     * pulsar -/+ rapido no deje `cantidad_g` desincronizada de los macros.
     *
     * La clave es "post-regla". Antes esto escalaba los campos crudos del catalogo
     * (`food.proteinas`...), que son los de la ETIQUETA: al tocar -/+ en un pan se le
     * colaban 6,8 g de proteina y 1,4 g de grasa que el metodo no cuenta, y el dia
     * pasaba a contar de mas hasta que el servidor recalculaba. Peor aun: como al
     * guardar un alimento no se guardan esos campos crudos, en los dias normales
     * `food.proteinas` ni existe y los macros se iban a 0.
     *
     * Ahora se escalan los `macros_efectivos` que ya tiene el alimento, que salieron
     * del motor con las reglas aplicadas. Es EQUIVALENTE a recalcular porque los
     * macros del metodo son lineales con la cantidad: las reglas (que() y el 25%)
     * deciden que cuenta y cuanto por 100 g / racion, no dependen de cuanto pongas.
     *
     * La calibracion progresiva del dia (cereales/panes y frutos secos por acumulado)
     * SI depende del resto del dia, pero de eso ya se encarga el recalculo del
     * servidor que se dispara tras cada cambio; esto es solo el eco inmediato.
     */
    /**
     * LA REGLA DE TRES NO VALE PARA UN ALIMENTO CALIBRADO (Francisco, 27-08).
     *
     * Al cambiar la cantidad, aquí se escalaban los macros en proporción para que el número se
     * moviera con el dedo y no esperase al servidor. Eso es exacto en 3.157 alimentos del
     * catálogo, y **falso en 62**: los que se calibran (frutos secos, cereales y panes) no
     * cuentan su proteína en proporción, sino POR ESCALONES según los gramos que lleve el DÍA
     * de esa familia -- hasta 20 g nada, de 20 a 40 la mitad, de 40 en adelante toda.
     *
     * Medido en la app, bajando unas almendras con el «−»:
     *
     *     45 -> 40 g   la pantalla pintaba 9,2 g de proteína  ·  el motor dice 4,6   (el doble)
     *     25 -> 20 g   la pantalla pintaba 2,3                ·  el motor dice 0
     *
     * Al cruzar un escalón la regla de tres se pasa de largo, y 300 ms después llega el
     * recálculo del servidor y el número cambia solo delante del cliente. Y por el camino la
     * comida podía darse por cuadrada con una proteína que no la respalda ningún motor.
     *
     * Así que aquí ya no se calcula nada de eso: los calibrados cambian de cantidad y sus
     * macros los pone quien sabe el día entero (`/calibrar-dia`, 300 ms después). El resto
     * siguen escalando, que en ellos la proporción es exacta y el número responde al instante.
     *
     * Cómo se sabe si uno se calibra: `bloque` y `proteina_crece` los escribe el propio
     * recálculo en cada alimento, y `/macros-efectivos` devuelve `bloque` al añadirlo. Si no
     * consta -- un alimento recién puesto que aún no ha pasado por ninguno de los dos --, se
     * trata como calibrado: esperar medio segundo no rompe nada y enseñar el doble sí.
     */
    const seCalibra = (food) => (
        food?.bloque != null ? food.proteina_crece !== false
        : (food?.macros_efectivos === undefined || food?.macros_efectivos === null));

    const scaleFood = (food, newQty) => {
        const qtyPrevia = food.cantidad_g || 0;
        const ef = food.macros_efectivos;

        if (qtyPrevia > 0 && ef && typeof ef.P === 'number' && !seCalibra(food)) {
            // Desde el punto de partida, no desde el último redondeo: con el «+» de poco en
            // poco el redondeo se comía cada subida (lib/escalarMacros, 27-09).
            return escalarMacros(food, newQty);
        }

        // Sin cantidad previa, sin macros ya calculados, o calibrado: no hay proporcion que
        // aplicar. Se deja la cantidad y se conservan los macros que hubiera; el recalculo del
        // servidor los pondra bien. Inventarlos con los crudos es lo que fallaba antes.
        return { ...food, cantidad_g: newQty };
    };

    // Lo minimo que tiene sentido de un alimento: de los que van por unidades, una
    // unidad entera (medio huevo no se pone en una dieta); del resto, 1 g.
    const cantidadMinima = (food) => (esPorUnidad(food) ? pesoUnidad(food) : 1);

    /**
     * El aviso de los dos caminos que sacan un alimento por quedarse corto de cantidad.
     *
     * El respiro de antes no es adorno. Por el camino de escribir la cantidad, esto se llama
     * desde el `onKeyDown` de la casilla, y la casilla se desmonta en ese mismo instante
     * (`setEditingQuantity(null)`). El diálogo devuelve el foco a quien lo abrió, y quien lo
     * abrió ya no existe: se abría y se cerraba en el mismo suspiro, así que no se veía nada
     * y el alimento no se quitaba ni se quedaba. Se espera al siguiente ciclo, cuando la
     * casilla ya se ha ido y el foco se ha asentado.
     */
    const preguntarSiLoQuito = async (food) => {
        await new Promise((sigue) => setTimeout(sigue, 0));
        return confirm({
            title: `¿Quitar ${food?.nombre || 'este alimento'}?`,
            description: 'Bajar de su cantidad mínima lo saca de la comida. Los demás ingredientes se quedan como están.',
            confirmLabel: 'Quitar',
            danger: true,
        });
    };

    /**
     * Cambia la cantidad de un alimento, y lo QUITA si baja de su minimo.
     *
     * Antes el suelo era `Math.max(1, ...)`: 1 gramo. En un alimento por unidades eso
     * dejaba "0 ud" en pantalla -- un huevo que ni esta ni deja de estar -- pero
     * seguia contando su gramo en los macros de la comida. Bajar del minimo es decir
     * "quitalo", asi que se quita.
     *
     * Y PREGUNTA ANTES (punto 129, el tercer camino). Jesús nombró Vaciar y la papelera,
     * pero había un tercero que se llevaba el alimento SIN AVISO NINGUNO Y SIN DESHACER: a
     * fuerza de «−» se cruza el mínimo y desaparece. Es el más fácil de disparar sin querer
     * de los tres -- el botón está a un dedo de la papelera y se pulsa repetido, sin mirar --
     * y era el único que no tenía red. Francisco lo cerró el 26-08: «también debe preguntar».
     */
    const updateFoodQuantity = async (mealKey, foodIndex, delta) => {
        // Lo único que se mira fuera del `setMealsData` es si esta pulsación cruza el mínimo,
        // porque preguntar no se puede hacer dentro del actualizador. El resto sigue yendo
        // por el `prev`, que es lo que aguanta las pulsaciones seguidas.
        const actual = (mealsData[mealKey]?.alimentos || [])[foodIndex];
        if (actual) {
            const paso = delta !== null ? delta : getQuantityIncrement(actual);
            if ((actual.cantidad_g || 0) + paso < cantidadMinima(actual)) {
                if (!(await preguntarSiLoQuito(actual))) return;
            }
        }
        setMealsData(prev => {
            const foods = [...(prev[mealKey]?.alimentos || [])];
            const food = foods[foodIndex];
            if (!food) return prev;
            const increment = delta !== null ? delta : getQuantityIncrement(food);
            const bruta = (food.cantidad_g || 0) + increment;
            const minimo = cantidadMinima(food);
            if (bruta < minimo) {
                return { ...prev, [mealKey]: { alimentos: foods.filter((_, i) => i !== foodIndex) } };
            }
            // El tope de 2000 g también con el «+»: verduras y bebidas suben de 50 en 50 y un
            // alimento por unidades, de una unidad entera (Jesús, 15-08, fallo 28).
            // Y el tope RAZONABLE de ese alimento, que es otro (16-08): a golpe de «+» se
            // llegaba a 1.000 g de leche de almendras sin que nadie dijera nada. Se avisa y
            // se pone igual, como hace el asistente.
            const aviso = avisoRazonable(food, bruta, {
                porUnidad: esPorUnidad(food), pesoUnidad: pesoUnidad(food),
            });
            if (aviso) toast.warning(aviso, { id: 'tope-razonable' });
            // EL POST: la bebida vegetal no pasa de los 4 g de grasa (lib/topeGrasaPost).
            let cantidad = bruta;
            if (mealKey === 'Post') {
                const tope = cantidadConTope(foods, foodIndex, bruta);
                if (tope.frenada) toast.info(tope.aviso || AVISO_TOPE_GRASA_POST, { id: 'tope-grasa-post' });
                cantidad = tope.cantidad;
            }
            foods[foodIndex] = scaleFood(food, cantidad);
            return { ...prev, [mealKey]: { alimentos: foods } };
        });
    };

    /**
     * Cantidad escrita a mano. En los alimentos por unidades se escriben UNIDADES,
     * que es como los piensa el usuario ("2 huevos", no "126 g de huevo"); aqui se
     * pasan a gramos, que es como se guardan. Acepta medias unidades.
     *
     * LO QUE NO SE ENTIENDE NO BORRA NADA (Jesús, 15-08, fallo 4). Escribir «-50» o «abc»
     * eliminaba el ingrediente de la comida: el texto no era un número, el número salía 0 y
     * el 0 estaba por debajo del mínimo, que significa «quítalo». Un guion puesto sin querer
     * y el alimento desaparecía sin decir nada y sin forma de recuperarlo. Ahora se rechaza
     * el valor y se queda el que había. Bajar de verdad del mínimo (escribir 0) sí lo quita,
     * porque es lo que se está pidiendo, pero pregunta antes (punto 129) y se puede deshacer.
     * Los dos, igual que en Vaciar: preguntar evita el toque sin querer y deshacer arregla el
     * «sí» dado deprisa. No sobra ninguno de los dos.
     */
    const updateFoodQuantityDirect = async (mealKey, foodIndex, valor) => {
        const food = (mealsData[mealKey]?.alimentos || [])[foodIndex];
        setEditingQuantity({ mealKey: null, foodIndex: null });
        if (!food) return;

        const lectura = leerCantidad(valor, {
            porUnidad: esPorUnidad(food),
            pesoUnidad: pesoUnidad(food),
            minimo: cantidadMinima(food),
            alimento: food,
        });

        if (lectura.estado === 'no_es_numero') { toast.error(AVISO_NO_ES_NUMERO); return; }
        if (lectura.estado === 'negativo') { toast.error(AVISO_NEGATIVO); return; }

        if (lectura.estado === 'por_debajo_del_minimo') {
            if (!(await preguntarSiLoQuito(food))) return;
            const previo = mealsData[mealKey];
            const quedan = { ...previo, alimentos: (previo?.alimentos || []).filter((_, i) => i !== foodIndex) };
            setMealsData(prev => ({
                ...prev,
                [mealKey]: { ...prev[mealKey], alimentos: (prev[mealKey]?.alimentos || []).filter((_, i) => i !== foodIndex) },
            }));
            // El aviso dice lo que ha pasado; la vuelta atrás se queda en la tarjeta de la
            // comida y sin cuenta atrás (Jesús, 6-09). Ver `deshacerComida`.
            toast.success(`${food.nombre} fuera de la comida`, { duration: 8000 });
            setDeshacerComida(prev => ({
                ...prev,
                [mealKey]: { antes: previo, huella: huellaConCantidades(quedan),
                             texto: `Quitaste ${food.nombre}.` },
            }));
            return;
        }

        if (lectura.estado === 'pasa_del_tope') toast.warning(avisoDelTope(lectura.gramos));
        // Dentro del tope duro y aun así demasiado para una comida (un litro de leche): se
        // dice y se pone, que es como se comporta el asistente.
        if (lectura.aviso) toast.warning(lectura.aviso, { id: 'tope-razonable' });
        setMealsData(prev => {
            const foods = [...(prev[mealKey]?.alimentos || [])];
            if (!foods[foodIndex]) return prev;
            let cantidad = Math.round(lectura.gramos);
            // EL POST: la bebida vegetal no pasa de los 4 g de grasa (lib/topeGrasaPost).
            if (mealKey === 'Post') {
                const tope = cantidadConTope(foods, foodIndex, cantidad);
                if (tope.frenada) toast.info(tope.aviso || AVISO_TOPE_GRASA_POST, { id: 'tope-grasa-post' });
                cantidad = tope.cantidad;
            }
            foods[foodIndex] = scaleFood(foods[foodIndex], cantidad);
            return { ...prev, [mealKey]: { ...prev[mealKey], alimentos: foods } };
        });
    };

    // LA PAPELERA PREGUNTA (punto 129 del artifact del 26-08: «la papelera de cada
    // ingrediente, igual. Las dos tienen que preguntar»).
    //
    // Era la única acción destructiva de la pantalla SIN ninguna red: un toque y el
    // alimento fuera, sin confirmar y sin deshacer, mientras que «Vaciar» -- que se lleva la
    // comida entera -- sí dejaba deshacer. La más pequeña era la más peligrosa, y encima el
    // icono está a un dedo del `−` de bajar gramos.
    //
    // Y YA NO PREGUNTA (Jesús, 27-09 22:45, con la captura del aviso «¿Quitar...?»: «fuera,
    // no tiene que confirmar nada: quito»). La papelera quita el alimento directamente.
    // Manda lo más reciente sobre el punto 129 del 26-08. «Vaciar», que se lleva la comida
    // entera, sigue preguntando.
    const removeFood = (mealKey, foodIndex) => {
        setMealsData(prev => ({
            ...prev,
            [mealKey]: { alimentos: (prev[mealKey]?.alimentos || []).filter((_, i) => i !== foodIndex) }
        }));
    };

    // Reordenar ingrediente hacia arriba - replica Calma Dieta.subir = mover(e, -1):
    // saca el elemento en i-1 y lo reinserta en i (swap adyacente con el anterior).
    const moveFoodUp = (mealKey, foodIndex) => {
        if (foodIndex <= 0) return;
        setMealsData(prev => {
            const foods = [...(prev[mealKey]?.alimentos || [])];
            const n = foods.splice(foodIndex - 1, 1);
            foods.splice(foodIndex, 0, n[0]);
            return { ...prev, [mealKey]: { ...prev[mealKey], alimentos: foods } };
        });
    };

    // VACIAR PREGUNTA (punto 129: «vaciar se lleva la comida entera de un toque»).
    //
    // No con el `confirm()` del navegador -- ese diálogo bloquea la pestaña entera y no pega
    // con el resto de la app --, sino con el de la casa, el mismo que ya protege a las
    // favoritas. Y el «Deshacer» de después se queda: preguntar evita el toque sin querer,
    // deshacer arregla el «sí» dado deprisa. No sobra ninguno de los dos.
    const clearMeal = async (mealKey) => {
        const previo = mealsData[mealKey];
        const nombre = mealInfo[mealKey]?.name || 'La comida';
        const cuantos = (previo?.alimentos || []).length;
        const adelante = await confirm({
            title: `¿Vaciar ${nombre}?`,
            description: cuantos === 1
                ? 'Se quita su único ingrediente. Podrás deshacerlo justo después.'
                : `Se quitan sus ${cuantos} ingredientes. Podrás deshacerlo justo después.`,
            confirmLabel: 'Vaciar',
            danger: true,
        });
        if (!adelante) return;
        setMealsData(prev => ({ ...prev, [mealKey]: { alimentos: [] } }));
        toast.success(`${nombre} vaciada`, { duration: 8000 });
        // Y el «Deshacer» se queda en la tarjeta, que aquí es donde más falta hace: la
        // comida se ha quedado vacía y el aviso de abajo se va solo (Jesús, 6-09).
        setDeshacerComida(prev => ({
            ...prev,
            [mealKey]: { antes: previo, huella: huellaConCantidades({ alimentos: [] }),
                         texto: `Vaciaste ${nombre.toLowerCase()}.` },
        }));
    };

    // Repeat from another day
    const loadRecentDiets = async () => {
        try {
            // `para`: el día abierto. El servidor solo ofrece comidas que se puedan
            // cuadrar con los macros de ESE día (caso 27; punto 14 del 23-08).
            // `hoy_cliente`: el día de ESTE reloj, que es el techo de la lista -- repetir
            // es mirar atrás, y sin techo salían primero los días de 2027 (punto 210).
            // `tipo_destino`: el tipo que el cliente tiene DELANTE en el selector. En un
            // día sin guardar, el servidor y la pantalla tenían cada uno su valor por
            // defecto y las etiquetas «Encaja / +12 G / Descanso» salían juzgadas con el
            // que el cliente no ve (3-09, montando el 21-12 desde el 3-09). La tercera
            // decía «Otro día» hasta el 6-09: ahora dice el tipo del día que se copia.
            const result = await api(
                `/api/diets/recent?limit=14&para=${currentDate}&hoy_cliente=${hoyISO()}&tipo_destino=${tipoDia}`);
            setRecentDiets(result.diets || []);
        } catch (err) {
            console.error('Error loading recent diets:', err);
            setRecentDiets([]);
        }
    };

    const openRepeatModal = async (mealKey) => {
        setRepeatMealModal({ open: true, mealKey });
        setSelectedDietForRepeat(null);
        await loadRecentDiets();
    };

    // EL DÍA VIENE POR PARÁMETRO, NO POR ESTADO (punto 4.3).
    //
    // Antes el modal hacía `setSelectedDietForRepeat(dia); copyMealFromDay(comida)` en la
    // misma línea, y `setState` de React es asíncrono: esta función se ejecutaba en el mismo
    // tick y leía el valor ANTERIOR del estado. De ahí las dos caras del fallo que reportó
    // Jesús, que son la misma:
    //
    //   - la primera vez el estado valía null -> "No hay alimentos en esa comida";
    //   - la segunda valía el día que había elegido en el intento anterior -> pedía el 3 de
    //     mayo y le copiaba el 17, y el aviso decía "Copiada Comida 1 del lun, 17 may".
    //
    // La vista previa nunca fallaba porque la pinta el modal con su propia selección, que sí
    // es la buena. No era una clave distinta: era el mismo dato leído un tick antes.
    const copyMealFromDay = async (sourceMealKey, dietElegida, { talCual = false } = {}) => {
        const targetMealKey = repeatMealModal.mealKey;
        const sourceDiet = dietElegida || selectedDietForRepeat;

        if (!sourceDiet || !sourceDiet.comidas || !sourceDiet.comidas[sourceMealKey]) {
            toast.error('No hay alimentos en esa comida');
            return;
        }
        
        const sourceAlimentos = sourceDiet.comidas[sourceMealKey].alimentos || [];
        if (sourceAlimentos.length === 0) {
            toast.error('Esa comida está vacía');
            return;
        }

        // «TAL CUAL» (Francisco, 01-10, por lo que pidió José Luis Huertas): las mismas
        // cantidades y en Manual, como entra una favorita. Lo de por defecto sigue siendo
        // ajustarla al hueco (punto 4.9 de Jesús, abajo); esto es la otra opción.
        if (talCual) {
            const esPeriDestino = ['Intra', 'Post'].includes(targetMealKey);
            setMealsData(prev => ({
                ...prev,
                [targetMealKey]: esPeriDestino
                    ? { alimentos: sourceAlimentos }
                    : { alimentos: sourceAlimentos, modo: 'manual' },
            }));
            setRepeatMealModal({ open: false, mealKey: null });
            setSelectedDietForRepeat(null);
            toast.success(`Copiada ${mealInfo[sourceMealKey]?.name || sourceMealKey} del ${formatDate(sourceDiet.fecha)} tal cual`);
            return;
        }
        
        // CUADRAR CON EL MISMO MOTOR QUE EL RECETARIO (punto 4.9).
        //
        // Aquí se escalaban las cantidades por el RATIO DE PROTEÍNA y ya: la proteína caía
        // cerca del objetivo y los hidratos y la grasa donde salieran. Con 30 P · 20 H · 10 G,
        // la prueba de Jesús dio 34,2 / 30,0 / 3,2 y la comida en rojo.
        //
        // Es decir: ni copiaba tal cual -- cambiaba las cantidades -- ni cuadraba. Lo peor de
        // las dos cosas. Y por el recetario sí cuadra, sin que nada dijera que los dos
        // caminos hacen cosas distintas. Ahora los dos llaman al mismo sitio.
        let scaledFoods = null;
        try {
            const r = await api('/api/calculator/cuadrar-comida', {
                method: 'POST',
                body: JSON.stringify({
                    items: sourceAlimentos.map(a => ({
                        alimento_id: a.alimento_id, cantidad_g: a.cantidad_g, nombre: a.nombre,
                    })),
                    mealKey: targetMealKey,
                    fecha: currentDate,
                    tipo_dia: tipoDia,
                    num_comidas: numComidas,
                    momento_entreno: momentoEntreno,
                    opcion_peri: opcionPeri,
                }),
            });
            scaledFoods = (r.items || []).length ? r.items : null;
            // YA NO SE AÑADEN ALIMENTOS AL REPETIR (Gonzalo, 28-09: «se inventó» chocolate,
            // turrón y lomo empanado). Si a la comida copiada le falta de dónde sacar un macro,
            // se le dice al cliente qué añadir; elegirlo es cosa suya (regla del 3-09).
            const NOMBRE = { P: 'proteína', H: 'hidratos', G: 'grasa' };
            const falta = (r.falta_fuente || []).map((m) => NOMBRE[m]).filter(Boolean);
            if (falta.length) {
                toast.info(`Para cuadrarla le falta algo con ${falta.length === 1 ? falta[0] : `${falta.slice(0, -1).join(', ')} y ${falta[falta.length - 1]}`}: añádelo con «Añadir ingrediente».`);
            }
            // Y SI LA CANTIDAD SE HA IDO, SE DICE (2-09). Cuadrando una comida de un solo
            // alimento el motor puede escribir 2.475 g de flan para llegar a la proteína del
            // día: más de lo que la propia pantalla deja teclear a mano. Antes se guardaba
            // en silencio y el cliente se encontraba dos kilos y medio en su comida.
            const pasados = (r.items || []).filter(i => i.pasa_de_razonable);
            if (pasados.length) {
                toast.warning(pasados.length === 1
                    ? `Para cuadrarla hemos puesto ${Math.round(pasados[0].cantidad_g)} g de ${pasados[0].nombre}. Es mucho más de lo normal: revísalo antes de guardar.`
                    : `Para cuadrarla hemos puesto cantidades muy altas de ${pasados.map(i => i.nombre).join(' y ')}. Revísalas antes de guardar.`);
            }
        } catch (err) {
            console.error('cuadrar-comida falló; se escala por proteína:', err);
        }

        // Si el servidor no responde se usa el escalado de antes. Es peor que cuadrar, pero
        // mucho mejor que dejar al cliente sin poder repetir su día.
        if (!scaledFoods) {
        const targetMacros = getMealTarget(targetMealKey);
        const sourceMacros = sourceAlimentos.reduce((acc, a) => ({
            P: acc.P + (a.macros_efectivos?.P || 0),
            H: acc.H + (a.macros_efectivos?.H || 0),
            G: acc.G + (a.macros_efectivos?.G || 0)
        }), { P: 0, H: 0, G: 0 });
        const scaleFactor = sourceMacros.P > 0 ? targetMacros.P / sourceMacros.P : 1;
        scaledFoods = [];
        for (const food of sourceAlimentos) {
            const scaledQuantity = Math.round(food.cantidad_g * scaleFactor);
            try {
                const result = await api('/api/calculator/macros-efectivos', {
                    method: 'POST',
                    body: JSON.stringify({
                        alimento_id: food.alimento_id,
                        cantidad_g: scaledQuantity,
                        es_vegano: false
                    })
                });
                scaledFoods.push({
                    ...food,
                    cantidad_g: scaledQuantity,
                    macros_efectivos: result.efectivos,
                    macros_brutos: result.brutos,
                    que_cuenta: result.que_cuenta
                });
            } catch (err) {
                // If recalc fails, use original scaled estimate
                scaledFoods.push({
                    ...food,
                    cantidad_g: scaledQuantity,
                    macros_efectivos: {
                        P: (food.macros_efectivos?.P || 0) * scaleFactor,
                        H: (food.macros_efectivos?.H || 0) * scaleFactor,
                        G: (food.macros_efectivos?.G || 0) * scaleFactor
                    }
                });
            }
        }
        }

        setMealsData(prev => ({
            ...prev,
            [targetMealKey]: { alimentos: scaledFoods }
        }));

        setRepeatMealModal({ open: false, mealKey: null });
        setSelectedDietForRepeat(null);
        toast.success(`Copiada ${mealInfo[sourceMealKey]?.name || sourceMealKey} del ${formatDate(sourceDiet.fecha)}, cuadrada a tu objetivo`);
    };

    // "Sugiéreme un menú": modal con dos pestañas. Biblioteca REAL (db.meal_library,
    // 266k comidas de clientes ya cuadradas con el método): menús por CERCANÍA al
    // objetivo de la comida, que se vuelcan tal cual (o ajustados con sus palancas).
    // Recetario (menu_templates, recetas ELM): la receta se cuadra a los macros al
    // elegirla (menu-apply). El modal carga sus propios datos; aquí solo se abre y
    // se vuelca lo que devuelve, que en ambos casos viene con la misma forma.
    const loadMenuOptions = (mealKey) => {
        setMenuOptionsModal({ open: true, mealKey });
    };

    const applyLibraryMenu = (menu) => {
        const mealKey = menuOptionsModal.mealKey;
        // Los items ya vienen del backend con las cantidades AJUSTADAS por las
        // palancas del menú y sus macros calculados: aquí solo se vuelcan.
        const foods = menu.items.map(item => ({
            alimento_id: item.alimento_id,
            nombre: item.nombre,
            cantidad_g: item.cantidad_g,
            macros_efectivos: item.macros_efectivos,
            macros_brutos: item.macros_brutos,
            que_cuenta: item.que_cuenta,
            categorias: item.categorias,
            racion: item.racion,
            unidades: item.unidades,
            url: item.url || null,   // para que el enlace salga ya, sin esperar a recargar el dia
        }));
        setMealsData(prev => ({ ...prev, [mealKey]: { alimentos: foods } }));
        setMenuOptionsModal({ open: false, mealKey: null });
        // Un menú compartido (29-09): usarlo desde «Sugiéreme» cuenta en el Ranking igual que
        // desde su ficha, y a su autor le llega el aviso. No si lo vuelca el equipo actuando
        // como el cliente: ese uso no es suyo (compartir también está apagado ahí).
        if (menu.compartido_id && !actuandoComoId()) {
            api(`/api/menus-compartidos/menu/${encodeURIComponent(menu.compartido_id)}/usado`, {
                method: 'POST', body: JSON.stringify({ como: 'comida' }),
            }).catch((err) => console.error('[uso del menú compartido]', err));
        }
        if (menu.origen === 'recetario') {
            // Pestaña Recetario: el backend ya ha cuadrado la receta a los macros
            toast.success(menu.clavado
                ? `${menu.nombre}: clava tu objetivo`
                : menu.cuadrada
                    ? `${menu.nombre}, cuadrada a tu objetivo`
                    : `${menu.nombre} añadida, ajusta a mano lo que falte`);
            return;
        }
        toast.success(menu.clavado
            ? 'Menú añadido: clava tu objetivo'
            : (menu.cuadrada || menu.ajustado)
                ? 'Menú añadido, ajustado a tu objetivo'
                : 'Menú añadido tal cual (menú real, cercano a tu objetivo)');
    };

    // Save & Copy
    const saveDiet = async () => {
        try {
            const res = await api('/api/diets', {
                method: 'POST',
                body: JSON.stringify({
                    fecha: currentDate,
                    tipo_dia: tipoDia,
                    num_comidas: numComidas,
                    momento_entreno: momentoEntreno,
                    opcion_peri: opcionPeri,
                    comidas: comidasDelReparto(),
                    macros_snapshot: distribution?.resumen,
                    distribution_targets: distribTargetsOverlay || null,
                    is_cuadrado: getDayStatus() === 'cuadrado',
                    comida_volcada: volcadoMeal,
                    base_updated_at: versionDiaRef.current,
                })
            });
            // La versión con la que seguimos trabajando es la que acaba de escribir el
            // servidor, igual que en el autoguardado: sin esto, guardar a mano dejaba la
            // referencia vieja y el siguiente guardado automático chocaba consigo mismo.
            if (res?.updated_at) versionDiaRef.current = res.updated_at;
            // Alguna comida se tocó por otro lado mientras esta pantalla la tenía abierta:
            // esa se ha respetado, y aquí se recarga para enseñar lo que hay de verdad.
            const chocaron = res?.conflictos || [];
            if (chocaron.length) {
                // `_dia` no es una comida: es el reparto del día (cuántas comidas, entreno o
                // descanso, peri). Nombrarlo por su clave sería enseñarle jerga al cliente.
                const nombres = chocaron
                    .map(k => (k === '_dia' ? 'El reparto del día' : (mealInfo[k]?.name || k)))
                    .join(' y ');
                toast(`${nombres} se había cambiado en otro sitio, así que dejé lo más `
                    + `reciente. Te lo recargo.`, { icon: '🔄' });
                await loadDiet(currentDate);
                return;
            }
            toast.success('Dieta guardada');
        } catch (err) { toast.error('Error guardando dieta'); }
    };

    /**
     * Copiar el día a otra fecha.
     *
     * Jesús, 15-08 (fallo 31): copió una dieta a otro día «sin un solo mensaje. Ni "copiado
     * a...", ni aviso de que ese día ya tenía algo, ni deshacer». Copiar PISA la dieta del
     * destino, así que antes se pregunta -- y se dice si allí había algo --, y al terminar
     * se dice qué se ha hecho y dónde.
     */
    const copyDiet = async () => {
        if (!copyDate) { toast.error('Selecciona una fecha'); return; }
        try {
            const sourceDiet = await api(`/api/diets/${currentDate}`);
            if (!sourceDiet || !sourceDiet.exists) {
                toast.error('No hay dieta guardada para hoy');
                return;
            }

            // Qué hay en el destino. Si no se puede mirar, se pregunta igual: mejor una
            // confirmación de más que pisar una dieta en silencio.
            let destino = null;
            try { destino = await api(`/api/diets/${copyDate}`); }
            catch (err) { console.error('[copiar dieta] no se pudo mirar el destino', err); }
            const destinoConDieta = Boolean(destino?.exists)
                && Object.values(destino.comidas || {}).some(c => (c?.alimentos || []).length > 0);
            const cuando = formatDate(copyDate);

            const adelante = await confirm({
                title: destinoConDieta ? `El ${cuando.toLowerCase()} ya tiene dieta` : `Copiar al ${cuando.toLowerCase()}`,
                description: destinoConDieta
                    ? 'Si sigues, la dieta de ese día se sustituye por esta. Eso no se puede deshacer.'
                    : 'Se copia este día entero: sus comidas, sus cantidades y su tipo de día.',
                confirmLabel: destinoConDieta ? 'Sustituirla' : 'Copiar',
                danger: destinoConDieta,
            });
            if (!adelante) return;

            // COPIAR SUSTITUYE DE VERDAD (31-08-2026).
            //
            // El cartel de arriba promete «la dieta de ese día se sustituye por esta», y no
            // era cierto: este POST fusiona (el servidor conserva lo que no le llega, para
            // que dos pestañas no se pisen), así que las comidas que el origen no tenía se
            // quedaban en el destino. Un cliente lo reportó el 31-08: copió un día y se
            // encontró unas lentejas «que se añadieron solas» -- eran las del día que creía
            // haber sustituido.
            //
            // `comidas_completas` es la puerta que el propio servidor dejó abierta para
            // quien de verdad quiera reemplazar el día entero. Aquí toca: el cliente acaba
            // de decir que sí a sustituirlo.
            //
            // Y se copia lo que el día de origen ENSEÑA, no lo que arrastra: un día que bajó
            // de cuatro comidas a tres guarda la cuarta escondida, y copiarla la haría
            // aparecer en el destino.
            const delOrigen = ordenDeComidas({
                numComidas: sourceDiet.num_comidas,
                tipoDia: sourceDiet.tipo_dia,
                opcionPeri: sourceDiet.opcion_peri,
                momentoEntreno: sourceDiet.momento_entreno,
                unica: sourceDiet.num_comidas === 1,
            });
            await api('/api/diets', {
                method: 'POST',
                body: JSON.stringify({
                    fecha: copyDate,
                    tipo_dia: sourceDiet.tipo_dia,
                    num_comidas: sourceDiet.num_comidas,
                    momento_entreno: sourceDiet.momento_entreno,
                    opcion_peri: sourceDiet.opcion_peri,
                    comidas: Object.fromEntries(delOrigen.map(
                        (k) => [k, (sourceDiet.comidas || {})[k] || { alimentos: [] }])),
                    comidas_completas: true,
                    macros_snapshot: sourceDiet.macros_snapshot,
                    distribution_targets: sourceDiet.distribution_targets,
                    is_cuadrado: sourceDiet.is_cuadrado,
                })
            });
            toast.success(destinoConDieta
                ? `Dieta copiada al ${cuando.toLowerCase()}, sustituyendo la que había`
                : `Dieta copiada al ${cuando.toLowerCase()}`);
            setCopyModalOpen(false);
            setCopyDate('');
        } catch (err) {
            console.error('[copiar dieta]', err);
            toast.error('No hemos podido copiar la dieta. Inténtalo de nuevo.');
        }
    };

    // ── Dietas favoritas (Calma guardarFavorita / favoritas) ──────────────────
    const loadDietFavorites = async () => {
        try {
            // Solo las de DÍA: desde el 25-08 conviven con las de comida en la misma
            // colección y sin el filtro se colaban aquí, donde no se pueden aplicar.
            const res = await api('/api/diets/favorites?ambito=dia');
            setDietFavorites(res.favorites || []);
        } catch (err) { setDietFavorites([]); }
    };

    const saveDietFavorite = async (name) => {
        try {
            await api('/api/diets/favorites', {
                method: 'POST',
                body: JSON.stringify({
                    name,
                    tipo_dia: tipoDia, num_comidas: numComidas,
                    momento_entreno: momentoEntreno, opcion_peri: opcionPeri,
                    comidas: comidasDelReparto(), macros_snapshot: distribution?.resumen,
                    distribution_targets: distribTargetsOverlay || null,
                })
            });
            // La confirmación dice DÓNDE vive lo guardado (doc 21-08): «Favorita
            // guardada» a secas no decía dónde volver a encontrarla. El último tramo
            // nombra el tipo de día CONTRARIO al guardado, que es cuando de verdad se
            // recuadra al aplicarla.
            // LA FRASE DECÍA LO CONTRARIO DE LO QUE PASA (5-09, prueba 1 de Gonzalo): «se
            // recuadrará sola si la pones en un día de descanso» daba a entender que en el
            // mismo tipo de día las cantidades vuelven tal cual, y no: al aplicar una favorita
            // SIEMPRE se recuadra a los macros del día (`refit-diet`), sea el tipo que sea.
            // Por eso a Gonzalo «las cantidades no eran exactamente las mismas» y le pareció
            // un fallo. Se dice lo que hace.
            // «CUANDO CREES», NO «CUANDO MONTES» (Francisco, 13-09, ficha 139). En toda la app
            // se dice crear desde el punto 7 del 23-08, y a este aviso se le había quedado la
            // palabra vieja.
            toast.success('Guardada · Ya es una de tus dietas', {
                description: 'La tienes en Una de mis dietas cuando crees cualquier día. '
                    + 'Al aplicarla se pone con estas cantidades; si no cuadra ese día, «Cuadrar el día».',
                duration: 8000,
            });
            loadDietFavorites();
        } catch (err) { toast.error('Error guardando favorita'); }
    };

    // El `cuadrarComida` del ÚLTIMO render, para los botones que viven dentro de un aviso.
    // Un aviso se pinta en un momento y se pulsa en otro, así que el que se quedó dentro de
    // su cierre puede estar mirando un día que ya no existe (ver `avisoCorta`, más abajo).
    // Se asigna en cuanto `cuadrarComida` está definido.
    const cuadrarComidaRef = useRef(null);

    const applyDietFavorite = async (fav, { adaptar = false, talCual = false } = {}) => {
        // Aplica la favorita RE-AJUSTANDO sus cantidades a los macros de HOY (no arrastra los
        // objetivos guardados): reusa /refit-diet, que redimensiona cada alimento con la lógica
        // CALMA sin pasarse y respetando el mínimo. No auto-guarda; el día se guarda después.
        // Modo adaptar (entreno<->descanso): se mantiene el tipo de día ACTUAL y las comidas
        // que no existen en él (p.ej. el peri en descanso) se quitan con aviso.
        const cfg = adaptar ? {
            tipoDia,                                    // el del día, no el de la favorita
            numComidas: fav.num_comidas || 4,           // las claves C1..Cn deben casar
            momentoEntreno,
            opcionPeri,
        } : {
            tipoDia: fav.tipo_dia || 'entrenamiento',
            numComidas: fav.num_comidas || 4,
            momentoEntreno: fav.momento_entreno ?? 1,
            opcionPeri: normPeri(fav.opcion_peri),
        };
        if (!adaptar) setTipoDia(cfg.tipoDia);
        setNumComidas(cfg.numComidas);
        setMomentoEntreno(cfg.momentoEntreno);
        setOpcionPeri(cfg.opcionPeri);
        setVolcadoMeal(null);
        setDistribTargetsOverlay(null);   // usar los macros de HOY, no los objetivos guardados
        setFavoritesModalOpen(false);
        try {
            const pedirRefit = (comidas) => api('/api/calculator/refit-diet', {
                method: 'POST',
                body: JSON.stringify({
                    fecha: currentDate,
                    tipo_dia: cfg.tipoDia,
                    // UNA FAVORITA DE TRES COMIDAS SE REPARTÍA ENTRE CUATRO (31-08-2026).
                    //
                    // Aquí ponía `(fav.num_comidas === 3) ? 4 : cfg.numComidas`. Es lo que
                    // quedó del apaño de cuando el reparto no sabía de días de tres comidas;
                    // el 17-06 se quitó de los otros tres sitios (702da51) y este se quedó.
                    //
                    // Lo reportó un cliente de tres comidas: sus objetivos por comida salían
                    // 45 P / 45 P / 36 P en vez de 60, y al día le faltaban los de una cuarta
                    // comida que no se pinta en ninguna parte. Reproducido contra el servidor
                    // (_guia/_fallo_favoritas_3108.js): con 3 pide 63,3 P por comida; con 4,
                    // 47,5 / 47,5 / 38 -- el mismo 1,25 entre la primera y la tercera que veía él.
                    num_comidas: cfg.numComidas,
                    momento_entreno: cfg.momentoEntreno,
                    opcion_peri: cfg.opcionPeri,
                    comidas,
                    descartar_sin_objetivo: adaptar,
                }),
            });
            let res = await pedirRefit(fav.comidas || {});
            // ── LAS FAVORITAS, CON LAS CANTIDADES QUE SE GUARDARON (Francisco, 26-09, opción b) ──
            //
            // Gonzalo: «son otras cantidades de las que yo había guardado». Se recuadraban solas
            // a los macros del día. Ahora se ponen tal cual (el listado ya las trae en gramos) y
            // «Cuadrar el día» queda a mano si no cuadran. Del servidor se usa el reparto del día;
            // y solo se quitan los alimentos que ya no están en el listado, igual que antes.
            if (talCual && res?.comidas) {
                const siguen = new Set(Object.values(res.comidas).flatMap((c) =>
                    (c?.alimentos || []).map((a) => String(a.alimento_id))));
                res = { ...res, decisiones: {}, desfases: {},
                    comidas: Object.fromEntries(Object.entries(fav.comidas || {}).map(([k, c]) => [k, {
                        ...c, alimentos: (c?.alimentos || [])
                            .filter((a) => siguen.has(String(a.alimento_id)))
                            .map((a) => ({ ...a })) }])) };
            }

            // ── «LO MISMO PARA CUANDO ELIGE UNA FAVORITA» (Francisco, 21-09) ────
            //
            // «Si necesita eliminar algo para cuadrar perfecto que lo avise y listo.» Hasta
            // hoy, una favorita que traía una comida pasada de macros se aplicaba tal cual y
            // se le decía «dale a Cuadrar en esa comida», o sea: te dejo el problema puesto y
            // te mando a otro botón a resolverlo. Ahora se mira antes de aplicar nada
            // (`refit-diet` calcula y devuelve, no guarda), se le dice qué habría que quitar
            // y, si acepta, se vuelve a pedir el reparto ya sin eso.
            //
            // Si dice que no, la favorita entra igual -- es suya -- y se queda el aviso de
            // siempre, que abajo sigue en pie.
            {
                const comidasDeHoy = ordenDeComidas({
                    numComidas: cfg.numComidas, tipoDia: cfg.tipoDia, opcionPeri: cfg.opcionPeri,
                    momentoEntreno: cfg.momentoEntreno, unica: cfg.numComidas === 1,
                });
                const cuadraLa = (k) => {
                    const d = res?.desfases?.[k];
                    if (!d) return true;
                    const cuales = (k === 'Intra' || k === 'Post') ? ['P', 'H'] : ['P', 'H', 'G'];
                    return cuales.every((x) => Math.abs(Number(d[x]) || 0) <= MARGEN);
                };
                const quitables = comidasDeHoy.filter((k) => {
                    if (cuadraLa(k)) return false;
                    const sug = res?.desfases?.[k]?.sugerencia;
                    if (!sug || sug.que_hacer !== 'quitar_o_bajar' || sug.alimento_id == null) return false;
                    // Quitar algo que aporta mucho más de lo que sobra deja la comida corta:
                    // eso no cuadra nada, solo cambia de lado el problema.
                    const aporta = Number(sug.aporta) || 0;
                    const sobra = Number(sug.sobra) || 0;
                    return aporta > 0 && aporta - sobra <= MARGEN;
                });
                if (quitables.length) {
                    const dónde = quitables.map((k) => {
                        const sug = res.desfases[k].sugerencia;
                        return `${sug.alimento} (${mealInfo[k]?.name || k})`;
                    });
                    const adelante = await confirm({
                        title: quitables.length === 1
                            ? `Esta favorita cuadra quitando ${res.desfases[quitables[0]].sugerencia.alimento}`
                            : `Esta favorita cuadra quitando ${quitables.length} alimentos`,
                        description: quitables.length === 1
                            ? `${dónde[0]} se pasa aunque esté todo al mínimo, así que bajando gramos no cuadra. Si lo quito, cuadran el día y todas las comidas.`
                            : `Estas comidas se pasan aunque esté todo al mínimo: ${dónde.join(', ')}. Si los quito, cuadran el día y todas las comidas.`,
                        lineas: quitables.map((k) => ({ que: res.desfases[k].sugerencia.alimento,
                                                        cambio: `fuera de ${mealInfo[k]?.name || k}` })),
                        confirmLabel: 'Quitar y aplicar',
                        cancelLabel: 'Aplicar tal cual',
                        danger: true,
                    });
                    if (adelante) {
                        const recortadas = { ...(fav.comidas || {}) };
                        for (const k of quitables) {
                            const id = res.desfases[k].sugerencia.alimento_id;
                            const lista = (recortadas[k]?.alimentos) || [];
                            recortadas[k] = { ...(recortadas[k] || {}),
                                              alimentos: lista.filter((a) => a.alimento_id !== id) };
                        }
                        const otra = await pedirRefit(recortadas);
                        if (otra?.comidas) res = otra;
                    }
                }
            }
            // LO QUE LA FAVORITA NO TRAE, SE VACÍA (31-08-2026).
            //
            // Aquí se ponía `res.comidas` tal cual, que solo tiene las comidas de la
            // favorita. Las demás desaparecían de la pantalla... pero no del día: el
            // autoguardado manda solo lo que tiene delante y el servidor FUSIONA (lo hace a
            // propósito desde el 16-08, para que dos pestañas no se pisen). Así que al
            // recargar volvían las de antes, y el cliente veía un alimento «que se añadió
            // solo». Reportado el 31-08 con unas lentejas en una comida que él ya no tenía.
            //
            // Se mandan vacías, que es lo único que el servidor entiende como «esta comida
            // ya no lleva nada». Las que no están en el reparto de hoy no se tocan: esas
            // siguen guardadas a propósito, para que volver a cuatro comidas las devuelva.
            const delReparto = ordenDeComidas({
                numComidas: cfg.numComidas, tipoDia: cfg.tipoDia, opcionPeri: cfg.opcionPeri,
                momentoEntreno: cfg.momentoEntreno, unica: cfg.numComidas === 1,
            });
            // AQUÍ NO SE PREGUNTA DE DÓNDE BAJAR (31-08-2026). Una favorita recuadra cuatro o
            // cinco comidas de golpe: preguntarlo comida por comida sería un interrogatorio.
            // El servidor baja en proporción, que es lo neutral, y las comidas donde hubo
            // decisión de verdad quedan marcadas para que la pregunta salte cuando el cliente
            // abra esa comida y le dé a «Cuadrar».
            const traidas = res.comidas || {};
            const conDecision = res.decisiones || {};
            setMealsData(Object.fromEntries(delReparto.map((k) => {
                const comida = traidas[k] || { alimentos: [] };
                if (!conDecision[k]) return [k, comida];
                return [k, { ...comida,
                    eleccion_pendiente: eleccionPendienteDe(fav.comidas?.[k], conDecision[k]) }];
            })));
            if (res.distribution) setDistribution(res.distribution);
            // Las dos preguntas que pueden quedar pendientes se cuentan cada una con su
            // verdad (3-09): en las de BAJAR se bajó de todo a la vez; en las de QUITAR no
            // se bajó nada -- ni con todo al mínimo cabe -- y decir «lo he bajado» era
            // mentira (la C4 de Francisco, con los mínimos sumando 18 H sobre 10).
            const aElegir = Object.keys(conDecision).filter(k => delReparto.includes(k));
            const aQuitar = aElegir.filter(k => conDecision[k]?.tipo === 'quitar');
            const aBajar = aElegir.filter(k => conDecision[k]?.tipo !== 'quitar');
            if (aBajar.length) {
                toast.info(aBajar.length === 1
                    ? `En ${mealInfo[aBajar[0]]?.name || aBajar[0]} sobraba y lo he bajado de todo a la vez.`
                    : `En ${aBajar.length} comidas sobraba y lo he bajado de todo a la vez.`,
                    { description: 'Si prefieres bajarlo de un alimento concreto, dale a "Cuadrar" en esa comida.',
                      duration: 9000 });
            }
            if (aQuitar.length) {
                toast.warning(aQuitar.length === 1
                    ? `En ${mealInfo[aQuitar[0]]?.name || aQuitar[0]} sobra aunque esté todo al mínimo.`
                    : `En ${aQuitar.length} comidas sobra aunque esté todo al mínimo.`,
                    { description: 'Dale a "Cuadrar" en esa comida para elegir qué quitar.',
                      duration: 9000 });
            }

            // LO QUE ENTRA Y TÚ EVITAS, DICHO (Gonzalo, minuto 25:59 del vídeo del 3-09):
            // «quité aves, apliqué la dieta que tenía pollo y me añadió el pollo igualmente».
            // No se quita, que la app no borra lo que ha puesto el cliente y una favorita es
            // suya; lo que faltaba era enterarse de que choca con lo que ha dicho que evita.
            const evitados = res.evitados || [];
            if (evitados.length) {
                toast.warning(evitados.length === 1
                    ? `Esta favorita lleva ${evitados[0]}, y lo tienes entre lo que evitas.`
                    : `Esta favorita lleva ${evitados.length} cosas que tienes entre lo que evitas: ${evitados.join(', ')}.`,
                    { description: 'Se ha aplicado igual: es tu favorita. Cámbialo si ya no lo quieres.',
                      duration: 9000 });
            }

            const excluidos = res.excluidos || [];
            const periQuitado = excluidos.filter(e => e.motivo === 'sin_objetivo_en_dia');
            const noCaben = excluidos.filter(e => e.motivo !== 'sin_objetivo_en_dia');
            const etiquetaDia = cfg.tipoDia === 'descanso' ? 'descanso' : 'entreno';

            // Si la composición de la favorita no da para cubrir alguna comida, se dice y
            // se ofrece el Cuadrar aquí mismo (doc 57, F5): antes solo quedaba el hueco en
            // rojo y el cliente tenía que descubrir el botón comida por comida. El peri va
            // aparte a propósito (ahí no hay Cuadrar desde el 08-08).
            const cortas = Object.entries(res.desfases || {})
                .filter(([k, d]) => !['Intra', 'Post'].includes(k)
                    && d && ['P', 'H', 'G'].some(m => (d[m] || 0) < -4))
                .map(([k]) => k);
            // POR QUÉ LAS CANTIDADES NO SON LAS QUE GUARDÓ (Gonzalo, ficha 37 del 6-09-2026:
            // «añade los alimentos que yo seleccioné sin añadir ninguno más nuevo, pero las
            // cantidades siguen sin ser exactamente las mismas»).
            //
            // Es lo previsto y no un fallo: una favorita guarda QUÉ comes, no CUÁNTO. El
            // cuánto depende de los macros del día sobre el que se pone, y por eso al
            // aplicarla se recuadra entera (`refit-diet`, arriba). Lo que fallaba era el
            // aviso: de los cuatro que hay, solo uno lo insinuaba («y ajustada a tus macros»)
            // y los otros tres no decían ni una palabra, así que los gramos cambiaban sin que
            // nadie se lo hubiera contado. Ahora lo dicen los cuatro, y con la misma frase con
            // la que ya se avisa al GUARDARLA (5-09), para que las dos pantallas cuenten lo
            // mismo con las mismas palabras.
            const porQueCambianLasCantidades =
                'Las cantidades no son las que guardaste: se recuadran solas a los macros de este día.';

            // EL «CUADRAR AHORA» DE ESTE AVISO SE LLEVABA EL DÍA POR DELANTE (Gonzalo,
            // prueba 1 del 6-09-2026: «al pulsar en el aviso la opción cuadrar macros se
            // descuadró todo»).
            //
            // El aviso se pinta AQUÍ DENTRO, así que su botón se quedaba con el
            // `cuadrarComida` de este render: uno que todavía ve el `mealsData` de ANTES de
            // aplicar la favorita. En un día vacío eso son cuatro comidas sin un solo
            // alimento, y al pulsar mandaba a `refit-diet` cuatro comidas vacías y escribía
            // la respuesta encima: el día entero desaparecía y la pantalla volvía a
            // «Todavía sin crear». Reproducido en el navegador con
            // `_guia/_repetir_y_favoritas_0609.js`: las llamadas salían con
            // `comidas[C1:0 C2:0 C3:0 C4:0]`.
            //
            // Se llama por un ref, que siempre apunta al `cuadrarComida` del último render.
            // Y las comidas se cuadran DE UNA EN UNA: cada una necesita ver lo que hizo la
            // anterior (el `contexto_dia` es el que decide el tramo de la calibración) y
            // cualquiera puede pararse a preguntar de dónde bajar, que con cuatro a la vez
            // eran cuatro preguntas superpuestas.
            const avisoCorta = {
                duration: 9000,
                description: porQueCambianLasCantidades,
                action: {
                    label: 'Cuadrar ahora',
                    onClick: async () => {
                        for (const k of cortas) await cuadrarComidaRef.current?.(k);
                    },
                },
            };
            const avisoAplicada = { duration: 8000, description: porQueCambianLasCantidades };
            const seQuedaCorta = cortas.length === 1
                ? 'una comida se queda corta de macros'
                : `${cortas.length} comidas se quedan cortas de macros`;

            if (talCual) {
                toast.success(`Aplicada "${fav.name}" con las cantidades que guardaste`, {
                    description: 'Si no cuadra con tus macros de hoy, pulsa «Cuadrar el día».',
                    duration: 9000,
                    action: { label: 'Cuadrar el día', onClick: () => handleRecuadrarDia() },
                });
            } else if (adaptar && periQuitado.length) {
                // La voz del doc del 3-09: «el intra y el post», no «el intra/post».
                toast.warning(`Aplicada "${fav.name}" adaptada a tu día de ${etiquetaDia}. El intra y el post se han quitado.`, avisoAplicada);
            } else if (adaptar && cortas.length) {
                toast.warning(`Aplicada "${fav.name}" adaptada a tu día de ${etiquetaDia}, pero ${seQuedaCorta}.`, avisoCorta);
            } else if (adaptar) {
                toast.success(`Aplicada "${fav.name}" adaptada a tu día de ${etiquetaDia}`, avisoAplicada);
            } else if (!noCaben.length && cortas.length) {
                toast.warning(`Aplicada "${fav.name}", pero ${seQuedaCorta}.`, avisoCorta);
            } else if (!noCaben.length) {
                toast.success(`Aplicada "${fav.name}" y ajustada a tus macros`, avisoAplicada);
            }
            // Singular y plural de verdad, no «alimento(s)» (Jesús, 15-08, fallo 42).
            //
            // Y EL MOTIVO, EL QUE ES. Decía «no cabía ni al mínimo», y de este motor un
            // alimento solo se cae por una razón: que ya no esté en el listado (o que la
            // línea no diga a cuál se refiere, que para el cliente es lo mismo). Con la
            // explicación de antes se ponía a bajar gramos, que no arregla nada.
            if (noCaben.length) {
                toast.warning(noCaben.length === 1
                    ? 'Quitamos un alimento que ya no está en el listado.'
                    : `Quitamos ${noCaben.length} alimentos que ya no están en el listado.`);
            }

            // LO QUE LA FAVORITA NO TRAE, SE DICE (31-08-2026).
            //
            // Este aviso solo salía al adaptar de descanso a entreno. Y hasta hoy daba igual,
            // porque lo que la favorita no traía no se vaciaba: seguía guardado y volvía al
            // recargar. Ahora se vacía de verdad, así que hay que contarlo siempre: cambiar
            // un añadido silencioso por un borrado silencioso no arregla nada.
            const trae = (k) => ((fav.comidas?.[k]?.alimentos) || []).length > 0;
            const vaciadas = delReparto.filter((k) => !trae(k)
                && ((mealsData[k]?.alimentos) || []).length > 0);
            if (vaciadas.length) {
                const soloPeri = vaciadas.every((k) => ['Intra', 'Post'].includes(k));
                // Sin el puntero a «Sugiéreme un menú»: el doc del 3-09 lo deja en que lo
                // rellena el cliente, y ya lo dice la línea gris de antes de aplicar.
                const nombrePeri = vaciadas.length === 2 ? 'El intra y el post han quedado vacíos'
                    : (vaciadas[0] === 'Intra' ? 'El intra ha quedado vacío' : 'El post ha quedado vacío');
                toast.info(soloPeri
                    ? `${nombrePeri}: la favorita no traía nada ahí.`
                    : `${vaciadas.map(k => mealInfo[k]?.name || k).join(' y ')}: la favorita no traía nada ahí y se han vaciado.`,
                    { duration: 8000 });
            }
        } catch (err) {
            toast.error('Error al aplicar la favorita');
        }
    };

    // Repetir un día reciente entero sobre el día abierto (pantalla de día vacío, doc
    // 21-08). El mecanismo es EL MISMO que aplicar una favorita: applyDietFavorite ->
    // /calculator/refit-diet, que recuadra cada alimento a los macros de HOY, con sus
    // avisos de comidas cortas y su «Cuadrar ahora» (doc 57). Si el tipo de día no
    // coincide («Se adapta»), va en modo adaptar: se queda el tipo del día abierto y el
    // peri que no exista aquí se quita con aviso.
    //
    // /diets/recent no trae momento_entreno ni opcion_peri, así que antes se pide el
    // día completo: sin eso, repetir un día «que encaja» le cambiaría el horario del
    // entreno y el peri por los valores por defecto.
    //
    // Y PREGUNTA ANTES (Jesús, 6-09-2026: «al elegir repetir un día tiene que pedir
    // confirmación»). Aquí no se pisa ninguna dieta -- esta pantalla solo sale con el día
    // vacío --, pero un toque en la lista montaba las comidas del día entero de golpe y sin
    // vuelta atrás, que es justo lo que la casa pregunta en todas partes: copiar a otro día,
    // vaciar una comida, borrar una favorita. La pregunta dice ADEMÁS qué va a pasar, que
    // hasta ahora solo se leía en la letra pequeña del pie de la lista.
    const NOMBRE_DEL_TIPO = { descanso: 'descanso', entrenamiento: 'entreno' };

    // `nombrandoElDestino` lo pide el camino que viene de INICIO («Repetir la de hoy», Jesús
    // 5-09): allí el cliente no está mirando el día que va a recibir la copia, así que la
    // pregunta tiene que decir los DOS por su nombre -- de qué día se copia y sobre cuál --,
    // y avisar si el de destino ya tenía dieta, que en la lista de «Repetir un día» no puede
    // pasar (esa pantalla solo sale con el día vacío).
    const repetirDiaReciente = async (diaReciente, { nombrandoElDestino = false } = {}) => {
        try {
            const cuando = formatDate(diaReciente.fecha).toLowerCase();
            const tipoOrigen = diaReciente.tipo_dia || 'entrenamiento';
            const otroTipo = tipoOrigen !== tipoDia;
            const destino = formatDate(currentDate).toLowerCase();
            // El día de destino con la fecha entera la PRIMERA vez, y solo por el día de la
            // semana en las otras dos: «...sobre el lunes, 7 de septiembre y se ajustan a tus
            // macros del lunes, 7 de septiembre» decía tres veces lo mismo y se leía peor.
            const destinoCorto = destino.split(',')[0];
            // Lo que hay en el día de destino, contado como en todas partes desde el 31-08:
            // el intra y el post no son comidas, así que se nombran aparte.
            const loQueSePisa = comidasDelDia > 0
                ? (comidasDelDia === 1 ? '1 comida' : `${comidasDelDia} comidas`)
                : periDelDia.length === 2 ? 'el intra y el post'
                    : periDelDia.length === 1 ? (periDelDia[0] === 'Intra' ? 'el intra' : 'el post')
                        : null;
            const conLosDosDias = () => {
                const partes = [];
                if (otroTipo) partes.push(`El ${cuando} era de ${NOMBRE_DEL_TIPO[tipoOrigen] || 'entreno'} y el ${destinoCorto} es de ${NOMBRE_DEL_TIPO[tipoDia] || 'entreno'}.`);
                partes.push(`Se copian las comidas del ${cuando} sobre el ${destino} y se ajustan a tus macros del ${destinoCorto}.`);
                // Singular de verdad, como en el aviso de las favoritas: «1 comida: se borra».
                const unaSola = comidasDelDia === 1
                    || (comidasDelDia === 0 && periDelDia.length === 1);
                if (loQueSePisa) partes.push(`El ${destinoCorto} ya tiene ${loQueSePisa}: ${unaSola ? 'se borra' : 'se borran'}.`);
                return partes.join(' ');
            };
            const adelante = await confirm({
                title: `Repetir el ${cuando}`,
                // Con los dos tipos distintos se dice, porque es lo que hace que el
                // resultado no se parezca a lo que tiene en la cabeza: los macros de un día
                // de descanso no son los de uno de entreno, y las cantidades van a cambiar.
                description: nombrandoElDestino
                    ? conLosDosDias()
                    : otroTipo
                        ? `Ese día era de ${NOMBRE_DEL_TIPO[tipoOrigen] || 'entreno'} y este es de ${NOMBRE_DEL_TIPO[tipoDia] || 'entreno'}. Se copian sus comidas y se ajustan a tus macros de este día.`
                        : 'Se copian sus comidas y se ajustan a tus macros de este día.',
                confirmLabel: 'Repetirlo',
                danger: otroTipo || Boolean(nombrandoElDestino && loQueSePisa),
            });
            if (!adelante) return;

            const full = await api(`/api/diets/${diaReciente.fecha}`);
            if (!full?.exists) {
                toast.error('Ese día ya no está guardado');
                return;
            }
            const comoFavorita = {
                name: formatDate(diaReciente.fecha),
                tipo_dia: full.tipo_dia || 'entrenamiento',
                num_comidas: full.num_comidas || 4,
                momento_entreno: normMomento(full.momento_entreno ?? 1),
                opcion_peri: normPeri(full.opcion_peri),
                comidas: full.comidas || {},
            };
            await applyDietFavorite(comoFavorita, {
                adaptar: comoFavorita.tipo_dia !== tipoDia,
            });
        } catch (err) {
            console.error('[repetir día]', err);
            toast.error('No hemos podido repetir ese día. Inténtalo de nuevo.');
        }
    };

    // ── `?repetir=AAAA-MM-DD`: EL «REPETIR LA DE HOY» DE INICIO ───────────────
    //
    // Jesús, 5-09-2026: «cuando pincho repetir me tiene que llevar al día siguiente y pedirme
    // que le confirme que quiero repetir la dieta del sábado». Inicio abre esta pantalla en
    // mañana (`?date=`) y le dice ADEMÁS qué día hay que traerse (`?repetir=`). Se eligió un
    // parámetro más en la URL, y no un estado compartido ni una pantalla nueva, porque es lo
    // único que hacía falta: el `?date=` ya viajaba así y el enlace queda legible.
    //
    // Se espera a que el día esté CARGADO: la pregunta nombra los dos días y dice si el de
    // destino ya tenía dieta, y eso no se sabe hasta que `mealsData` es el de esta fecha.
    // Y el parámetro se quita de la URL en cuanto se recoge, para que recargar la página no
    // vuelva a preguntar; el ref es el candado del doble montaje de StrictMode.
    // ── `?menu=<id>`: EL «USAR EN UNA COMIDA» DEL RANKING (26-09) ─────────────
    // Igual que `?repetir=`: se espera al día cargado, se quita de la URL y se pregunta en qué
    // comida se pone.
    const menuPedidoRef = useRef(null);
    useEffect(() => {
        if (loading || loadedDateRef.current !== currentDate) return;
        const id = new URLSearchParams(window.location.search).get('menu');
        if (!id || menuPedidoRef.current === id) return;
        menuPedidoRef.current = id;
        try {
            const url = new URL(window.location.href);
            url.searchParams.delete('menu');
            window.history.replaceState(null, '', url.toString());
        } catch (e) { /* sale igual */ }
        api(`/api/menus-compartidos/menu/${encodeURIComponent(id)}`)
            .then((m) => setMenuParaUsar(m))
            .catch(() => toast.error('Ese menú ya no está compartido.'));
    }, [loading, currentDate, api]);

    const usarMenuEn = async (mealKey) => {
        const m = menuParaUsar;
        setMenuParaUsar(null);
        if (!m) return;
        await aplicarComidaFavorita({ name: m.titulo || 'El menú', alimentos: m.alimentos || [] }, mealKey);
        api(`/api/menus-compartidos/menu/${encodeURIComponent(m.id)}/usado`, {
            method: 'POST', body: JSON.stringify({ como: 'comida' }),
        }).catch(() => {});
    };

    const repetirPedidoRef = useRef(null);
    useEffect(() => {
        if (loading || loadedDateRef.current !== currentDate) return;
        const pedido = new URLSearchParams(window.location.search).get('repetir');
        if (!pedido || !/^\d{4}-\d{2}-\d{2}$/.test(pedido) || pedido === currentDate) return;
        const marca = `${pedido}->${currentDate}`;
        if (repetirPedidoRef.current === marca) return;
        repetirPedidoRef.current = marca;
        try {
            const url = new URL(window.location.href);
            url.searchParams.delete('repetir');
            window.history.replaceState(null, '', url.toString());
        } catch (e) { /* si el navegador no deja tocar la URL, la pregunta sale igual */ }
        (async () => {
            // El tipo del día de origen hace falta ANTES de preguntar (la pregunta lo dice
            // cuando no coincide). Si ya está en los días recientes no se pide nada.
            const yaLoTengo = (recentDiets || []).find(d => d.fecha === pedido);
            let origen = yaLoTengo;
            if (!origen) {
                const full = await api(`/api/diets/${pedido}`).catch(() => null);
                if (!full?.exists) { toast.error('Ese día ya no está guardado'); return; }
                origen = { fecha: pedido, tipo_dia: full.tipo_dia || 'entrenamiento' };
            }
            await repetirDiaReciente(origen, { nombrandoElDestino: true });
        })();
    }, [loading, currentDate, recentDiets]); // eslint-disable-line

    // ── ¿DE DÓNDE SE BAJA? (Jesús, nota de voz del 31-08-2026) ───────────────
    //
    // «Cuando recalcule, pregunte, pregunte de qué quiere bajar la proteína, del polvo o del
    // queso [...] es imposible que la aplicación aprenda eso, porque te puede quedar más
    // denso, menos denso [...] lo más sencillo es preguntar.»
    //
    // El servidor dice CUÁNDO hay algo que preguntar y con qué números (core/de_donde_bajo.py);
    // aquí solo se pinta. Las opciones no dicen nada que la app no sepa: cuánto hay, cuánto
    // pone y en cuánto se quedaría. Nada de «queda más espeso», que en un plato de pollo con
    // arroz no significaría nada.
    const NOMBRE_DEL_MACRO = { P: 'proteína', H: 'hidratos', G: 'grasa' };

    /** Lo que hay que guardar para poder preguntar MÁS TARDE por una comida que se recuadró
     *  sin preguntar: la pregunta que devolvió el servidor y las cantidades sobre las que se
     *  hizo. Sin las cantidades de partida no hay nada que elegir: ya están bajadas. */
    const eleccionPendienteDe = (comidaEnviada, decision) => ({
        decision,
        alimentos: ((comidaEnviada?.alimentos) || []).map(a => ({
            alimento_id: a.alimento_id, nombre: a.nombre, cantidad_g: a.cantidad_g,
        })),
    });

    const huellaDeLaComida = (mealKey) => ((mealsData[mealKey]?.alimentos) || [])
        .map(a => a.alimento_id).sort().join(',');

    // La respuesta vale para ESA comida de ESE día mientras no cambien sus alimentos. No se
    // guarda para otros días ni para otras comidas: es justo lo que Jesús dice que la app no
    // puede dar por sabido. Sirve para que el recuadre automático -- que no puede preguntar --
    // respete lo que el cliente ya contestó en vez de decidir por su cuenta.
    const eleccionGuardada = (mealKey) => {
        const g = mealsData[mealKey]?.bajar_de;
        if (!g || g.huella !== huellaDeLaComida(mealKey)) return null;
        if (g.alimento_ids?.length) return { modo: 'varios', alimento_ids: g.alimento_ids };
        return g.alimento_id ? { modo: 'solo', alimento_id: g.alimento_id } : { modo: 'proporcional' };
    };

    const preguntarDeDondeBajo = async (decision) => {
        const macro = NOMBRE_DEL_MACRO[decision.macro] || decision.macro;
        const opciones = (decision.opciones || []).map((o) => (o.modo === 'quitar'
            ? {
                valor: `quitar-${o.alimento_id}`,
                titulo: `${o.nombre} · ${num1(o.cantidad_ahora)} g`,
                // Lo que importa aquí no es lo que pone ahora, sino lo que seguiría poniendo
                // aunque se bajara al mínimo: eso es lo que de verdad se lleva quitarlo.
                //
                // Y NADA DE «quitándolo aún sobrarían N» EN CADA LÍNEA: ese número es el de
                // quitar ese solo, así que en cuanto marcas dos, cada línea dice una cosa y
                // el pie otra. La cuenta la lleva el pie, que es el que sabe lo que hay
                // marcado.
                detalle: `${num1(o.aporta_en_el_minimo)} g de ${macro} aunque lo bajes al mínimo`,
            }
            : o.modo === 'proporcional'
            ? {
                valor: 'proporcional',
                titulo: 'De todos, en la misma proporción',
                detalle: (o.cantidades || []).map(c =>
                    `${c.nombre}: ${num1(c.cantidad_ahora)} → ${num1(c.queda_en)} g`).join(' · '),
            }
            : {
                valor: String(o.alimento_id),
                // «Pone 52,8 g» pegado a «· 60 g» se lee como si fueran los mismos gramos:
                // uno es lo que pesa el alimento y otro lo que aporta. Va con el nombre del
                // macro para que no haya duda.
                titulo: `${o.nombre} · ${num1(o.cantidad_ahora)} g`,
                detalle: `${num1(o.aporta)} g de ${macro}`
                    + ` → se quedaría en ${num1(o.queda_en)} g`
                    + (o.sobraria_aun > 4 ? `, y aún sobrarían ${num1(o.sobraria_aun)}` : ''),
            }));
        // QUITAR SE PREGUNTA UNA SOLA VEZ, MARCANDO VARIOS (Francisco, 31-08-2026).
        //
        // «Va haciendo muchas veces las preguntas, supongo que lo hará hasta que cuadre pero
        // es tedioso.» Y tenía razón: su comida pedía cinco diálogos seguidos, uno por
        // alimento. La decisión es la misma -- la toma él, no la app --, pero cabe entera en
        // una pantalla: se marcan los que sobran y el pie va diciendo cuánto queda por quitar,
        // así se ve de un vistazo cuántos hacen falta en vez de descubrirlo a golpes.
        if (decision.tipo === 'quitar') {
            const enElMinimo = new Map((decision.opciones || [])
                .map(o => [`quitar-${o.alimento_id}`, o.aporta_en_el_minimo || 0]));
            const marcados = await elegir({
                title: decision.titulo,
                description: 'Bajar las cantidades ya no da más de sí. Cuadrar no quita nada por su cuenta: eliges tú.',
                opciones,
                multiple: true,
                resumen: (sel) => {
                    const fuera = sel.reduce((s, v) => s + (enElMinimo.get(v) || 0), 0);
                    const queda = (decision.sobra || 0) - fuera;
                    if (!sel.length) return `Hay que quitar unos ${num1(decision.sobra)} g de ${macro}.`;
                    return queda > 4
                        ? `Con eso aún sobrarían ${num1(queda)} g de ${macro}.`
                        : 'Con eso ya cuadra.';
                },
                confirmLabel: 'Quitar los marcados',
                cancelLabel: 'Dejarlo como está',
            });
            if (!marcados || !marcados.length) return null;
            return { modo: 'quitar',
                     alimento_ids: marcados.map(v => Number(v.slice(7))) };
        }
        // BAJAR TAMBIÉN SE PREGUNTA UNA SOLA VEZ, MARCANDO VARIOS (Francisco, 23-09: «me
        // repite esto cada vez que quito algo; si tengo que bajar más de uno, que me lo diga
        // y me indique cuando con los seleccionados ya cuadraría, como en otros botones»).
        // Era una elección de uno: si el elegido no llegaba («y aún sobrarían 20,2»), se
        // aplicaba y se volvía a preguntar lo mismo. Ahora se marcan los que hagan falta y el
        // pie lleva la cuenta; «de todos en la misma proporción» sigue como opción suelta.
        const cabe = new Map((decision.opciones || []).filter((o) => o.modo === 'solo')
            .map((o) => [String(o.alimento_id), Math.max(0, (decision.sobra || 0) - (o.sobraria_aun || 0))]));
        const marcadosBajar = await elegir({
            title: decision.titulo,
            description: 'Marca de qué alimentos bajar: solo bajan esos, el resto se queda con los gramos que le pusiste.',
            // «De todos, en la misma proporción» va al final de la lista; marcada, manda ella.
            opciones,
            multiple: true,
            resumen: (sel) => {
                if (sel.includes('proporcional')) return 'Bajan todos un poco, en la misma proporción: con eso ya cuadra.';
                const baja = sel.reduce((t, v) => t + (cabe.get(v) || 0), 0);
                const queda = (decision.sobra || 0) - baja;
                if (!sel.length) return `Hay que bajar ${num1(decision.sobra)} g de ${macro}. Marca uno o varios.`;
                return queda > 4 ? `Con eso aún sobrarían ${num1(queda)} g de ${macro}: marca otro.` : 'Con eso ya cuadra.';
            },
            confirmLabel: 'Bajar los marcados',
            cancelLabel: 'Dejarlo como está',
        });
        if (!marcadosBajar) return null;
        if (marcadosBajar === 'proporcional' || (Array.isArray(marcadosBajar) && marcadosBajar.includes('proporcional'))) {
            return { modo: 'proporcional' };
        }
        if (!marcadosBajar.length) return null;
        if (marcadosBajar.length === 1) return { modo: 'solo', alimento_id: Number(marcadosBajar[0]) };
        return { modo: 'varios', alimento_ids: marcadosBajar.map(Number) };
    };

    // Cuadrar una comida a demanda: re-ajusta sus alimentos a los macros de HOY, sin pasarse y
    // respetando el mínimo de cada uno (reusa /refit-diet solo para esa comida).
    // `silencioso`: lo usa el recuadre automático al cruzar un umbral (doc 57, F3), que ya
    // pone su propio aviso; sin él saldrían dos carteles por el mismo gesto. Y como no puede
    // parar a preguntar, tira de la respuesta que el cliente ya dio para esta comida.
    const cuadrarComida = async (mealKey, { silencioso = false } = {}) => {
        try {
            // LO QUE HABÍA ANTES, PARA PODER DESHACER (ficha 117 del parte de Gonzalo, 5-09).
            // Quitar un ingrediente y vaciar una comida ya dejaban volver atrás; Cuadrar, que
            // mueve todas las cantidades de golpe, no. Se guarda la comida tal cual está en
            // pantalla (no `dePartida`, que puede ser la de antes de una elección pendiente)
            // y el aviso de después ofrece «Deshacer» unos segundos: al pulsarlo vuelven las
            // cantidades exactas y el autoguardado las escribe como cualquier otro cambio.
            // Y DESDE EL 6-09 EL «DESHACER» NO VA EN EL AVISO, VA EN LA TARJETA (Jesús):
            // «cuando aparece el botón de deshacer en las notificaciones no se puede tocar».
            // El aviso se sigue dando -- dice cómo ha quedado la comida --, pero la vuelta
            // atrás vive donde se ve el resultado y sin cuenta atrás. Ver `deshacerComida`.
            const antesDeCuadrar = mealsData[mealKey] || { alimentos: [] };
            // SI QUEDÓ UNA ELECCIÓN PENDIENTE, LA PREGUNTA VA SOBRE LAS CANTIDADES DE ENTONCES.
            //
            // Al aplicar una favorita no se puede preguntar comida por comida, así que se baja
            // de todo a la vez y la comida queda marcada. Pero para cuando el cliente toca la
            // marca, esas cantidades YA están bajadas: preguntar sobre ellas no daría ninguna
            // opción, porque no sobra nada. Se guardó lo que había antes justo para esto.
            const pendiente = (!silencioso && mealsData[mealKey]?.eleccion_pendiente) || null;
            const dePartida = pendiente
                ? { alimentos: pendiente.alimentos || [] }
                : (mealsData[mealKey] || { alimentos: [] });
            const huella = (dePartida.alimentos || []).map(a => a.alimento_id).sort().join(',');
            // ── EL MOTOR TIENE QUE CUADRAR CONTRA EL OBJETIVO QUE SE VE (Francisco, 21-09) ──
            //
            // «Intenté cuadrar la comida y no lo hace; le doy a cuadrar y sigue sin cuadrar.»
            // Su Comida 4 pedía 67,4 P en pantalla y el motor la daba por buena con 59,7.
            // No era que no supiera subir el pollo: es que estaba cuadrando contra OTRO
            // objetivo, el del reparto normal (60 P), porque el volcado vive solo en la
            // pantalla y al motor no se le decía.
            //
            // Con el volcado puesto, esa comida absorbe lo que queda del día y las demás
            // quedan bloqueadas: su objetivo no es el del reparto. `ajuste_objetivo` es
            // justo la manija para contarlo -- la diferencia entre lo que pide la pantalla y
            // lo que reparte el día --, la misma que usa «Cuadrar el día».
            //
            // Sin esto, el botón no podía cuadrar nunca: perseguía un número que no era.
            // SI YA CUADRA, NO SE TOCA (JDG, Trello 21-09). Recalcular desde cero una comida
            // que ya está dentro de margen la deja distinta -- 47,5 → 47,2 de proteína, los
            // hidratos de «cuadrado» a «válido −0,8» -- y eso se lee como que el botón la
            // descuadra. Solo se mueve lo que hace falta mover.
            if (!silencioso && getMealStatus(mealKey) === 'cuadrada') {
                toast.success('Esta comida ya está cuadrada. No hay nada que mover.');
                return;
            }
            const objetivoQueSeVe = getMealTarget(mealKey) || {};
            const objetivoDelReparto = (distribution?.comidas || {})[mealKey]
                || (distribution?.periworkout || {})[mealKey] || {};
            // SOLO EN EL CUADRE QUE PREGUNTA. Se probó aplicarlo también al recuadre
            // automático -- el del cruce de umbral, que no pregunta -- y salta DESPUÉS de
            // que el cliente acepte, así que rehacía el reparto por su cuenta y deshacía lo
            // aceptado: en la prueba dejó las fresas en 500 g y la comida peor que antes.
            // El automático sigue trabajando contra el reparto del día, que es lo que ha
            // hecho siempre; el volcado lo cuadra quien lo ha puesto.
            const correccionDelVolcado = (activeVolcado && !silencioso)
                ? ['P', 'H', 'G'].reduce((acc, x) => {
                    const dif = (Number(objetivoQueSeVe[x]) || 0) - (Number(objetivoDelReparto[x]) || 0);
                    if (Math.abs(dif) > 0.05) acc[x] = Number(dif.toFixed(1));
                    return acc;
                }, {})
                : null;
            const hayCorreccion = correccionDelVolcado && Object.keys(correccionDelVolcado).length > 0;

            const pedir = (lista, ajuste) => api('/api/calculator/refit-diet', {
                method: 'POST',
                body: JSON.stringify({
                    fecha: currentDate,
                    tipo_dia: tipoDia, num_comidas: numComidas,
                    momento_entreno: momentoEntreno, opcion_peri: opcionPeri,
                    comidas: { [mealKey]: { ...dePartida, alimentos: lista } },
                    ...(hayCorreccion ? { ajuste_objetivo: { [mealKey]: correccionDelVolcado } } : {}),
                    // EL TRAMO DE LA CALIBRACIÓN LO DECIDE EL DÍA ENTERO (3-09): si solo
                    // viaja una comida, el servidor mediría las almendras con un día de
                    // cero gramos de frutos secos y contaría otra proteína que la pantalla.
                    // El resto del día va aparte, solo ids y cantidades.
                    contexto_dia: Object.fromEntries(getMealOrder()
                        .filter(k => k !== mealKey)
                        .map(k => [k, (mealsData[k]?.alimentos || []).map(a => ({
                            alimento_id: a.alimento_id ?? null,
                            cantidad_g: a.cantidad_g ?? 0,
                        }))])),
                    ...(ajuste ? { ajuste: { [mealKey]: ajuste } } : {}),
                }),
            });

            // SE PREGUNTA HASTA QUE CUADRE (Francisco, 31-08-2026).
            //
            // «Los macros tienen que quedar cuadrados, ese es el objetivo del botón.» Una
            // sola pregunta no basta: al bajar la proteína puede seguir sobrando grasa, y hay
            // comidas que no cuadran por mucho que se baje. Así que cada respuesta se aplica,
            // se vuelve a mirar, y se vuelve a preguntar si queda algo. El tope de vueltas es
            // un seguro, no un límite de diseño: cada respuesta quita sobra, así que la cosa
            // converge sola; y «Dejarlo como está» corta en cualquier momento.
            let alimentos = dePartida.alimentos || [];
            let siguienteAjuste = silencioso ? eleccionGuardada(mealKey) : null;
            let elegido = null;
            const quitados = [];
            let res = null;
            let hizoAlgo = false;

            for (let vuelta = 0; vuelta < 20; vuelta++) {
                const aplicando = siguienteAjuste;
                res = await pedir(alimentos, aplicando);
                if (aplicando) {
                    elegido = aplicando;
                    // Se materializa lo que ha salido para volver a mirar sobre eso.
                    alimentos = res.comidas?.[mealKey]?.alimentos || alimentos;
                    siguienteAjuste = null;
                    hizoAlgo = true;
                }
                if (silencioso) break;
                if (aplicando) continue;   // ¿queda algo por decidir con lo que ha quedado?
                const decision = res.decisiones?.[mealKey];
                if (!decision) break;      // ya cuadra, o no hay nada que elegir
                const respuesta = await preguntarDeDondeBajo(decision);
                // Cerrar sin elegir es «déjalo como está»: si no se había tocado nada, ni se
                // toca; si ya se habían quitado cosas, se guarda lo hecho hasta aquí.
                //
                // PERO SE DICE CÓMO QUEDA. Este `return` se iba de la función sin pintar ni
                // un aviso, así que al que decía «déjalo como está» el botón se le quedaba
                // mudo: la comida seguía descuadrada, sin una palabra, y desde fuera parecía
                // que el botón no funcionaba. Es lo que Gonzalo describe como «le doy a
                // cuadrar y no pasa nada». No se toca la comida, que es lo que él ha pedido;
                // lo que se hace es contarle en qué se queda.
                if (!respuesta) {
                    if (!hizoAlgo) {
                        const queda = desajusteEnPalabras(res.desfases?.[mealKey]);
                        if (queda) toast.info(`Lo dejo como está: ${queda}.`, { duration: 7000 });
                        return;
                    }
                    break;
                }
                if (respuesta.modo === 'quitar') {
                    const fuera = new Set(respuesta.alimento_ids || []);
                    alimentos.filter(a => fuera.has(a.alimento_id))
                        .forEach(a => quitados.push(a.nombre));
                    alimentos = alimentos.filter(a => !fuera.has(a.alimento_id));
                    hizoAlgo = true;
                } else {
                    siguienteAjuste = respuesta;
                }
            }

            // ── Y SI SIGUE SIN CUADRAR, SE INSISTE (Francisco, 20-09) ───────────
            //
            // «Tuve que presionar dos veces el botón de cuadrar para que cuadre, ¿por qué no
            // lo hace a la primera?» Porque el bucle de arriba solo vuelve a pedir cuando hay
            // algo QUE PREGUNTAR: si el motor deja la comida fuera de margen pero sin nada
            // que decidir, se salía y ahí se quedaba. Al pulsar otra vez, el motor partía del
            // resultado anterior y se acercaba otro poco. Dos pulsaciones haciendo lo que
            // tenía que hacer una.
            //
            // Ahora se repite aquí mismo, partiendo de lo que acaba de salir, hasta que la
            // comida cierre o hasta que una vuelta no mueva ni un gramo. El tope es un
            // seguro: cada vuelta se acerca, asi que en la practica sale en una o dos.
            const cuadra = (d) => !d || ['P', 'H', 'G'].every((x) => Math.abs(d[x] || 0) <= MARGEN);
            for (let vuelta = 0; vuelta < 4 && res && !cuadra(res.desfases?.[mealKey]); vuelta++) {
                const anterior = (res.comidas?.[mealKey]?.alimentos || []).map(a => a.cantidad_g);
                const otra = await pedir(res.comidas?.[mealKey]?.alimentos || alimentos);
                const ahora = (otra?.comidas?.[mealKey]?.alimentos || []).map(a => a.cantidad_g);
                if (!otra?.comidas?.[mealKey]) break;
                res = otra;
                hizoAlgo = true;
                // Una vuelta que no mueve nada no va a mover nada en la siguiente.
                if (JSON.stringify(anterior) === JSON.stringify(ahora)) break;
            }

            // ── Y SI SE PASA Y NO PUEDE BAJAR MÁS, SE OFRECE QUITAR (Francisco, 21-09) ──
            //
            // «Si necesita borrar un alimento para que cuadre... que avise.» Una comida que
            // se pasa con todo al mínimo no cuadra moviendo gramos: no hay de dónde bajar.
            // El motor, cuando llega ahí, deja de ofrecer decisión pero sí apunta qué
            // alimento sobra y cuánto aporta; con eso se propone la retirada y se recuadra
            // sin él. Nada se aplica todavía: se pregunta abajo, con su nombre.
            const retirados = [];
            for (let ronda = 0; ronda < 3 && res && !cuadra(res.desfases?.[mealKey]); ronda++) {
                const sug = res.desfases?.[mealKey]?.sugerencia;
                if (!sug || sug.que_hacer !== 'quitar_o_bajar' || sug.alimento_id == null) break;
                // Quitar un alimento que aporta mucho más de lo que sobra deja la comida
                // peor de lo que estaba: eso no es cuadrar, es vaciar.
                const aporta = Number(sug.aporta) || 0;
                const sobra = Number(sug.sobra) || 0;
                if (!aporta || aporta - sobra > MARGEN) break;
                const lista = res.comidas?.[mealKey]?.alimentos || alimentos;
                const sinEl = lista.filter((a) => a.alimento_id !== sug.alimento_id);
                if (!sinEl.length || sinEl.length === lista.length) break;
                const otra = await pedir(sinEl);
                if (!otra?.comidas?.[mealKey]) break;
                // Y SOLO SI CON ESO CUADRA DE VERDAD. Medido el 21-09 con su Comida 4 al
                // 140 %: para arreglar 9 g de hidratos se propuso quitar el Skyr, que se
                // llevaba 42 g de proteína, y la comida pasó de «sobran 9» a «faltan 43».
                // Mirar solo el macro que sobra no basta: se mira cómo queda entera.
                if (!cuadra(otra.desfases?.[mealKey])) break;
                res = otra;
                hizoAlgo = true;
                retirados.push(sug.alimento || 'un alimento');
            }

            const refit = res?.comidas?.[mealKey];
            if (!refit) { if (!silencioso) toast.error('No se pudo cuadrar la comida'); return; }
            const comidaCuadrada = {
                ...refit,
                eleccion_pendiente: null,
                ...(elegido ? { bajar_de: { ...elegido, huella } } : {}),
            };
            // ── Y QUÉ SE VA A TOCAR, ANTES DE TOCARLO (Francisco, 16-09) ────────
            //
            // «Cuando le doy a cuadrar en una comida se debe avisar qué alimento y cuántos
            // gramos va a quitar, y yo aceptar o cancelar.» Es lo mismo que ya hace «Cuadrar
            // el día», y por el mismo motivo: aquí se mueven de golpe las cantidades de todo
            // lo que hay en la comida, y hasta ahora el cliente se enteraba después.
            //
            // Todavía no se ha tocado nada -- `refit-diet` calcula y devuelve, no guarda --,
            // así que la lista es exactamente lo que se va a aplicar. Solo se nombra lo que
            // de verdad se mueve: un alimento que se queda en sus gramos no entra.
            //
            // Los que el cliente ya dijo de quitar arriba salen igual, para que el resumen
            // sea el cambio entero y no una parte.
            const filasDelCambio = [];
            // Emparejado por alimento y no por posición: desde que el cuadre puede retirar
            // alguno, las dos listas ya no van en paralelo y los índices harían parecer
            // cambiados los gramos de los que no se han movido.
            {
                const deQuien = (a) => (a?.alimento_id != null ? `id:${a.alimento_id}` : `n:${a?.nombre || ''}`);
                const pendientes = (comidaCuadrada.alimentos || []).map((a) => ({ ...a }));
                for (const previo of (antesDeCuadrar.alimentos || [])) {
                    const i = pendientes.findIndex((a) => a && deQuien(a) === deQuien(previo));
                    const de = Math.round(Number(previo?.cantidad_g) || 0);
                    if (i < 0) {
                        filasDelCambio.push({ que: previo?.nombre || 'Alimento', cambio: 'fuera' });
                        continue;
                    }
                    const a_ = Math.round(Number(pendientes[i]?.cantidad_g) || 0);
                    if (de !== a_) {
                        filasDelCambio.push({ que: pendientes[i]?.nombre || previo?.nombre || 'Alimento',
                                              cambio: `${de} → ${a_} g` });
                    }
                    pendientes[i] = null;
                }
            }
            if (!silencioso && filasDelCambio.length) {
                const adelante = await confirm({
                    title: retirados.length
                        ? (retirados.length === 1
                            ? `Para que cuadre hay que quitar ${retirados[0]}`
                            : `Para que cuadre hay que quitar ${retirados.length} alimentos`)
                        : `Vamos a ajustar ${mealInfo[mealKey]?.name || 'esta comida'}`,
                    description: retirados.length
                        ? 'Esta comida se pasa y sus alimentos ya están al mínimo, así que bajando gramos no cuadra. Quitando esto sí cuadra.'
                        : (quitados.length
                            ? 'Esto es lo que cambia, con lo que pediste quitar.'
                            : 'Estos son los gramos que cambian. No se quita ningún alimento.'),
                    lineas: filasDelCambio,
                    confirmLabel: retirados.length ? 'Quitar y cuadrar' : 'Cuadrar la comida',
                    ...(retirados.length ? { danger: true } : {}),
                });
                // Se queda exactamente como estaba, y sin más mensajes: los de abajo cuentan
                // cómo ha quedado, y no ha quedado de ninguna manera.
                if (!adelante) return;
            }
            setMealsData(prev => {
                const siguiente = { ...prev, [mealKey]: comidaCuadrada };
                // Y NO SE LLEVA POR DELANTE EL «DESHACER» DEL DÍA (Francisco, 21-09, «ahí
                // falta el deshacer»). Nada más cuadrar el día, la calibración cambia de
                // tramo y saltan estos recuadres silenciosos en varias comidas; movían
                // gramos, la huella del día dejaba de coincidir y el «Deshacer» del día se
                // escondía a los 0,8 s de salir. Deshacer sigue devolviendo el día de antes
                // del cuadre, así que la huella se pone al día en vez de tirarlo. Va dentro
                // del actualizador porque aquí llegan varios a la vez y cada uno tiene que
                // ver el estado que dejó el anterior.
                if (silencioso) {
                    const deAntes = cantidadesDelDia(prev);
                    const deAhora = cantidadesDelDia(siguiente);
                    queueMicrotask(() => setDeshacerElDia((d) =>
                        (d && d.huella === deAntes) ? { ...d, huella: deAhora } : d));
                }
                return siguiente;
            });
            // El recuadre silencioso (el del cruce de umbral) no lo ha pedido nadie, así que
            // tampoco ofrece deshacerlo: lo que hace es dejar la comida al día con sus macros
            // nuevos, y volver atrás la dejaría con los viejos.
            if (!silencioso) {
                setDeshacerComida(prev => ({
                    ...prev,
                    [mealKey]: { antes: antesDeCuadrar,
                                 huella: huellaConCantidades(comidaCuadrada),
                                 // Y SIN DECIR «CUADRÉ» SI NO CUADRÓ (21-09). Con la Comida 4
                                 // al 140 % la tarjeta decía «Cuadré las cantidades» y debajo,
                                 // «faltan 30 de proteína». Lo que se hizo fue ajustar; si
                                 // cuadró o no, lo dicen los números, y el texto va con ellos.
                                 texto: cuadra(res.desfases?.[mealKey])
                                     ? 'Cuadré las cantidades de esta comida.'
                                     : 'Ajusté las cantidades, pero esta comida sigue sin cuadrar.' },
                }));
            }
            if (quitados.length && !silencioso) {
                toast.info(quitados.length === 1
                    ? `Fuera ${quitados[0]}, como pediste.`
                    : `Fuera ${quitados.length} alimentos, como pediste: ${quitados.join(', ')}.`);
            }
            setDistribTargetsOverlay(null);   // pasa a mostrar los macros de hoy
            // Tras el recuadre automático del cruce de umbral, los macros del refit vienen
            // SIN la calibración del día encima: se fuerza una pasada más para que lo que
            // se ve (y se guarda) quede asentado con la regla nueva de la familia.
            if (silencioso) setCalibracionIntento(n => n + 1);
            // Cuadrar ya no quita ingredientes (08-08-2026): reparte y, si no llega a
            // cuadrar del todo, lo dice. El aviso de antes ("no cabían ni al mínimo")
            // además no era cierto: sí cabían, lo que pasaba es que los macros se
            // agotaban antes de llegar a ellos.
            const nEx = res.excluidos?.length || 0;
            const d = res.desfases?.[mealKey];
            // Lo que falta o sobra, con el mismo listón y las mismas palabras que el
            // «Cuadrar el día»: los dos botones hacen lo mismo y tienen que contarlo igual.
            const desajuste = desajusteEnPalabras(d);
            const nombre = NOMBRE_MACRO;
            // AQUÍ IBA LA COLETILLA DEL REDONDEO, Y SE HA IDO (Francisco, 31-08-2026): «quita
            // el texto intermedio, cantidades redondeadas para pesarlas fácil, no tiene
            // sentido».
            //
            // Nació de un motivo bueno (Jesús, 15-08, fallo 29: «5 g de aguacate es media
            // cucharadita, nadie pesa eso»), se reescribió en el punto 9 del 23-08 y el propio
            // Francisco ya la había acortado una vez. Pero salía pegada a «Comida cuadrada a
            // tus macros», que es la buena noticia, y la dejaba en un «cuadrada, pero...» que
            // no lleva a ninguna parte: el cliente no puede hacer nada con esa frase.
            //
            // Lo que la frase explicaba sigue estando donde se puede usar: las cantidades ya
            // salen en números pesables, y cuando de verdad no cuadra se dice por cuánto y de
            // qué (el aviso de abajo).
            if (silencioso) return;
            if (nEx) {
                toast.warning(`Comida cuadrada. ${nEx === 1 ? 'Un alimento ya no está' : `${nEx} alimentos ya no están`} en el catálogo y se quitó.`,
                    { duration: 9000 });
            } else if (desajuste) {
                const texto = desajuste;
                // Y se dice por dónde empezar, que es lo que hace útil el aviso. Quitar
                // lo decide el cliente: la app no toca lo que él ha puesto.
                const s = d.sugerencia;
                const comoArreglarlo = (s?.que_hacer === 'quitar_o_bajar'
                    ? ` Para cuadrarlo tendrías que quitar o bajar ${s.alimento}, que pone ${num1(s.aporta)} g de ${nombre[s.macro]}.`
                    : s?.que_hacer === 'anadir'
                        ? ` Para cuadrarlo te falta añadir algo con ${nombre[s.macro]}.`
                        : '')
                    // Y LO QUE FALTA, ADEMÁS (21-09): bajar lo que sobra no trae la
                    // proteína que falta, así que se dicen las dos cosas.
                    + (s?.ademas?.anadir
                        ? ` Y te falta añadir algo con ${nombre[s.ademas.anadir]}: ${num1(s.ademas.falta)} g.`
                        : '');
                // EL AVISO DECÍA TRES COSAS QUE NO ERAN (Gonzalo, minuto 10:59 de la reunión).
                //
                // La frase era una sola para todos los casos: «No se puede cuadrar sin quitar
                // nada: X. No se ha quitado ninguno.» Y de ahí salían tres mentiras:
                //
                //   1. Hablaba de QUITAR cuando lo que pasaba era que FALTA. En la misma línea
                //      decía «no se puede cuadrar sin quitar nada» y «te falta añadir algo».
                //      Quitar no arregla que falte: no sobra, falta.
                //   2. Decía «no se ha quitado ninguno» JUSTO DESPUÉS de quitar lo que el
                //      cliente acababa de marcar, y los dos avisos salían a la vez.
                //   3. Venía de cuando cuadrar sí quitaba por su cuenta (antes del 08-08).
                //      Desde que pregunta, esa cabecera ya no describía nada.
                //
                // Ahora la cabecera sale de lo que de verdad ha pasado: si sobra y no cabe
                // bajando, se dice; si falta, se dice que falta; y la coletilla de «no se ha
                // quitado ninguno» solo aparece cuando de verdad no se quitó nada.
                const soloFalta = d && ['P', 'H', 'G'].every(m => (d[m] ?? 0) <= MARGEN_QUE_SE_DICE);
                const cabecera = soloFalta
                    ? 'No llega a cuadrar'
                    : 'No se puede cuadrar sin quitar nada';
                // Y la coletilla solo cuando de verdad viene a cuento: si no sobraba nada,
                // nadie esperaba que se quitara nada, así que decirlo es ruido.
                const coletilla = (quitados.length || soloFalta) ? '' : ' No se ha quitado ninguno.';
                // Con «Deshacer» también aquí, y en la tarjeta: el motor ha movido cantidades
                // aunque no haya llegado a cuadrar, y es justo cuando más falta hace volver a
                // las de antes.
                toast.warning(
                    `${cabecera}: ${texto}.${comoArreglarlo}${coletilla}`,
                    { duration: 9000 });
            } else {
                toast.success('Comida cuadrada a tus macros.', { duration: 8000 });
            }
        } catch { toast.error('No se pudo cuadrar la comida'); }
    };
    // Al día, en cada render: es lo que hace que el «Cuadrar ahora» de un aviso trabaje sobre
    // el día que hay en pantalla y no sobre el que había cuando salió el aviso.
    cuadrarComidaRef.current = cuadrarComida;

    // La papelera preguntaba nada y borraba (Jesús, 15-08, fallo 33): un clic y la favorita
    // desaparecía, sin confirmar y sin deshacer. Es lo único de esta pantalla que no se
    // puede reconstruir desde el día, así que aquí sí se pregunta antes.
    const deleteDietFavorite = async (id) => {
        const fav = dietFavorites.find(f => f.id === id);
        const adelante = await confirm({
            title: `¿Borrar "${fav?.name || 'esta favorita'}"?`,
            // «CREASTE» Y NO «MONTASTE» (Francisco, 13-09, ficha 139): misma palabra vieja
            // que en el aviso de guardar la favorita.
            description: 'La favorita se borra para siempre. Los días que creaste con ella no se tocan.',
            confirmLabel: 'Borrar',
            danger: true,
        });
        if (!adelante) return;
        try {
            await api(`/api/diets/favorites/${id}`, { method: 'DELETE' });
            setDietFavorites(prev => prev.filter(f => f.id !== id));
            toast.success('Favorita borrada');
        } catch (err) {
            console.error('[borrar favorita]', err);
            toast.error('No hemos podido borrar la favorita. Inténtalo de nuevo.');
        }
    };

    /** Cambiarle el nombre a una favorita, sin tocar lo que lleva dentro. */
    const renombrarDietFavorite = async (id, nombre) => {
        try {
            await api(`/api/diets/favorites/${id}`, {
                method: 'PATCH', body: JSON.stringify({ name: nombre }),
            });
            setDietFavorites(prev => prev.map(f => (f.id === id ? { ...f, name: nombre } : f)));
            toast.success(`Ahora se llama "${nombre}"`);
        } catch (err) {
            console.error('[renombrar favorita]', err);
            toast.error('No hemos podido cambiarle el nombre. Inténtalo de nuevo.');
        }
    };

    // ── Favoritas de UNA comida (25-08) ───────────────────────────────────────
    // «CREAR INTRA» / «CREAR POST» (Jesús, artefacto del 25-09; Francisco: «lo monta de un
    // toque»). El servidor dice qué va (el primero de Proteína y el primero de Hidratos del
    // orden del método, con la cantidad que cubre el 100 %) y aquí se pone en la comida igual
    // que al guardar la ventana de montar: el día se guarda solo detrás.
    const [creandoPeri, setCreandoPeri] = useState(null);
    // ── EL INTRA, PASO A PASO (Jesús, artefacto del 27-09) ─────────────────────
    //
    // EL FAVORITO A LOS MACROS DE HOY. «La app ya sabe cuadrar: cuando le cambies los macros,
    // recalcula las cantidades del favorito.» Lo de proteína se escala para cubrir la proteína
    // de hoy; lo que solo lleva hidratos, para cubrir los hidratos que queden; lo que no aporta
    // (las bebidas zero) se queda como está. Gramos enteros hacia arriba, como el sugeridor.
    function ajustarPeriFavorito(alimentos, objetivo) {
        const lista = (alimentos || []).map((a) => ({ ...a }));
        const obj = { P: Number(objetivo?.P) || 0, H: Number(objetivo?.H) || 0 };
        if (!obj.P && !obj.H) return lista;
        const porG = (a, k) => ((a.macros_efectivos?.[k] || 0) / (a.cantidad_g || 1));
        const conP = lista.filter((a) => porG(a, 'P') > 0);
        const soloH = lista.filter((a) => porG(a, 'P') <= 0 && porG(a, 'H') > 0);
        const escalar = (a, f) => {
            if (!(f > 0) || !Number.isFinite(f)) return;
            const antes = a.cantidad_g || 0;
            const g = Math.max(1, Math.ceil(antes * f - 1e-9));
            const r = g / (antes || 1);
            a.cantidad_g = g;
            a.macros_efectivos = Object.fromEntries(Object.entries(a.macros_efectivos || {})
                .map(([k, v]) => [k, Math.round((v || 0) * r * 10) / 10]));
        };
        const sumP = conP.reduce((t, a) => t + (a.macros_efectivos?.P || 0), 0);
        if (sumP > 0 && obj.P > 0) conP.forEach((a) => escalar(a, obj.P / sumP));
        const hDeLaProteina = conP.reduce((t, a) => t + (a.macros_efectivos?.H || 0), 0);
        const sumH = soloH.reduce((t, a) => t + (a.macros_efectivos?.H || 0), 0);
        if (sumH > 0) soloH.forEach((a) => escalar(a, Math.max(0, obj.H - hDeLaProteina) / sumH));
        return lista;
    }

    // «NO QUIERO INTRA»: hoy sin intra. «Los 9 P y 4,5 H no se pierden: se reparten en las
    // demás comidas» -- que es lo que ya hace la opción del día sin intra -- «y se lo diría».
    // Solo cambia HOY: la ficha sigue con su intra (por eso no va por `handleSetOpcionPeri`).
    const intraQuitadoRef = useRef(null);
    const noQuieroPeri = (mealKey) => {
        if (mealKey !== 'Intra') return;
        const obj = getMealTarget('Intra') || {};
        const sinIntra = opcionPeri === 'solo_intra' ? 'sin_peri' : 'solo_post';
        setOpcionPeri(sinIntra);
        // Lo que llevaba se guarda para «Ponerlo»: vuelve tal cual estaba, no vacío.
        intraQuitadoRef.current = mealsData?.Intra ? { ...mealsData.Intra } : null;
        setMealsData((prev) => ({ ...prev, Intra: { ...(prev?.Intra || {}), alimentos: [], puesto_solo: false } }));
        const cuanto = [obj.P > 0 && `${num1(obj.P)} P`, obj.H > 0 && `${num1(obj.H)} H`].filter(Boolean).join(' y ');
        toast.success('Hoy sin intra', {
            description: cuanto ? `Sus ${cuanto} van a las demás comidas.` : 'Sus macros van a las demás comidas.',
        });
    };
    const ponerIntraOtraVez = () => {
        setOpcionPeri(opcionPeri === 'sin_peri' ? 'solo_intra' : 'intra_post');
        const antes = intraQuitadoRef.current;
        intraQuitadoRef.current = null;
        if ((antes?.alimentos || []).length) setMealsData((prev) => ({ ...prev, Intra: antes }));
    };
    // La tarjeta gris: la ficha lleva intra, hoy no, y es día de entreno con peri.
    const intraRechazado = tipoDia !== 'descanso'
        && ['intra_post', 'solo_intra'].includes(opcionPeriFicha)
        && ['solo_post', 'sin_peri'].includes(opcionPeri)
        && !(profile?.macros_periworkout
            && !(Number(profile.macros_periworkout.protein ?? profile.macros_periworkout.proteinas ?? 0) > 0)
            && !(Number(profile.macros_periworkout.carbs ?? profile.macros_periworkout.hidratos ?? 0) > 0));
    const tarjetaIntraRechazado = (
        <div key="intra-rechazado" className="surface p-3.5 flex items-center justify-between gap-3 opacity-70"
            data-testid="intra-rechazado">
            <div className="min-w-0">
                <p className="font-heading font-bold uppercase text-sm text-muted-foreground">Intra</p>
                <p className="text-xs text-muted-foreground">Hoy sin intra · sus macros van a las demás comidas</p>
            </div>
            <button type="button" onClick={ponerIntraOtraVez} data-testid="poner-intra"
                className="flex-shrink-0 px-3 py-1.5 rounded-lg border border-border text-xs font-semibold text-foreground hover:border-brand/50">
                Ponerlo
            </button>
        </div>
    );

    // AL GUARDAR EL INTRA, LA PREGUNTA. La primera vez, si lo deja puesto («Si ya dijo que no
    // una vez, ¿se lo vuelves a preguntar? Yo lo preguntaría la primera vez y no más»); y si ya
    // tiene uno puesto y guarda otro distinto, si vale a partir de ahora.
    const firmaDe = (lista) => (lista || []).map((a) => a.alimento_id).filter((x) => x != null).sort().join('-');
    const alGuardarComida = (mealKey, alimentos) => {
        if (!['Intra', 'Post'].includes(mealKey) || !(alimentos || []).length || actuandoComoId()) return;
        const peri = mealKey === 'Intra' ? 'intra' : 'post';
        const fav = profile?.peri_favorito?.[peri];
        const esElPuesto = fav && firmaDe(fav.alimentos) === firmaDe(alimentos);
        setMealsData((prev) => ({ ...prev, [mealKey]: { ...(prev?.[mealKey] || {}), puesto_solo: !!esElPuesto } }));
        if (esElPuesto) return;
        if (fav) setPreguntaPeriFav({ tipo: 'cambio', peri, mealKey, alimentos, antes: fav.alimentos });
        else if (!profile?.peri_favorito_preguntado?.[peri]) setPreguntaPeriFav({ tipo: 'nuevo', peri, mealKey, alimentos });
    };
    const contestarPeriFav = async (dejarlo) => {
        const p = preguntaPeriFav;
        setPreguntaPeriFav(null);
        if (!p) return;
        try {
            await api('/api/diets/peri-favorito', {
                method: 'PUT',
                body: JSON.stringify(dejarlo ? { peri: p.peri, alimentos: p.alimentos } : { peri: p.peri, preguntado: true }),
            });
            if (dejarlo) {
                setMealsData((prev) => ({ ...prev, [p.mealKey]: { ...(prev?.[p.mealKey] || {}), puesto_solo: true } }));
                toast.success(p.tipo === 'nuevo'
                    ? `${p.peri === 'intra' ? 'Intra' : 'Post'} puesto: te saldrá hecho cada día de entreno`
                    : 'Cambiado: a partir de ahora, éste');
            }
            await refreshProfile?.();
        } catch {
            toast.error('No se pudo guardar. Prueba otra vez.');
        }
    };

    const crearPeri = async (mealKey) => {
        const obj = getMealTarget(mealKey) || {};
        const peri = mealKey === 'Intra' ? 'intra' : 'post';
        const nombre = mealKey === 'Intra' ? 'intra' : 'post';
        setCreandoPeri(mealKey);
        try {
            const res = await api(`/api/calculator/peri-sugerencias?peri=${peri}`
                + `&p_rest=${Math.max(0, obj.P || 0)}&h_rest=${Math.max(0, obj.H || 0)}`);
            const elegidos = res?.crear || [];
            if (elegidos.length === 0) {
                toast.error(`No hemos encontrado con qué montar el ${nombre}. Puedes hacerlo tú con «Lo hago yo».`);
                return;
            }
            const alimentos = elegidos.map((f) => ({
                ...f,
                alimento_id: f.id,
                cantidad_g: f._cantidad_sugerida,
                por_unidad: f.por_unidad ?? f.unidades ?? false,
                peso_unidad: f.peso_unidad || f.racion || 100,
                racion: f.racion || 100,
                macros_efectivos: {
                    P: f._macros_sugeridos?.P || 0,
                    H: f._macros_sugeridos?.H || 0,
                    G: f._macros_sugeridos?.G || 0,
                },
                bloque: null,
            }));
            setMealsData((prev) => ({ ...prev, [mealKey]: { ...prev[mealKey], alimentos } }));
            toast.success(`${nombre === 'intra' ? 'Intra' : 'Post'} creado · ${alimentos.map((a) => a.nombre).join(' y ')}`);
        } catch (err) {
            console.error('[crear peri]', err);
            toast.error(`No hemos podido crear el ${nombre}. Prueba otra vez.`);
        } finally {
            setCreandoPeri(null);
        }
    };

    // ── COMPARTIR UNA COMIDA (César 24-09, Francisco 26-09) ──────────────────
    // Actuando como un cliente no se comparte: el menú saldría con SU nombre y eso lo decide él.
    const puedeCompartir = !actuandoComoId();
    useEffect(() => {
        if (!puedeCompartir) return;
        api('/api/menus-compartidos/mios')
            .then((r) => setCompartidas(new Set((r.menus || []).map((m) => m.firma))))
            .catch(() => {});
    }, [api, puedeCompartir]);

    // SE COMPARTE SOLO, AL QUEDAR EL DÍA CUADRADO (Jesús, 27-09: «lo de compartir debajo de
    // cada comida no; las comidas se comparten por defecto si están todas cuadradas»). Es lo
    // que ya decía el artefacto del 24-09: «todas las que le queden cuadradas se comparten
    // solas». Sin avisos: si una no pasa el filtro de la biblioteca (sin verdura, pocos
    // alimentos...), simplemente no se comparte. Quien lo apagó en Mi perfil no comparte.
    // Espera a que el día se quede quieto unos segundos, para no compartir cada paso intermedio.
    const yaMandadasRef = useRef(new Set());
    const compartirDiaCuadrado = () => {
        if (!puedeCompartir) return;
        comidasParaCompartir({
            diaCuadrado: getDayStatus() === 'cuadrado', compartir: profile?.compartir_menus,
            orden: getMealOrder(), comidas: mealsData, compartidas,
            yaMandadas: yaMandadasRef.current, fecha: currentDate,
        }).forEach(({ comida, alimentos, clave }) => {
            yaMandadasRef.current.add(clave);
            api('/api/menus-compartidos', {
                method: 'POST',
                body: JSON.stringify({ comida, alimentos, macros: calculateMealMacros(comida) }),
            })
                .then((r) => setCompartidas((prev) => new Set([...prev, r.menu.firma])))
                .catch(() => {});
        });
    };

    const abrirFavoritasDeComida = async (mealKey) => {
        setFavComidaModal({ open: true, mealKey });
        try {
            const res = await api('/api/diets/favorites?ambito=comida');
            // EN EL POST, SUS POSTENTRENOS FAVORITOS (Jesús, 27-09: «te tiene que meter en sus
            // postentrenos favoritos, no en las comidas favoritas»). Los de otros usuarios
            // están en «Sugiéreme un postentreno».
            const todas = res.favorites || [];
            setComidaFavoritas(mealKey === 'Post' ? todas.filter((f) => f.comida_origen === 'Post') : todas);
        } catch (err) {
            console.error('[favoritas de comida]', err);
            setComidaFavoritas([]);
        }
    };

    const guardarComidaFavorita = async (name) => {
        const mealKey = favComidaModal.mealKey;
        const alimentos = (mealsData[mealKey]?.alimentos) || [];
        try {
            await api('/api/diets/favorites', {
                method: 'POST',
                body: JSON.stringify({
                    name,
                    ambito: 'comida',
                    comida: mealKey,
                    tipo_dia: tipoDia,
                    alimentos,
                    macros_snapshot: calculateMealMacros(mealKey),
                }),
            });
            toast.success(`Guardada · "${name}" ya es una de tus comidas`, {
                description: 'La tienes en Favoritas de cualquier comida, no solo de esta.',
                duration: 6000,
            });
            const res = await api('/api/diets/favorites?ambito=comida');
            setComidaFavoritas(res.favorites || []);
        } catch (err) {
            console.error('[guardar comida favorita]', err);
            toast.error('No hemos podido guardar la comida. Inténtalo de nuevo.');
        }
    };

    // `destino`: la comida donde va, cuando no sale del modal de favoritas (el «Usar en una
    // comida» de un menú del ranking).
    const aplicarComidaFavorita = async (fav, destino = null) => {
        const mealKey = destino || favComidaModal.mealKey;
        // Las unidades, enteras: las favoritas de Calma traen la cucharadita a 1,7 g (01-10).
        const alimentos = unidadesEnteras(fav.alimentos || []);
        setFavComidaModal({ open: false, mealKey: null });

        // En el peri no se cuadra (igual que el botón «Cuadrar», que ahí no se pinta):
        // se vuelca tal cual y el cliente ajusta con el sugeridor.
        if (['Intra', 'Post'].includes(mealKey)) {
            setMealsData(prev => ({ ...prev, [mealKey]: { alimentos } }));
            toast.success(`"${fav.name}" puesta tal cual`);
            return;
        }
        // EN MANUAL, O SI YA CUADRA, CON SUS CANTIDADES (Jesús Díaz, 29-09): su batido de
        // 48 P · 47 H · 12 G iba a una comida de 47,5 · 48 · 12 y el recuadre lo dejó en 490 g
        // de queso batido y 10 g de whey, y encima pasó la comida a Automático. El modo de la
        // comida se conserva siempre (ver lib/favoritaTalCual).
        const modo = mealsData[mealKey]?.modo;
        const conModo = (comida) => (modo ? { ...comida, modo } : comida);
        if (entraTalCual({ alimentos, objetivo: getMealTarget(mealKey), modo, margen: MARGEN })) {
            setMealsData(prev => ({ ...prev, [mealKey]: conModo({ alimentos }) }));
            setDistribTargetsOverlay(null);
            toast.success(`"${fav.name}" puesta con sus cantidades`);
            return;
        }
        // Los gramos guardados son los de OTRO día: se re-cuadran a los macros de hoy,
        // igual que hace la favorita de día. Si el refit falla, se vuelca tal cual y se
        // dice, que es mejor que dejar la comida vacía.
        try {
            // LA RECETA ENTRA CUADRADA, O LO INTENTA COMO EL BOTÓN (JDG, Trello 21-09):
            // «cuando añado una receta a una comida no sale cuadrada directamente». Aquí se
            // pedía UNA pasada y sin el resto del día; el botón «Cuadrar» insiste hasta
            // cuadrar y manda el contexto del día para el tramo. Se hace igual.
            const pedirRefit = (lista) => api('/api/calculator/refit-diet', {
                method: 'POST',
                body: JSON.stringify({
                    fecha: currentDate,
                    tipo_dia: tipoDia, num_comidas: numComidas,
                    momento_entreno: momentoEntreno, opcion_peri: opcionPeri,
                    comidas: { [mealKey]: { alimentos: lista } },
                    contexto_dia: Object.fromEntries(getMealOrder()
                        .filter(k => k !== mealKey)
                        .map(k => [k, (mealsData[k]?.alimentos || []).map(a => ({
                            alimento_id: a.alimento_id ?? null,
                            cantidad_g: a.cantidad_g ?? 0,
                        }))])),
                }),
            });
            let res = await pedirRefit(alimentos);
            const cuadraYa = (d) => !d || ['P', 'H', 'G'].every((x) => Math.abs(d[x] || 0) <= MARGEN);
            for (let vuelta = 0; vuelta < 3 && res?.comidas?.[mealKey] && !res.decisiones?.[mealKey]
                    && !cuadraYa(res.desfases?.[mealKey]); vuelta++) {
                const anterior = (res.comidas[mealKey].alimentos || []).map(a => a.cantidad_g);
                const otra = await pedirRefit(res.comidas[mealKey].alimentos);
                if (!otra?.comidas?.[mealKey]) break;
                res = otra;
                if (JSON.stringify((otra.comidas[mealKey].alimentos || []).map(a => a.cantidad_g)) === JSON.stringify(anterior)) break;
            }
            const refit = res.comidas?.[mealKey];
            // Si al recuadrarla sobraba y había varios sitios de donde bajarlo, se ha bajado
            // de todos a la vez y la comida queda marcada: la pregunta salta con «Cuadrar».
            const hayQueElegir = res.decisiones?.[mealKey];
            setMealsData(prev => ({
                ...prev,
                [mealKey]: hayQueElegir
                    ? conModo({ ...(refit || { alimentos }),
                        eleccion_pendiente: eleccionPendienteDe({ alimentos }, hayQueElegir) })
                    : conModo(refit || { alimentos }),
            }));
            setDistribTargetsOverlay(null);   // los macros de HOY, no los del día guardado
            if (!refit) {
                toast.success(`"${fav.name}" puesta con las cantidades guardadas`);
                return;
            }
            // NO SE DICE «CUADRADA» SI NO LO ESTÁ. Una comida guardada en otro sitio puede
            // no caber en el objetivo de esta (p. ej. 33 g de hidratos en una comida de 10),
            // y cuadrar no quita ingredientes: los reparte lo mejor que puede. Mismo
            // criterio de desfase que el botón «Cuadrar», para que no digan cosas distintas.
            const d = res.desfases?.[mealKey];
            const nombre = { P: 'proteína', H: 'hidratos', G: 'grasa' };
            const falla = d ? ['P', 'H', 'G'].filter(m => Math.abs(d[m]) > 4) : [];
            if (!falla.length) {
                toast.success(`"${fav.name}" puesta y cuadrada a esta comida`);
                return;
            }
            const texto = falla.map(m => `${d[m] > 0 ? 'sobran' : 'faltan'} `
                + `${num1(Math.abs(d[m]))} g de ${nombre[m]}`).join(' y ');
            toast.warning(`"${fav.name}" puesta, pero no cuadra aquí: ${texto}.`, {
                description: 'Venía de una comida con otro objetivo. Ajusta o quita algo.',
                duration: 9000,
            });
        } catch (err) {
            console.error('[aplicar comida favorita]', err);
            setMealsData(prev => ({ ...prev, [mealKey]: conModo({ alimentos }) }));
            toast.success(`"${fav.name}" puesta con las cantidades guardadas`, {
                description: 'No hemos podido ajustarlas a los macros de hoy: revisa las cantidades.',
            });
        }
    };

    const borrarComidaFavorita = async (id) => {
        const fav = comidaFavoritas.find(f => f.id === id);
        const adelante = await confirm({
            title: `¿Borrar "${fav?.name || 'esta comida'}"?`,
            description: 'Se borra para siempre. Los días donde ya la usaste no se tocan.',
            confirmLabel: 'Borrar',
            danger: true,
        });
        if (!adelante) return;
        try {
            await api(`/api/diets/favorites/${id}`, { method: 'DELETE' });
            setComidaFavoritas(prev => prev.filter(f => f.id !== id));
            toast.success('Comida borrada de favoritas');
        } catch (err) {
            console.error('[borrar comida favorita]', err);
            toast.error('No hemos podido borrarla. Inténtalo de nuevo.');
        }
    };

    // Day summary
    // Per-meal builder mode (manual | auto). Default auto. Switching never touches the
    // already-loaded foods (spread prev[mealKey]); autosave persists `modo` inside comidas.
    const setMealMode = (mealKey, modo) => {
        setMealsData(prev => ({
            ...prev,
            [mealKey]: { alimentos: [], ...(prev[mealKey] || {}), modo },
        }));
    };

    const dayMacros = calculateDayMacros();
    const dayTarget = distribution?.resumen || { P_total: 0, H_total: 0, G_total: 0, kcal_total: 0 };
    // Peri (intra/post) grasas do NOT count toward the comidas budget. Calma: peri objetivo has
    // no grasas key, so resumen.G_total = sum(comidas.G) only (backend macro_distribution sums
    // peri P/H but NOT G). dayMacros.G however includes peri served grasas → subtract them so the
    // comidas G served stays consistent with the comidas-only G_total. (P/H need no subtraction:
    // their _total budgets already include peri.)
    const servedPeriG = (calculateMealMacros('Intra').G || 0) + (calculateMealMacros('Post').G || 0);
    const comidasG = dayMacros.G - servedPeriG;
    const remainingDay = {
        P: Math.max(0, Math.round((dayTarget.P_total || 0) - dayMacros.P)),
        H: Math.max(0, Math.round((dayTarget.H_total || 0) - dayMacros.H)),
        G: Math.max(0, Math.round((dayTarget.G_total || 0) - comidasG)),
    };

    // Calma volcarMacros(t): meal `t` absorbs the day's remaining macros (target computed in
    // getMealTarget), every OTHER meal is locked. Locking lives in `volcadoMeal` state, not in
    // an overlay, so removing the volcado restores the normal per-meal targets exactly.
    const isMealLocked = (mealKey) => activeVolcado != null && mealKey !== activeVolcado;

    const persistVolcado = async (meal) => {
        try {
            await api('/api/diets', {
                method: 'POST',
                body: JSON.stringify({
                    fecha: currentDate,
                    tipo_dia: tipoDia, num_comidas: numComidas,
                    momento_entreno: momentoEntreno, opcion_peri: opcionPeri,
                    comidas: comidasDelReparto(), macros_snapshot: distribution?.resumen,
                    distribution_targets: distribTargetsOverlay || null,
                    is_cuadrado: getDayStatus() === 'cuadrado',
                    comida_volcada: meal,
                    base_updated_at: versionDiaRef.current,
                })
            });
        } catch (err) { /* silent: volcado state is already applied in the UI */ }
    };

    const handleVolcarToMeal = (mealKey) => {
        if (['Intra', 'Post'].includes(mealKey)) return;
        setVolcadoMeal(mealKey);
        persistVolcado(mealKey);
        toast.success(`Macros volcados en ${mealInfo[mealKey]?.name} - las demás comidas quedan bloqueadas`);
    };

    const handleEliminarVolcado = () => {
        setVolcadoMeal(null);
        persistVolcado(null);
        toast.info('Volcado deshecho: cada comida vuelve a su objetivo.');
    };

    // RECUADRAR EL DÍA ENTERO (punto 26 del doc del 23-08): al cambiar los macros, los
    // días ya creados se quedaban en «TE PASAS» contra el objetivo nuevo y nadie le
    // ofrecía rehacerlos. El motor ya existía (refit-diet, el mismo de las favoritas):
    // reajusta cantidades sin pasarse y respetando el mínimo de cada alimento.
    const [recuadrando, setRecuadrando] = useState(false);

    // La suplementación del cliente, para poder poner «+ Creatina» debajo de su comida. Se
    // pide UNA vez al montar la pantalla: no cambia al pasar de un día a otro. Si el plan no
    // la lleva, el servidor devuelve 403 y se queda vacía sin decir nada.
    const [suplementos, setSuplementos] = useState([]);
    useEffect(() => {
        let vivo = true;
        api('/api/supplements/current')
            .then(r => { if (vivo) setSuplementos(r?.actual || []); })
            .catch(() => {});
        return () => { vivo = false; };
    }, [api]);

    // LOS SUPLEMENTOS DEL DÍA (maqueta de la parte 6). El protocolo ya trae resuelto con qué
    // comida va cada uno (`en_comidas`, punto 174); aquí sólo se reparten entre las comidas que
    // este día tiene, con la misma regla que el Inicio. Si la petición falla -- o el plan no
    // lleva suplementación y devuelve 403 --, no sale nada y la pantalla sigue igual.
    const supPorComida = React.useMemo(
        () => suplementosPorComida(suplementos, getMealOrder().filter(k => k !== 'Intra' && k !== 'Post')),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [suplementos, numComidas, momentoEntreno, opcionPeri]);
    // Las comidas a las que «Cuadrar el día» le ha mandado (25-09). Se pintan mientras sigan
    // fuera de su margen: en cuanto las cuadra, se van solas de la lista.
    //
    // CON SU FECHA (29-09): era solo la lista, así que al cambiar de día el aviso seguía
    // puesto y juzgaba las comidas del día nuevo con las del anterior.
    const [comidasACuadrar, setComidasACuadrar] = useState(null);
    // LOS AVISOS SE PUEDEN CERRAR (Jesús, 28-09): ver `lib/avisosCerrados`. El contador solo
    // sirve para volver a pintar después de cerrar uno (lo cerrado vive en sessionStorage).
    const [, setAvisosCerradosTick] = useState(0);
    const cerrarAvisoDelDia = (aviso, huella = '') => {
        cerrarAviso(uid, aviso, currentDate, huella);
        setAvisosCerradosTick((n) => n + 1);
    };
    const comidaFueraDeMargen = (k) => {
        if (!((mealsData[k]?.alimentos) || []).length) return false;
        const objetivo = getMealTarget(k) || {};
        const hay = calculateMealMacros(k);
        const cuales = (k === 'Intra' || k === 'Post') ? ['P', 'H'] : ['P', 'H', 'G'];
        return cuales.some((x) => Math.abs((hay[x] || 0) - (objetivo[x] || 0)) > MARGEN);
    };
    // LLEVAR A UNA COMIDA: elegirla, desplegarla e ir a ella (la tarjeta que se ve: la del
    // teléfono y la del escritorio conviven en la página).
    const irAComida = (k) => {
        setSelectedMeal(k);
        setExpandedMeals((prev) => ({ ...prev, [k]: true }));
        setTimeout(() => {
            const nodos = [...document.querySelectorAll(`[data-testid="meal-card-${k}"]`)];
            const visible = nodos.find((n) => n.offsetHeight > 0);
            if (visible) {
                const y = visible.getBoundingClientRect().top + window.scrollY - 90;
                window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
            }
        }, 250);
    };

    const handleRecuadrarDia = async () => {
        setRecuadrando(true);
        // EL DÍA ENTERO DE ANTES, PARA «DESHACER» (ficha 117, 5-09): lo mismo que hace
        // `cuadrarComida`, pero con las seis comidas a la vez, que es lo que este botón toca.
        //
        // Y DESDE EL 6-09 CADA COMIDA SE DESHACE POR SU CUENTA, EN SU TARJETA (Jesús: el
        // botón del aviso no se puede tocar). Este botón toca varias comidas de una vez, así
        // que no hay UNA tarjeta donde ponerlo: se deja el «Deshacer» en TODAS las que hayan
        // cambiado, cada una con las cantidades que tenía. Se pierde el «deshazlo todo» de un
        // solo toque, y se gana poder devolver la comida que no gustó sin tirar las otras
        // cinco. Ver `deshacerComida`.
        const antesDelDia = mealsData;
        // ── LAS COMIDAS QUE YA ESTÁN BIEN NO SE TOCAN (Francisco, 17-09) ──────
        //
        //     «Me ofrece ajustar Comida 1 y Comida 3, pero esas comidas están válidas o
        //      cuadradas; la que no cuadra es la cuatro.»
        //
        // Tenía razón y era de fondo, no del cartel: el botón mandaba al motor las SEIS
        // comidas y el motor re-repartía el día entero, así que devolvía cantidades nuevas
        // también para las que estaban en su sitio. Con el aviso previo del 16-09 eso ya no
        // pasa desapercibido -- ahora se lee lo que iba a tocar --, y lo que se lee es que
        // toca lo que no hacía falta tocar.
        //
        // Ahora se le manda SOLO lo que está fuera de margen, y el resto se queda tal cual.
        // Si no hay ninguna fuera pero el día no cierra -- cuatro comidas válidas con «un
        // poco» cada una suman un día de +6,7 (Jesús, 10-09) --, entonces sí entran todas:
        // ahí no hay otro sitio de donde sacarlo.
        const sePasaOFalta = (k) => {
            const objetivo = getMealTarget(k) || {};
            const hay = calculateMealMacros(k);
            // La grasa del peri no cuenta en el método, así que ahí tampoco se juzga.
            const cuales = (k === 'Intra' || k === 'Post') ? ['P', 'H'] : ['P', 'H', 'G'];
            return cuales.some((x) => Math.abs((hay[x] || 0) - (objetivo[x] || 0)) > MARGEN);
        };
        // UNA COMIDA VACÍA NO SE CUADRA: SE MONTA (Francisco, 21-09). Con el Intra y el Post
        // sin nada, «Terminar el día» decía «Intra no lleva nada que ponga hidratos y le
        // faltan 21 g» -- mentira: no es que no lleve hidratos, es que no lleva NADA -- y
        // encima intentaba tapar sus macros con las demás comidas. Las vacías se quedan
        // fuera del cuadre y se nombran como lo que son: sin montar.
        const vacias = getMealOrder().filter((k) => !((mealsData[k]?.alimentos) || []).length);
        const descuadradas = getMealOrder().filter((k) => !vacias.includes(k) && sePasaOFalta(k));
        // ── SI HAY COMIDAS FUERA DE SU MARGEN, SE LE MANDA A ELLAS (Jesús, reunión del 25-09) ──
        //
        //     «El día no cuadra bien. Es mejor que para que cuadre mal, directamente te diga
        //      que te vayas a las comidas: que tienes que cuadrar la comida X y la comida X,
        //      y ya te lleve a esas comidas. Así no va a fallar nunca, porque la comida la
        //      cuadra bien.»
        //
        // Así que aquí no se mueve nada: se le dice cuáles y se le lleva. El motor del día
        // solo entra cuando todas las comidas están en su margen y aun así el total no cierra
        // (ahí no hay una comida a la que mandarle).
        //
        // Y SIN BAJAR SOLO A LA COMIDA (Jesús, 28-09, 22:21): «el aviso te salta, pero te
        // baja a la comida y no te da tiempo a grabarlo... no te da tiempo ni a verlo».
        // Hasta hoy se llamaba aquí a `irAComida` con la primera. Ahora la pantalla se
        // queda donde está y el que lleva a cada comida es su botón «Ir a Comida N» del
        // aviso. Solo si el aviso queda fuera de la vista (se pulsó «Terminar el día» con
        // la página bajada) se acerca el AVISO, no la comida.
        if (descuadradas.length) {
            reabrirAviso(uid, 'cuadra-estas-comidas', currentDate);
            setComidasACuadrar({ fecha: currentDate, comidas: descuadradas });
            setRecuadrando(false);
            setTimeout(() => {
                const aviso = [...document.querySelectorAll('[data-testid="cuadra-estas-comidas"]')]
                    .find((n) => n.offsetHeight > 0);
                if (!aviso) return;
                const r = aviso.getBoundingClientRect();
                if (r.top < 70 || r.bottom > window.innerHeight) {
                    const y = r.top + window.scrollY - 90;
                    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
                }
            }, 100);
            return;
        }
        //: Con qué comidas se trabaja. Empieza por las que están fuera y se amplía a todas
        //: solo si con ellas el día no llega a cerrar (ver `hizoFaltaAmpliar`).
        let seTocan = descuadradas.length ? descuadradas : getMealOrder();
        //: DE DONDE PARTE CADA VUELTA. Por defecto, lo que hay en pantalla; pero al repetir
        //: hay que partir de lo que devolvio la vuelta anterior, que si no cada vuelta pide
        //: exactamente lo mismo y devuelve exactamente lo mismo (Francisco, 20-09: «tuve que
        //: presionar dos veces»). Eso era el segundo clic: partir de lo ya cuadrado.
        let puntoDePartida = null;
        const soloLasQueSeTocan = () => Object.fromEntries(
            Object.entries(puntoDePartida || comidasDelReparto())
                .filter(([k]) => seTocan.includes(k)));
        // El día entero, con lo cuadrado encima: el motor solo devuelve lo que se le manda, y
        // tanto la medición del día como lo que se pinta necesitan las seis.
        const conLasDeSiempre = (comidas) => ({ ...comidasDelReparto(), ...(comidas || {}) });
        // UNA PASADA ENTERA con el juego de comidas que toque en ese momento: el refit de
        // esas comidas y las vueltas de cierre del día. Se puede repetir con otro juego, que
        // es lo que hace la ampliación de más abajo.
        const unaPasada = async () => {
            // EL DÍA LO CIERRA EL SERVIDOR (22-09). Se le manda lo que se toca, el resto
            // del día como contexto (cuenta en el total y no se mueve) y el objetivo del
            // día, y devuelve el día ya cerrado en `dia` (ver `core/cerrar_el_dia`). Las
            // vueltas de cierre que había aquí se quedan solo como respaldo por si la
            // respuesta viniera sin `dia` (un servidor viejo).
            const lasQueSeTocan = soloLasQueSeTocan();
            const res = await api('/api/calculator/refit-diet', {
                method: 'POST',
                body: JSON.stringify({
                    fecha: currentDate, tipo_dia: tipoDia, num_comidas: numComidas,
                    momento_entreno: momentoEntreno, opcion_peri: opcionPeri,
                    comidas: lasQueSeTocan,
                    meal_order: getMealOrder(),
                    contexto_dia: Object.fromEntries(getMealOrder()
                        .filter(k => !(k in lasQueSeTocan))
                        .map(k => [k, (mealsData[k]?.alimentos || []).map(a => ({
                            alimento_id: a.alimento_id ?? null,
                            cantidad_g: a.cantidad_g ?? 0,
                        }))])),
                    cerrar_dia: { objetivo: { P: dayTarget.P_total || 0, H: dayTarget.H_total || 0, G: dayTarget.G_total || 0 } },
                }),
            });
            // ── Y EL DÍA TIENE QUE CERRAR, NO SOLO CADA COMIDA (Jesús, 10-09) ──
            //
            // «El botón de terminar, a nivel de día... que reparta entre las comidas lo que
            // le falte al total.» Una pasada sola no lo consigue: cada comida se afina
            // aceptando pasarse un poco y queda dentro de su margen, pero cuatro «un poco»
            // sumaban un día de +6,7 g de hidratos con las cuatro comidas en verde. Es su
            // regla de fondo del revés: «la comida se da por válida con margen. El día no:
            // el día tiene que cerrar».
            //
            // Así que se mide lo que le sobra o le falta AL DÍA y se reparte como corrección
            // sobre los objetivos de las comidas principales -- a peso, según lo grande que
            // sea cada una --, y se vuelve a pedir. El peri no entra: sus objetivos son los
            // que son y la grasa allí va libre.
            let yaCuadrado = conLasDeSiempre(res.comidas);
            // Fuera del `try`: el aviso de los alimentos que se caen lo mira después, y
            // declarada dentro se quedaba sin definir justo donde hace falta.
            let segunda = null;
            const acumulado = {};
            // Lo que le queda al DÍA en la última medición, para poder contarlo después con
            // la misma vara que la banda de arriba. `{P,H,G}` como «objetivo menos servido».
            let restoDelDia = null;
            // Lo que le falta o le sobra AL DÍA con unas comidas dadas, medido como lo mide
            // la banda. Separado del cierre para poder medir DESPUÉS de cada vuelta (22-09).
            const medirElDia = async (comidas) => {
                const objetivo = { P: dayTarget.P_total || 0, H: dayTarget.H_total || 0,
                                   G: dayTarget.G_total || 0 };
                // CON LOS NÚMEROS DE LA PANTALLA, NO CON LOS DEL MOTOR. El refit devuelve sus
                // macros calibrados con el tramo del día DE ENTRADA, y al cambiar las
                // cantidades el tramo puede cambiar: 300 ms después `/calibrar-dia` los
                // reescribe y son esos los que el cliente ve y los que deciden si el día
                // cierra. Midiendo los del motor el resto salía cero y esta segunda pasada
                // no llegaba a hacerse nunca, con el día en +6,7 g de hidratos en pantalla.
                const orden = getMealOrder();
                let cal = null;
                try {
                    cal = await api('/api/calculator/calibrar-dia', {
                        method: 'POST',
                        body: JSON.stringify({
                            meal_order: orden,
                            comidas: Object.fromEntries(orden.map(k => [k,
                                (comidas[k]?.alimentos || []).map(a => ({
                                    alimento_id: a.alimento_id ?? null,
                                    cantidad_g: a.cantidad_g ?? 0,
                                }))])),
                        }),
                    });
                } catch { /* sin calibración se miden los del motor, que es lo de antes */ }
                const macrosDe = (k, a, i) => (cal?.comidas?.[k]?.[i]?.macros_efectivos)
                    || a?.macros_efectivos || {};
                const suma = { P: 0, H: 0, G: 0 };
                const peso = {};
                let pesoTotal = 0;
                for (const [k, c] of Object.entries(comidas)) {
                    (c?.alimentos || []).forEach((a, i) => {
                        const m = macrosDe(k, a, i);
                        // La grasa del peri no cuenta en el total del día, igual que arriba.
                        for (const x of ['P', 'H', 'G']) {
                            if (x === 'G' && (k === 'Intra' || k === 'Post')) continue;
                            suma[x] += Number(m[x]) || 0;
                        }
                    });
                    if (k === 'Intra' || k === 'Post') continue;
                    const t = distribution?.comidas?.[k] || {};
                    peso[k] = (Number(t.P) || 0) + (Number(t.H) || 0) + (Number(t.G) || 0);
                    pesoTotal += peso[k];
                }
                const resto = { P: objetivo.P - suma.P, H: objetivo.H - suma.H, G: objetivo.G - suma.G };
                return { resto, peso, pesoTotal };
            };
            // ¿CIERRA EL DÍA? Con la misma vara que la banda (`lib/estadoDelDia`): hidratos
            // y grasa dentro del margen y la proteína cubierta, que pasarse de proteína no
            // es pasarse. Y cuánto le falta para cerrar, para comparar dos vueltas.
            const cierra = (resto) => ['H', 'G'].every((x) => Math.abs(resto[x]) <= MARGEN) && resto.P <= MARGEN;
            const loQueLeFalta = (resto) => ['H', 'G'].reduce((t, x) => t + Math.max(0, Math.abs(resto[x]) - MARGEN), 0)
                + Math.max(0, resto.P - MARGEN);
            const cerrarElDia = async (comidas, medida) => {
                const { resto, peso, pesoTotal } = medida || await medirElDia(comidas);
                restoDelDia = resto;
                // Con el día ya cerrado no se toca nada: una vuelta que no hace falta solo
                // puede empeorarlo. Y CERRADO ES DENTRO DEL MARGEN, no por debajo de un
                // gramo (22-09): exigir menos de un gramo, con cantidades que se mueven de
                // cinco en cinco, hacía seguir dando vueltas a un día que ya estaba bien --
                // la Comida 3 iba y venía de 37,5 a 50 g -- y la última lo dejaba peor.
                if (!pesoTotal || cierra(resto)) return null;
                // Y LA CORRECCIÓN VA ENTERA A UNA COMIDA, NO REPARTIDA A TROZOS. Las
                // cantidades se afinan en pasos de cinco gramos: repartir 6,7 g de hidratos
                // entre cuatro comidas son 1,7 en cada una, que no llega a mover un solo
                // alimento y devuelve el día exactamente igual. Cada macro se corrige en la
                // comida que más tiene de ese macro, que es la que tiene de dónde.
                const ajuste = {};
                for (const x of ['P', 'H', 'G']) {
                    if (Math.abs(resto[x]) < 0.5) continue;
                    let elegida = null, mayor = -1;
                    // Y ENTRE LAS QUE SE TOCAN, no entre las seis (17-09): si la corrección
                    // cayera en una comida que estaba bien, volveríamos a mover justo lo que
                    // se ha decidido no mover.
                    for (const k of Object.keys(peso).filter((c) => seTocan.includes(c))) {
                        const t = Number((distribution?.comidas?.[k] || {})[x]) || 0;
                        if (t > mayor) { mayor = t; elegida = k; }
                    }
                    if (!elegida) continue;
                    // ACUMULADO, porque cada vuelta se le manda al motor el día ORIGINAL:
                    // si la corrección fuese relativa al último resultado, la segunda vuelta
                    // desharía la primera y el día se quedaba oscilando entre 159,7 y 166,7.
                    acumulado[elegida] = { ...(acumulado[elegida] || {}) };
                    acumulado[elegida][x] = (Number(acumulado[elegida][x]) || 0) + resto[x];
                    ajuste[elegida] = { ...acumulado[elegida] };
                }
                for (const k of Object.keys(acumulado)) ajuste[k] = { ...acumulado[k] };
                if (!Object.keys(ajuste).length) return null;
                console.debug('[cuadrar dia] resto', JSON.stringify(resto), 'ajuste', JSON.stringify(ajuste));
                return api('/api/calculator/refit-diet', {
                    method: 'POST',
                    body: JSON.stringify({
                        fecha: currentDate, tipo_dia: tipoDia, num_comidas: numComidas,
                        momento_entreno: momentoEntreno, opcion_peri: opcionPeri,
                        comidas: soloLasQueSeTocan(), ajuste_objetivo: ajuste,
                    }),
                });
            };
            if (res?.dia && res.dia.resto) {
                // Ya viene cerrado del servidor: lo que queda es lo que dice, y con eso se
                // cuenta después. Sin vueltas aquí.
                restoDelDia = res.dia.resto;
                console.debug('[cuadrar dia] servidor', JSON.stringify(res.dia.antes), '->', JSON.stringify(res.dia.resto), 'movimientos', (res.dia.movimientos || []).length);
                return { res, segunda, yaCuadrado, restoDelDia };
            }
            try {
                // RESPALDO (servidor sin `dia`): se repite mientras siga acercándose.
                let medida = await medirElDia(yaCuadrado);
                let mejor = { comidas: yaCuadrado, segunda: null, medida };
                for (let vuelta = 0; vuelta < 4; vuelta++) {
                    const r = await cerrarElDia(yaCuadrado, medida);
                    if (!r?.comidas) break;
                    // LAS DOS HUELLAS, SOBRE LAS MISMAS COMIDAS. `yaCuadrado` es el día
                    // entero y `r.comidas` solo las que se tocan: comparadas tal cual nunca
                    // coincidirían y la vuelta que no mueve nada no se detectaría.
                    const cantidadesDe = (comidas) => JSON.stringify(Object.fromEntries(
                        Object.entries(comidas || {}).filter(([k]) => seTocan.includes(k))
                            .map(([k, c]) => [k, (c?.alimentos || []).map(a => a.cantidad_g)])));
                    const huellaAntes = cantidadesDe(yaCuadrado);
                    const huellaDespues = cantidadesDe(r.comidas);
                    yaCuadrado = conLasDeSiempre(r.comidas);
                    // SE MIDE LO QUE HA QUEDADO, no lo que había antes de pedir (22-09). El
                    // resto de antes de la última vuelta era lo que se contaba al final, y
                    // por eso el aviso decía «Día cuadrado» con la banda en «faltan 6,1».
                    medida = await medirElDia(yaCuadrado);
                    console.debug('[cuadrar dia] vuelta', vuelta, huellaAntes === huellaDespues ? 'no movio nada' : 'movio', huellaDespues, 'queda', JSON.stringify(medida.resto));
                    if (loQueLeFalta(medida.resto) < loQueLeFalta(mejor.medida.resto)) {
                        mejor = { comidas: yaCuadrado, segunda: r, medida };
                    }
                    if (huellaAntes === huellaDespues || cierra(medida.resto)) break;
                }
                // Y SE QUEDA LA MEJOR VUELTA, NO LA ÚLTIMA. Las vueltas pueden oscilar
                // (una comida que va y viene entre dos cantidades redondas) y la última no
                // tiene por qué ser la buena.
                yaCuadrado = mejor.comidas;
                segunda = mejor.segunda;
                restoDelDia = mejor.medida.resto;
            } catch (e) {
                // Si la segunda pasada falla, se queda la primera, que ya mejora el día.
                console.error('[cerrar el día]', e);
            }
            return { res, segunda, yaCuadrado, restoDelDia };
        };

        try {
            let { res, segunda, yaCuadrado, restoDelDia } = await unaPasada();
            //: Lo que se lleva movido, para contarlo entero en el cuadro de confirmacion
            //: aunque hayan hecho falta varias vueltas.
            let vueltasDadas = 1;
            // ── SI LO DESCUADRADO NO DA MÁS DE SÍ, SE AMPLÍA Y SE DICE (17-09) ──
            //
            // Tocar solo lo que está fuera es lo que se pidió, pero hay días en los que eso
            // no basta: la comida que se pasa está con todo al mínimo y no hay de dónde
            // quitar. Ahí el botón se quedaba sin hacer nada, que es peor que tocar de más:
            // se pulsa, no pasa nada y nadie dice por qué.
            //
            // Así que se vuelve a intentar con el día entero y el cuadro lo cuenta con su
            // nombre: quien decide sigue siendo el cliente, pero ahora con el dato delante.
            // La misma vara que `cierra` de `unaPasada` (y que el servidor y `core/dia_cuadrado`):
            // la proteína solo falla si FALTA. Aquí se exigía |P| ≤ 4 y con la proteína pasada
            // 6 g el servidor daba el día por cerrado y la pantalla seguía insistiendo (23-09).
            // Escrita aquí y no llamando a aquella: vive dentro de `unaPasada` y desde aquí no
            // se ve (llamarla rompía el botón con «No hemos podido cuadrar el día»).
            const cierraElDia = (r) => !!r && Math.abs(r.H || 0) <= MARGEN && Math.abs(r.G || 0) <= MARGEN
                && (r.P || 0) <= MARGEN;
            let hizoFaltaAmpliar = false;
            // ── UNA COMIDA SIN CON QUÉ NO SE CARGA SOBRE LAS DEMÁS (Francisco, 21-09) ──
            //
            // Su jueves 3: la Comida 1 sin nada con proteína (−48,3). Cerrar el día metía
            // esos 48 g en la Comida 4, que salía con +49 de proteína, y el día «cerraba».
            // Al pulsar otra vez, lo mismo al revés: un ping-pong entre la 1 y la 4 que
            // nunca acaba. Lo que le falta a esa comida no está en el día: hay que
            // añadirlo. Así que el día NO se cierra sobre las otras; se cuadra cada comida
            // con lo suyo, y se le dice con su nombre cuál se queda y qué le falta.
            const comidaSinConQue = (k) => {
                if (vacias.includes(k)) return false;     // vacía no es «sin con qué»: es sin montar
                const sug = res?.desfases?.[k]?.sugerencia;
                return sug && sug.que_hacer === 'anadir';
            };
            // Y con una comida sin montar, el día tampoco se cierra sobre las demás.
            const hayComidaSinConQue = getMealOrder().some(comidaSinConQue) || vacias.length > 0;
            if (hayComidaSinConQue && res?.comidas) {
                yaCuadrado = conLasDeSiempre(res.comidas);
            }
            // Y TAMBIÉN SI EL DÍA CIERRA PERO UNA COMIDA QUEDA FUERA (Francisco, 21-09).
            // Con tres comidas «válidas» pero cortas (−1,9, −3,1 y −2,4 de proteína) y la
            // cuarta corta de verdad, solo se tocaba la cuarta, y ahí iba a parar TODO lo
            // que faltaba al día: el día cerraba y la cuarta salía pasada (+7,6). Es su
            // misma queja, «una sobra y otra falta», al revés. Si con las que se tocan
            // alguna queda fuera, entran todas y se reparte.
            //
            // OJO: no vale el desfase que devuelve el motor. Al cerrar el día se le manda a
            // cada comida tocada un objetivo corregido con lo que falta al día, y contra
            // ESE objetivo la ve cuadrada. La pantalla mide contra el objetivo de siempre,
            // que es el que el cliente lee en la tarjeta, así que aquí se mide igual.
            const fueraDeSuObjetivo = (comidas, k) => {
                const objetivo = (distribution?.comidas || {})[k]
                    || (distribution?.periworkout || {})[k];
                if (!objetivo) return false;
                const hay = (comidas?.[k]?.alimentos || []).reduce((t, a) => {
                    const m = a?.macros_efectivos || {};
                    return { P: t.P + (Number(m.P) || 0), H: t.H + (Number(m.H) || 0),
                             G: t.G + (Number(m.G) || 0) };
                }, { P: 0, H: 0, G: 0 });
                const cuales = (k === 'Intra' || k === 'Post') ? ['P', 'H'] : ['P', 'H', 'G'];
                return cuales.some((x) => Math.abs(hay[x] - (Number(objetivo[x]) || 0)) > MARGEN);
            };
            const algunaTocadaFuera = () => seTocan.some((k) => fueraDeSuObjetivo(yaCuadrado, k));
            // Y DÓNDE FALTA O SOBRA, CON SU NOMBRE (Francisco, 21-09: «faltan 19,9 g de
            // proteína y 10 de hidratos... no dice en dónde»). Comida por comida, contra el
            // objetivo que lee la tarjeta, y solo lo que se sale del margen.
            const dondeSeSale = (comidas) => getMealOrder().map((k) => {
                if (!((comidas?.[k]?.alimentos) || []).length) return null;
                const objetivo = (distribution?.comidas || {})[k] || (distribution?.periworkout || {})[k];
                if (!objetivo) return null;
                const hay = (comidas[k].alimentos).reduce((t, a) => {
                    const m = a?.macros_efectivos || {};
                    return { P: t.P + (Number(m.P) || 0), H: t.H + (Number(m.H) || 0), G: t.G + (Number(m.G) || 0) };
                }, { P: 0, H: 0, G: 0 });
                const cuales = (k === 'Intra' || k === 'Post') ? ['P', 'H'] : ['P', 'H', 'G'];
                const partes = cuales.map((x) => {
                    const d = hay[x] - (Number(objetivo[x]) || 0);
                    if (Math.abs(d) <= MARGEN) return null;
                    return `${d < 0 ? 'faltan' : 'sobran'} ${num1(Math.abs(d))} de ${NOMBRE_MACRO[x]}`;
                }).filter(Boolean);
                return partes.length ? `${mealInfo[k]?.name || k}: ${partes.join(' y ')}` : null;
            }).filter(Boolean);
            // UNA COMIDA SIN CON QUÉ SOLO FRENA SU PROPIO MACRO (23-09, el día de Luis: la
            // Comida 2 sin de dónde sacar proteína dejaba el día con +7,9 de grasa, que venía
            // de un poco en tres comidas que nadie tocaba). Si lo que no cierra del día es OTRO
            // macro, se amplía a todas: el remate del servidor no saca ninguna de su margen.
            const lesFalta = new Set(getMealOrder().filter(comidaSinConQue)
                .map((k) => res?.desfases?.[k]?.sugerencia?.macro).filter(Boolean));
            const fallaElDia = ['H', 'G'].filter((x) => Math.abs(restoDelDia?.[x] || 0) > MARGEN)
                .concat((restoDelDia?.P || 0) > MARGEN ? ['P'] : []);
            const bloqueaLaSinConQue = hayComidaSinConQue && fallaElDia.every((x) => lesFalta.has(x));
            if (!bloqueaLaSinConQue && (!cierraElDia(restoDelDia) || algunaTocadaFuera()) && seTocan.length < getMealOrder().length) {
                hizoFaltaAmpliar = true;
                seTocan = getMealOrder();
                ({ res, segunda, yaCuadrado, restoDelDia } = await unaPasada());
                vueltasDadas += 1;
            }

            // ── Y SE INSISTE HASTA QUE EL DIA CIERRE (Francisco, 20-09) ─────────
            //
            // «Tuve que presionar dos veces el botón de cuadrar para que cuadre, ¿por qué no
            // lo hace a la primera?» Medido con una dieta aplicada: cada pulsación acercaba
            // el día un poco y se paraba (de faltar 126,7 g de hidratos a 33,1, luego a 5,8).
            // El motivo es que una pasada mide el resto del día y aplica UNA corrección; como
            // las cantidades se mueven de cinco en cinco gramos, esa corrección casi nunca
            // deja el día cerrado al primer intento.
            //
            // Ahora la siguiente vuelta parte de lo que dejó la anterior -- que es justo lo
            // que hacía el segundo clic -- y se repite hasta que el día cierra o hasta que
            // una vuelta no mueve ni un gramo. Tres vueltas como tope: cada una se acerca,
            // así que si tres no bastan es que ese día no cierra moviendo cantidades, y eso
            // ya se cuenta abajo con su nombre.
            // OJO CON EL NOMBRE (21-09): esto se llamaba `cantidadesDelDia`, igual que la
            // huella de fuera con la que el «Deshacer» del día comprueba que lo que hay en
            // pantalla es lo que dejó el cuadre. Al taparla, la huella se guardaba con un
            // formato y se comparaba con otro: nunca iguales, y el «Deshacer» del día
            // llevaba un día sin salir («ahí falta el deshacer», Francisco, jueves 3).
            const cantidadesEnGramos = (comidas) => JSON.stringify(
                getMealOrder().map(k => (comidas?.[k]?.alimentos || []).map(a => a.cantidad_g)));
            for (let vuelta = 0; vuelta < 3 && !hayComidaSinConQue && !cierraElDia(restoDelDia); vuelta++) {
                const antes = cantidadesEnGramos(yaCuadrado);
                puntoDePartida = yaCuadrado;
                const otra = await unaPasada();
                if (!otra?.res?.comidas) break;
                vueltasDadas += 1;
                ({ res, segunda, yaCuadrado, restoDelDia } = otra);
                if (cantidadesEnGramos(yaCuadrado) === antes) break;
            }
            puntoDePartida = null;

            // ── Y QUE CUADREN TAMBIÉN LAS COMIDAS, NO SOLO EL DÍA (Francisco, 21-09) ──
            //
            // «Cualquier botón que cuadre una comida o el día debe cuadrar las comidas, no
            // solo el día. Si necesita borrar un alimento para que cuadre el día y también
            // las comidas, que avise... así no queda una que sobra y otra a la que le falta.»
            //
            // Su caso: el día cerraba con la Comida 1 a −7,8 de hidratos y la Comida 4 a
            // +8,2. Los dos números son el mismo: el exceso de una tapaba la falta de la
            // otra. Moviendo cantidades no hay salida, porque la que se pasa ya está con todo
            // al mínimo; la única forma de que cuadren las dos es quitar algo de la que se
            // pasa.
            //
            // Y el motor ya sabe QUÉ quitar: cuando una comida se pasa y no puede bajar más,
            // devuelve una sugerencia con el alimento y lo que aporta (medido en su Comida 4:
            // «quitar_o_bajar · Harina de avena · aporta 6 g de hidratos, sobran 4,1»).
            //
            // Así que se le hace caso, se vuelve a cuadrar sin ese alimento, y lo que se
            // quita se nombra en el cuadro de confirmación de abajo, donde ya se nombran los
            // gramos. Nada se aplica hasta que él diga que sí: `refit-diet` calcula y
            // devuelve, no guarda.
            const fueraPorQueSobra = [];      // [{comida, nombre, alimento_id}]
            // Se mide con el desfase que devuelve el propio motor para cada comida, no
            // recalculando macros aquí: `macros_efectivos` falta en casi todos los alimentos
            // del catálogo y sumarlos daría ceros.
            const seSale = (k) => fueraDeSuObjetivo(yaCuadrado, k);
            for (let ronda = 0; ronda < 3; ronda++) {
                const sobradas = getMealOrder().filter((k) => {
                    const sug = res?.desfases?.[k]?.sugerencia;
                    return seSale(k) && sug && sug.que_hacer === 'quitar_o_bajar' && sug.alimento_id != null;
                });
                if (!sobradas.length) break;
                let quitoAlguno = false;
                for (const k of sobradas) {
                    let sug = res.desfases[k].sugerencia;
                    // EL QUE RESUELVE, NO EL QUE MÁS PONE (23-09, el Post de Luis: crema de
                    // arroz 21 g y medio plátano 10 g, sobran 10 de hidratos con todo al
                    // mínimo). La sugerencia señala al que más aporta -- la crema -- y como
                    // quitarla dejaba la comida 11 g corta, no se quitaba nada y el Post se
                    // quedaba pasado. Si el motor trae la pregunta de qué quitar, se coge de ahí
                    // el alimento cuyo aporte se parece más a lo que sobra: el plátano.
                    const dec = res.decisiones?.[k];
                    if (dec?.tipo === 'quitar' && (dec.opciones || []).length) {
                        const sobraDec = Number(dec.sobra) || 0;
                        const mejor = [...dec.opciones]
                            .filter((o) => (Number(o.aporta_en_el_minimo) || 0) >= sobraDec - MARGEN)
                            .sort((a, b) => (a.aporta_en_el_minimo - sobraDec) - (b.aporta_en_el_minimo - sobraDec))[0];
                        if (mejor) {
                            sug = { ...sug, alimento: mejor.nombre, alimento_id: mejor.alimento_id,
                                    aporta: mejor.aporta_en_el_minimo, sobra: sobraDec };
                        }
                    }
                    const lista = yaCuadrado?.[k]?.alimentos || [];
                    // Solo se quita si con lo que aporta se resuelve lo que sobra: quitar un
                    // alimento que aporta 30 para arreglar un exceso de 4 deja la comida peor
                    // de lo que estaba, y eso no es cuadrar, es vaciar.
                    const aporta = Number(sug.aporta) || 0;
                    const sobra = Number(sug.sobra) || 0;
                    if (!aporta || aporta - sobra > MARGEN) continue;
                    const sinEl = lista.filter((a) => a.alimento_id !== sug.alimento_id);
                    if (sinEl.length === lista.length || !sinEl.length) continue;
                    fueraPorQueSobra.push({ comida: k, nombre: sug.alimento,
                                            alimento_id: sug.alimento_id });
                    yaCuadrado = { ...yaCuadrado, [k]: { ...yaCuadrado[k], alimentos: sinEl } };
                    quitoAlguno = true;
                }
                if (!quitoAlguno) break;
                // Con el alimento fuera, el día vuelve a repartirse entero: quitar 6 g de
                // hidratos de una comida deja hueco que las demás pueden aprovechar.
                const antesDeLaRonda = { res, segunda, yaCuadrado, restoDelDia,
                                         quitados: fueraPorQueSobra.length };
                seTocan = getMealOrder();
                puntoDePartida = yaCuadrado;
                const otra = await unaPasada();
                puntoDePartida = null;
                if (!otra?.res?.comidas) break;
                ({ res, segunda, yaCuadrado, restoDelDia } = otra);
                vueltasDadas += 1;
                // Y SOLO SI CON ESO CUADRAN DE VERDAD (21-09): quitar un alimento por el
                // macro que sobra puede hundir otro macro de esa misma comida. Si alguna de
                // las tocadas sigue fuera, la ronda entera se deshace y se deja como estaba,
                // que lo demás ya lo cuenta el cuadro con su nombre.
                const tocadas = fueraPorQueSobra.slice(antesDeLaRonda.quitados).map((b) => b.comida);
                if (tocadas.some(seSale)) {
                    ({ res, segunda, yaCuadrado, restoDelDia } = antesDeLaRonda);
                    fueraPorQueSobra.length = antesDeLaRonda.quitados;
                    break;
                }
            }

            // ── «Y SI HUBIERA QUE QUITAR ALGUNO, TE AVISAMOS ANTES» ──────────────
            //
            // Esa frase está escrita en la tarjeta desde el 9-09, y hasta ahora no era
            // verdad: el alimento se quitaba y el aviso salía DESPUÉS y en pasado -- «un
            // alimento no cabía ni al mínimo y se quitó» --, o sea que se le contaba lo que
            // ya le habían hecho. Prometer una cosa y hacer la contraria en el mismo botón
            // es peor que no prometer nada.
            //
            // Aquí todavía no se ha tocado nada: `refit-diet` calcula y devuelve, no guarda.
            // Así que si de esta pasada sale algún alimento fuera, se pregunta con su
            // nombre delante y no se aplica hasta que diga que sí.
            // ── Y QUÉ SE VA A TOCAR, ANTES DE TOCARLO (Francisco, 16-09) ────────
            //
            // «Que al apretar salga un cuadro de confirmación avisando qué comidas y qué
            // alimentos y cantidades va a tocar.» Hasta hoy el botón cambiaba los gramos de
            // hasta seis comidas de golpe y el cliente se enteraba después, mirando a ver
            // qué había cambiado. El «Deshacer» de cada tarjeta lo arregla a posteriori;
            // esto lo evita antes.
            //
            // Aquí no se ha guardado nada todavía -- `refit-diet` calcula y devuelve --, así
            // que la lista es exactamente lo que se va a aplicar, no una estimación.
            //
            // Solo lo que de verdad se mueve: un alimento que se queda en sus gramos no se
            // nombra, que si no la lista es el día entero y no se lee.
            const calcularCambios = (resultado) => {
            const cambios = [];
            for (const clave of getMealOrder()) {
                const antes = (antesDelDia?.[clave]?.alimentos) || [];
                const despues = (resultado?.[clave]?.alimentos) || [];
                const filas = [];
                // SE EMPAREJA POR ALIMENTO, NO POR POSICIÓN. Desde que el cuadre puede
                // retirar alguno, las listas de antes y después ya no van en paralelo: con
                // índices, quitar el segundo alimento hacía que los de abajo parecieran
                // cambiados de gramos sin haberse movido.
                const pendientes = despues.map((a) => ({ ...a }));
                const deQuien = (a) => (a?.alimento_id != null ? `id:${a.alimento_id}` : `n:${a?.nombre || ''}`);
                for (const previo of antes) {
                    const i = pendientes.findIndex((a) => a && deQuien(a) === deQuien(previo));
                    const de = Math.round(Number(previo?.cantidad_g) || 0);
                    if (i < 0) {
                        // Ya no está: o lo retira este cuadre, o lo descarta el motor.
                        filas.push({ que: previo?.nombre || 'Alimento', cambio: 'se quita' });
                        continue;
                    }
                    const a_ = Math.round(Number(pendientes[i]?.cantidad_g) || 0);
                    if (de !== a_) {
                        filas.push({ que: pendientes[i]?.nombre || previo?.nombre || 'Alimento',
                                     cambio: `${de} → ${a_} g` });
                    }
                    pendientes[i] = null;
                }
                if (filas.length) {
                    cambios.push({ titulo: mealInfo[clave]?.name || clave }, ...filas);
                }
            }
            return cambios;
            };
            let cambios = calcularCambios(yaCuadrado);

            // ── CUANDO CERRAR EL DÍA DESHACE LO QUE CUADRÓ EN LAS COMIDAS (Francisco, 21-09) ──
            //
            // Su jueves 3: la Comida 1 sin nada con proteína (−48,3) y la Comida 4 con 95 g
            // de whey (+49,1). El día cerraba. El motor lo hacía bien -- bajaba el whey a 35
            // y la 4 quedaba cuadrada --, pero al cerrar el día la falta de la 1, que no
            // tiene con qué, se volvía a cargar sobre la 4 y el whey volvía a 95: el
            // resultado era IDÉNTICO a lo que había. Sin cambios no salía cuadro, ni
            // deshacer, ni una palabra. «Tuve que pulsar dos veces y no funciona.»
            //
            // Una comida sin nada que ponga el macro que le falta no se arregla moviendo
            // gramos: se arregla añadiendo. Así que cuando cerrar el día no ha movido nada,
            // se aplica el cuadre de las comidas tal cual y se le dice con su nombre cuál
            // se queda y qué le falta; el día no cierra, y eso también se dice.
            const sinConQue = getMealOrder().filter((k) => comidaSinConQue(k) && fueraDeSuObjetivo(yaCuadrado, k));
            const elDiaNoCierra = hayComidaSinConQue;
            const loQueNoTieneConQue = sinConQue.map((k) => {
                const sug = res.desfases[k].sugerencia;
                const macro = NOMBRE_MACRO[sug.macro] || sug.macro;
                // «NO LLEVA NADA QUE PONGA» SOLO SI ES VERDAD (22-09): la Comida 2 llevaba
                // pan y tortitas y se le decía que no llevaba nada con hidratos. Lo que pasa
                // es que lo que lleva no llega ni subiéndolo a su tope; se dice eso.
                const llevaAlgo = (yaCuadrado?.[k]?.alimentos || []).some((a) => (Number(a?.macros_efectivos?.[sug.macro]) || 0) > 0);
                return llevaAlgo
                    ? `A la ${mealInfo[k]?.name || k} le faltan ${num1(sug.falta)} g de ${macro} y lo que lleva no llega ni subiéndolo al tope: para cuadrarla, añade algo más con ${macro}`
                    : `${mealInfo[k]?.name || k} no lleva nada que ponga ${macro} y le faltan ${num1(sug.falta)} g: para cuadrarla, añade algo con ${macro}`;
            });
            if (vacias.length) {
                const nombres = vacias.map((k) => mealInfo[k]?.name || k);
                loQueNoTieneConQue.push(nombres.length === 1
                    ? `${nombres[0]} está sin crear: el día no cierra hasta que la crees`
                    : `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]} están sin crear: el día no cierra hasta que las crees`);
            }
            // Y SI NO HAY NADA QUE MOVER, SE DICE, en vez de quedarse mudo.
            if (!cambios.length && !((segunda || res).excluidos || []).length && !fueraPorQueSobra.length) {
                toast.info(loQueNoTieneConQue.length
                    ? `No hay nada que mover. ${loQueNoTieneConQue.join('. ')}.`
                    : 'No hay nada que mover: las cantidades ya están donde tienen que estar.',
                    { duration: 9000 });
                return;
            }
            const cuantasComidas = cambios.filter(c => c.titulo).length;

            const seIrian = [...new Set(((segunda || res).excluidos || [])
                .map(e => e?.nombre || e?.alimento || '')
                .filter(Boolean))];
            // UN SOLO DIÁLOGO, NO DOS SEGUIDOS. Si además hay que quitar algún alimento, ese
            // aviso manda -- es lo que el cliente pierde -- y la lista de cantidades va
            // debajo, en el mismo cuadro.
            // Lo que se retira porque la comida se pasaba tiene su propio aviso: ahí la
            // pérdida es el titular, no los gramos.
            const bajas = [...new Set(fueraPorQueSobra.map((b) => b.nombre).filter(Boolean))];
            if (!seIrian.length && !bajas.length && cambios.length) {
                const adelante = await confirm({
                    title: cuantasComidas === 1
                        ? 'Vamos a ajustar una comida'
                        : `Vamos a ajustar ${cuantasComidas} comidas`,
                    // Y SI HUBO QUE METER COMIDAS QUE ESTABAN BIEN, SE DICE POR QUÉ. Sin
                    // esta línea, ver una comida válida en la lista se lee como un error de
                    // la app, que es justo lo que pasaba antes del 17-09.
                    description: (loQueNoTieneConQue.length
                        ? `${loQueNoTieneConQue.join('. ')}. ${elDiaNoCierra && sinConQue.length ? 'El día se queda sin cerrar hasta entonces. ' : ''}`
                        : '') + (!hizoFaltaAmpliar
                        ? 'Estos son los gramos que cambian. No se quita ningún alimento.'
                        // Y EL CASO QUE MÁS DESCONCIERTA: la comida que se pasa ni aparece
                        // en la lista. Pasa cuando sus alimentos ya están al mínimo y no hay
                        // de dónde bajar, así que el día se cierra moviendo las otras y ésa
                        // se queda como está. Decirlo con su nombre es la diferencia entre
                        // «la app toca lo que no debe» y «esta comida no se puede cuadrar
                        // sola». Arreglarla es cosa de quitar o cambiar un alimento, y eso
                        // no lo hace el botón por su cuenta.
                        : (() => {
                            const tocadas = new Set(cambios.filter(c => c.titulo).map(c => c.titulo));
                            const quietas = descuadradas.filter(k => !tocadas.has(mealInfo[k]?.name || k));
                            if (quietas.length === descuadradas.length && descuadradas.length) {
                                const nombres = quietas.map(k => mealInfo[k]?.name || k);
                                return `${nombres.join(' y ')} no se ${nombres.length === 1 ? 'puede' : 'pueden'} cuadrar bajando cantidades, así que se ${nombres.length === 1 ? 'queda' : 'quedan'} igual: para que el día cierre se mueven las otras. Estos son los gramos que cambian y no se quita ningún alimento.`;
                            }
                            return `Con ${descuadradas.length === 1 ? 'la comida que se descuadra' : 'las comidas que se descuadran'} no llega: para que cuadren el día y todas las comidas hay que mover también alguna de las que ya están bien. Estos son los gramos que cambian y no se quita ningún alimento.`;
                        })()),
                    lineas: cambios,
                    confirmLabel: 'Cuadrar el día',
                });
                if (!adelante) return;
            }
            // ── «QUE AVISE QUE VA A ELIMINAR ALGUNA PARA QUE CUADRE PERFECTO» ───
            //
            // Palabras suyas del 21-09. Se le dice qué se va a quitar, de qué comida y por
            // qué, y sólo se aplica si dice que sí. Debajo van los gramos, que cambian
            // igualmente en las demás comidas al repartirse lo que deja el alimento.
            if (bajas.length && !seIrian.length) {
                const dónde = fueraPorQueSobra
                    .map((b) => `${b.nombre} (${mealInfo[b.comida]?.name || b.comida})`);
                const adelante = await confirm({
                    title: bajas.length === 1
                        ? `Para que cuadren todas hay que quitar ${bajas[0]}`
                        : `Para que cuadren todas hay que quitar ${bajas.length} alimentos`,
                    description: bajas.length === 1
                        ? `${dónde[0]} se pasa y sus alimentos ya están al mínimo, así que bajando gramos no cuadra. Quitándolo cuadran el día y todas las comidas.`
                        : `Estas comidas se pasan con sus alimentos ya al mínimo, así que bajando gramos no cuadran: ${dónde.join(', ')}. Quitándolos cuadran el día y todas las comidas.`,
                    lineas: cambios,
                    confirmLabel: 'Quitar y cuadrar',
                    danger: true,
                });
                if (!adelante) return;
            }
            if (seIrian.length) {
                const lista = seIrian.length <= 3
                    ? seIrian.join(', ')
                    : `${seIrian.slice(0, 3).join(', ')} y ${seIrian.length - 3} más`;
                // Y CON EL MOTIVO DE VERDAD. Aquí un alimento solo se cae por una razón:
                // que ya no esté en nuestro listado (o que la línea no diga a qué alimento
                // se refiere, que para el cliente es lo mismo). Lo de «no cabía ni al
                // mínimo» que se decía antes no pasa en este botón: es de otro motor, y
                // dárselo como explicación le manda a bajar gramos que no arreglan nada.
                const adelante = await confirm({
                    title: seIrian.length === 1
                        ? `Para cuadrar el día hay que quitar ${seIrian[0]}`
                        : `Para cuadrar el día hay que quitar ${seIrian.length} alimentos`,
                    description: seIrian.length === 1
                        ? 'Ya no está en nuestro listado, así que no podemos calcular sus gramos. El resto del día se queda: solo cambian las cantidades.'
                        : `Ya no están en nuestro listado, así que no podemos calcular sus gramos: ${lista}. El resto del día se queda: solo cambian las cantidades.`,
                    lineas: cambios,
                    confirmLabel: 'Quitar y cuadrar',
                    danger: true,
                });
                // Se queda exactamente como estaba, y sin más mensajes.
                if (!adelante) return;
            }

            // EL RECUADRE AUTOMÁTICO POR CAMBIO DE TRAMO NO PASA POR ENCIMA DE ESTO (22-09).
            // Al cerrar el día pueden cruzarse los 20/40 g de frutos secos o los 50/100 de
            // cereales, y `anotarCalibracion` recuadraba entonces cada comida POR SU CUENTA,
            // deshaciendo el reparto que acaba de cerrar el día: medido con la cuenta de
            // Rocío, «Hemos recuadrado 2 comidas...» y el día en «faltan 6,9 g de hidratos»
            // con el aviso de «Día cuadrado» encima. Aquí ya se ha medido y repartido con
            // el tramo nuevo puesto (`medirElDia` calibra), así que ese recuadre no aporta
            // nada: se da por hecho para las dos familias y este día.
            for (const bloque of Object.keys(FAMILIAS_CALIBRADAS)) {
                recuadresHechos.current.add(`${bloque}:${currentDate}`);
            }
            // Y LAS ELECCIONES PENDIENTES SE LIMPIAN en lo que se acaba de cuadrar (22-09):
            // la marca «de dónde bajo» venía de aplicar la favorita y se quedaba puesta
            // encima de una comida ya dentro de margen. Lo que el cuadre tocó ya está decidido.
            for (const k of Object.keys(yaCuadrado || {})) {
                if (yaCuadrado[k]?.eleccion_pendiente && seTocan.includes(k)) {
                    yaCuadrado = { ...yaCuadrado, [k]: { ...yaCuadrado[k], eleccion_pendiente: null } };
                }
            }
            {
                const suma = { P: 0, H: 0, G: 0 };
                for (const [k, c] of Object.entries(yaCuadrado || {})) for (const a of (c?.alimentos || [])) {
                    const m = a?.macros_efectivos || {};
                    suma.P += Number(m.P) || 0; suma.H += Number(m.H) || 0;
                    if (!['Intra', 'Post'].includes(k)) suma.G += Number(m.G) || 0;
                }
                console.debug('[cuadrar dia] aplico', JSON.stringify(suma), 'objetivo', JSON.stringify({ P: dayTarget.P_total, H: dayTarget.H_total, G: dayTarget.G_total }), 'resto', JSON.stringify(restoDelDia), 'cantidades', JSON.stringify(Object.fromEntries(Object.entries(yaCuadrado || {}).map(([k, c]) => [k, (c?.alimentos || []).map((a) => a.cantidad_g)]))));
            }
            setMealsData(yaCuadrado);
            // UN GESTO, UNA VUELTA ATRÁS (Francisco, 16-09). Aquí se dejaba un «Deshacer» en
            // cada tarjeta movida, y para volver atrás de un cuadre de cuatro comidas había
            // que entrar en las cuatro. Ahora el día se deshace entero de una vez, arriba,
            // donde se pulsó el botón. Ver `deshacerElDia`.
            //
            // Y SE LIMPIAN LOS DE LAS COMIDAS TOCADAS: si quedara el de antes, la tarjeta
            // ofrecería volver a un estado anterior al de este cuadre y los dos enlaces
            // devolverían cosas distintas.
            const movidas = Object.keys(yaCuadrado).filter((k) =>
                huellaConCantidades(antesDelDia[k]) !== huellaConCantidades(yaCuadrado[k]));
            setDeshacerComida((prev) => {
                const siguiente = { ...prev };
                for (const k of movidas) delete siguiente[k];
                return siguiente;
            });
            setDeshacerElDia(movidas.length
                ? { antes: antesDelDia, huella: cantidadesDelDia(yaCuadrado), cuantas: movidas.length }
                : null);
            const fuera = (res.excluidos || []).length;
            // Y AQUÍ TAMBIÉN EL MOTIVO DE VERDAD. Decía «no cabía ni al mínimo», que en
            // este botón no pasa nunca: se cae el que ya no está en el listado. Ahora
            // además el cliente ya lo ha dicho que sí arriba, así que esto solo lo
            // recuerda.
            const quitados = fuera
                ? ` ${fuera === 1 ? 'Quitamos el alimento que ya no está en el listado' : `Quitamos los ${fuera} alimentos que ya no están en el listado`}.`
                : '';

            // Y SE MIRA CÓMO HA QUEDADO ANTES DE CANTAR VICTORIA (1-09-2026). Esto decía
            // «Día cuadrado a tus macros» en cuanto la petición no fallaba, y el cliente se
            // quedaba con el aviso verde encima y los números de al lado diciendo «faltan
            // 21 de proteína». Un mensaje que dice lo contrario de lo que se ve es peor que
            // no decir nada: enseña a no leer los mensajes.
            //
            // CON LA MISMA VARA QUE LA BANDA DE ARRIBA (Francisco, 11-09).
            //
            // Esto sumaba los desfases POR COMIDA que devuelve el motor, y la banda mide el
            // TOTAL DEL DÍA con los números recalibrados de la pantalla. Dos cuentas
            // distintas de lo mismo, y se contradecían delante del cliente: el aviso decía
            // «el día no cuadra del todo: sobran 5,2 g de hidratos» y justo encima la banda
            // decía «✓ Día completo · No te falta nada» con 297,3 · 159,6 · 49,9, los tres
            // dentro de margen.
            //
            // Pasa porque una comida puede pasarse por su cuenta y las demás compensarlo: el
            // día cierra aunque la C4 sobre. Lo que se le cuenta es el DÍA, que es de lo que
            // va el botón; lo de la comida que no puede bajar más se sigue diciendo aparte,
            // porque eso sí es suyo y sí puede hacer algo.
            //
            // `restoDelDia` viene como «objetivo menos servido» y aquí se cuenta al revés,
            // que es como lo dice `desajusteEnPalabras`: positivo es que sobra.
            const total = restoDelDia
                ? { P: -restoDelDia.P, H: -restoDelDia.H, G: -restoDelDia.G }
                : (() => {
                    const t = { P: 0, H: 0, G: 0 };
                    for (const d of Object.values(res.desfases || {})) {
                        for (const m of ['P', 'H', 'G']) t[m] += Number(d?.[m] || 0);
                    }
                    return t;
                })();
            // Y PASARSE DE PROTEÍNA NO ES PASARSE (Jesús, 13-08; Francisco, 16-09).
            //
            // Era la mitad que faltaba de la corrección del 11-09. Aquella igualó de dónde
            // salen los números -- el total del día, como la banda -- pero no la REGLA con
            // la que se juzgan: el juez de la casa (`lib/estadoDelDia`, `core/dia_cuadrado`)
            // da la proteína por hecha en cuanto está CUBIERTA, y esta frase la medía por
            // los dos lados. Resultado: un día con la proteína 10 g por encima y los otros
            // dos dentro salía con la banda diciendo «✓ Día listo · No te falta nada» y,
            // encima, un aviso amarillo diciendo «El día no cuadra del todo: sobran 10 g de
            // proteína». El mismo día, dos veredictos, que es lo que el documento del 10-09
            // prohíbe y lo que enseña al cliente a no leer los avisos.
            //
            // Se mide con la misma vara: lo que sobra de proteína no cuenta como desajuste.
            // Lo que falta sí, que eso sí es un día sin terminar.
            const totalComoLoJuzgaLaCasa = { ...total, P: Math.min(0, total.P) };
            const desajuste = desajusteEnPalabras(totalComoLoJuzgaLaCasa);

            // Y CUANDO NO HAY NADA QUE HACER, SE DICE CUÁL ES LA COMIDA (ficha 44 del parte
            // de Gonzalo, 4-09). Con una comida que se pasa aunque se baje todo al mínimo
            // del catálogo, este botón no cambia nada -- correcto, no hay de dónde bajar --
            // y el cliente se quedaba con «el día no cuadra» sin saber dónde mirar, después
            // de que el cartel de arriba le hubiera prometido un reajuste. El servidor
            // manda `en_el_minimo` por comida (qué macro sigue sobrando y cuánto con todos
            // sus alimentos al mínimo): con eso se le dice la verdad y qué hacer, que es
            // quitar algo de ahí. Si son varias, la que más se pasa.
            let sinSalida = null;
            for (const [comida, d] of Object.entries(res.desfases || {})) {
                const m = d?.en_el_minimo;
                if (m && (!sinSalida || m.sobra > sinSalida.sobra)) {
                    sinSalida = { ...m, comida: mealInfo[comida]?.name || comida };
                }
            }
            const noHayDeDonde = sinSalida
                ? ` No podemos acercarlo más: ${sinSalida.comida} sobra ${num1(sinSalida.sobra)} g de ${NOMBRE_MACRO[sinSalida.macro]} aunque lo bajemos todo al mínimo. Quita algo de ahí.`
                : '';

            // La misma palabra que el botón: si el botón dice «Cuadrar el día», lo que sale
            // después no puede decir «reajustado» (parte 6).
            if (desajuste) {
                const porComida = dondeSeSale(yaCuadrado);
                // DE DÓNDE VIENE LO DEL DÍA, MACRO A MACRO (Francisco, 23-09: «habla de grasas y
                // luego de que faltan hidratos y proteínas»). El «Dónde» nombraba las comidas
                // fuera de su margen, que eran de OTROS macros; la grasa de más del día venía de
                // un poco en cada comida, todas dentro de margen, y no salía por ningún lado.
                // Ahora cada macro del día dice sus comidas, aunque estén dentro de margen.
                const deCadaMacro = ['P', 'H', 'G'].map((x) => {
                    const diaX = totalComoLoJuzgaLaCasa[x] || 0;
                    if (Math.abs(diaX) <= MARGEN) return null;
                    const trozos = getMealOrder().map((k) => {
                        if (x === 'G' && (k === 'Intra' || k === 'Post')) return null;
                        const obj = (distribution?.comidas || {})[k] || (distribution?.periworkout || {})[k];
                        const lista = yaCuadrado?.[k]?.alimentos || [];
                        if (!obj || !lista.length) return null;
                        const hay = lista.reduce((t, a) => t + (Number(a?.macros_efectivos?.[x]) || 0), 0);
                        const d = hay - (Number(obj[x]) || 0);
                        return Math.sign(d) === Math.sign(diaX) && Math.abs(d) >= 0.5
                            ? `${mealInfo[k]?.name || k} ${d > 0 ? '+' : '−'}${num1(Math.abs(d))}` : null;
                    }).filter(Boolean);
                    return trozos.length
                        ? `${diaX > 0 ? 'La' : 'Lo que falta de'} ${NOMBRE_MACRO[x]}${diaX > 0 ? ' de más' : ''} viene de ${trozos.join(', ')}`
                        : null;
                }).filter(Boolean);
                const sinMontar = vacias.length
                    ? ` ${vacias.map((k) => mealInfo[k]?.name || k).join(' y ')} ${vacias.length === 1 ? 'está' : 'están'} sin crear.`
                    : '';
                const donde = (deCadaMacro.length ? ` ${deCadaMacro.join('. ')}.` : '')
                    + (porComida.length ? ` Además, fuera de su margen: ${porComida.join('; ')}.` : '');
                toast.warning(
                    `El día no cuadra del todo: ${desajuste}.${donde}${sinMontar}${quitados}${noHayDeDonde || ' No se ha quitado nada más.'}`,
                    { duration: 12000 });
            } else {
                // EL DÍA CIERRA, PERO SI UNA COMIDA NO PUEDE BAJAR MÁS, SE DICE IGUAL. Es el
                // caso que destapó la contradicción: el día queda en 297,3 · 159,6 · 49,9
                // -- los tres dentro -- y aun así la Comida 4 sobra 8,2 g de hidratos aunque
                // se baje todo al mínimo, porque las demás lo compensan. El día está bien y
                // esa comida no, y las dos cosas se le cuentan sin que suenen a lo mismo.
                toast.success(
                    `Día cuadrado a tus macros.${quitados}` + (sinSalida
                        ? ` Ojo a una cosa: ${sinSalida.comida} sobra ${num1(sinSalida.sobra)} g de ${NOMBRE_MACRO[sinSalida.macro]} aunque se baje todo al mínimo. El día cuadra porque las otras lo compensan.`
                        : ''),
                    { duration: sinSalida ? 12000 : 8000 });
            }
        } catch (err) {
            console.error('[cuadrar dia]', err);
            toast.error('No hemos podido cuadrar el día. Inténtalo de nuevo.');
        } finally {
            setRecuadrando(false);
        }
    };
    const dayKcal = dayMacros.P * 4 + dayMacros.H * 4 + comidasG * 9;  // peri grasas excluded (match G_total)
    const targetKcal = dayTarget.kcal_total || 0;
    
    // Periworkout totals from distribution
    const periTarget = distribution?.periworkout || {};
    const intraTarget = periTarget.Intra || { P: 0, H: 0 };
    const postTarget = periTarget.Post || { P: 0, H: 0 };
    const totalPeriP = intraTarget.P + postTarget.P;
    const totalPeriH = intraTarget.H + postTarget.H;
    const servedPeriP = (calculateMealMacros('Intra').P || 0) + (calculateMealMacros('Post').P || 0);
    const servedPeriH = (calculateMealMacros('Intra').H || 0) + (calculateMealMacros('Post').H || 0);


    // Day status calculation
    const getDayStatus = () => {
        // EL DÍA SE JUZGA CON EL MISMO MARGEN QUE LAS COMIDAS: ±4 g (decisión de Francisco,
        // 16-08). Aquí había un 0 a pelo, así que un día con 234,8 de 235 de proteína se
        // guardaba como NO cuadrado mientras la cabecera, las tarjetas de comida, el cierre
        // del día y el reporte del mes lo daban por bueno. De ahí salían dos cuentas
        // distintas de «cuadraste N días» sobre los mismos datos: la del calendario de
        // Nutrición, que sale de este `is_cuadrado` guardado, y la del reporte.
        const margin = MARGEN;
        const pDiff = dayMacros.P - (dayTarget.P_total || 0);
        const hDiff = dayMacros.H - (dayTarget.H_total || 0);
        const gDiff = comidasG - (dayTarget.G_total || 0);  // peri grasas excluded from comidas G

        // LOS TRES MACROS, CON EL MISMO CRITERIO QUE CADA COMIDA (`lib/exceso`). Hasta el
        // 5-09 aquí solo entraban hidratos y grasa (la excepción de Jesús del 13-08), y un
        // día pasado SOLO de proteína -- 280 g sobre 235 y todo lo demás a cero -- no sacaba
        // el aviso «Este día no cuadra con tus macros de ahora» (ficha 34 del parte de
        // Gonzalo). La regla del 3-09 ya pintaba ese exceso en naranja en la cabecera; el
        // aviso tenía que decir lo mismo.
        if (excesos({ P: dayMacros.P, H: dayMacros.H, G: comidasG },
                    { P: dayTarget.P_total || 0, H: dayTarget.H_total || 0, G: dayTarget.G_total || 0 },
                    { margen: margin }).length) return 'sobra';

        const pOk = Math.abs(pDiff) <= margin;
        const hOk = Math.abs(hDiff) <= margin;
        const gOk = Math.abs(gDiff) <= margin;

        // Y NINGUNA COMIDA GENUINAMENTE DESCUADRADA (doc 57, F3): una comida corta de
        // hidratos y otra pasada se compensan en el total, y el día decía CUADRADO con un
        // «faltan» y un «sobran» a la vista. El criterio por comida es el de siempre
        // (getMealStatus).
        const algunaComidaMal = getMealOrder().some(k =>
            (mealsData[k]?.alimentos || []).length > 0 && getMealStatus(k) !== 'cuadrada');

        if (pOk && hOk && gOk && !algunaComidaMal) return 'cuadrado';
        return 'falta';
    };

    // QUÉ LE PASA AL DÍA, PARA PODER CONTARLO BIEN.
    //
    // `getDayStatus` responde una palabra y no se toca: de ella salen el `is_cuadrado` que
    // se guarda, el verde del calendario y la cuenta de días cuadrados del reporte. Esto es
    // aparte y solo sirve para las palabras del aviso: si además de pasarse falta algo, el
    // titular no puede decir «se pasa» a secas. Mismo listón y mismas palabras que los dos
    // «Cuadrar» (`desajusteEnPalabras`), para que la pantalla no se contradiga consigo misma.
    const desajusteDelDia = (() => {
        const desfase = {
            P: dayMacros.P - (dayTarget.P_total || 0),
            H: dayMacros.H - (dayTarget.H_total || 0),
            G: comidasG - (dayTarget.G_total || 0),   // la grasa del peri no cuenta, como arriba
        };
        return {
            falta: ['P', 'H', 'G'].some(m => desfase[m] < -MARGEN_QUE_SE_DICE),
            // REDONDO, que este es el cartel DE ARRIBA (ficha 34). Ver `lib/exceso`.
            frase: desajusteEnPalabrasRedondo(desfase),
        };
    })();

    // Cuando el día queda cuadrado y se está quieto 4 segundos, sus comidas se comparten
    // solas (`compartirDiaCuadrado`, Jesús 27-09).
    const diaCuadradoAhora = getDayStatus() === 'cuadrado';
    const huellaParaCompartir = getMealOrder()
        .map(k => (mealsData[k]?.alimentos || []).map(f => `${f.alimento_id}:${f.cantidad_g}`).join(','))
        .join('|');
    useEffect(() => {
        if (!diaCuadradoAhora) return undefined;
        const t = setTimeout(compartirDiaCuadrado, 4000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [diaCuadradoAhora, huellaParaCompartir, currentDate]);

    // ── EL ESTADO DEL DÍA, EN UN SOLO SITIO (Jesús, «Cuando la dieta está lista», 9-09) ──
    //
    // Su encargo: «hoy entras en Nutrición y tienes tres números; para saber si el día está
    // terminado hay que leerlos y sumarlos de cabeza». Tres estados y ninguno más:
    // SIN CREAR · SIN TERMINAR · LISTO.
    //
    // Y su regla de fondo, que es la que da sentido a todo esto: «la comida se da por válida
    // con margen; el día no: el día tiene que cerrar. Cuatro comidas un poco cortas hacen un
    // día corto de casi siete gramos de proteína, y ahora mismo las cuatro salen en verde».
    //
    // SE CALCULA AQUÍ Y SE PASA HECHO, que es lo otro que pide: «el mismo estado en los
    // cuatro sitios... se calcula una vez y se escribe igual en todas partes. Hoy Inicio se
    // inventa el suyo».
    //
    // El caso «se pasa» NO entra en la banda a propósito: tiene su propio aviso, y él mismo
    // dice que no se mezclen nunca porque son problemas distintos y se arreglan distinto
    // («El día que no cuadra», mismo día).
    //: ¿Hay algo montado hoy? Lo usan el juez del día y el bloque de extras, que no tiene
    //: sentido en un día sin crear.
    const hayComidaCreada = Object.values(mealsData || {})
        .some(c => (c?.alimentos || []).length > 0);

    const estadoDelDia = (() => {
        const hayAlgo = hayComidaCreada;
        // LA BANDA JUZGA EL DÍA, NO COMIDA POR COMIDA (Jesús, 10-09).
        //
        // `getDayStatus` pide además que TODAS las comidas estén cuadradas al gramo, y con
        // eso «Día completo» no salía casi nunca: en su propia maqueta el día completo lleva
        // los hidratos en «válido +0,9». Su regla es la del total: «el día tiene que cerrar».
        // Aquí se mira eso y nada más. `getDayStatus` no se toca, que es lo que escribe
        // `is_cuadrado` y de ahí comen Mi semana, el calendario y los informes.
        //
        // Y LA CUENTA ES LA DE `lib/estadoDelDia`, la misma que lee la línea de Inicio: «se
        // calcula una vez y se escribe igual en todas partes».
        const servido = { P: dayMacros.P, H: dayMacros.H, G: comidasG };
        const objetivo = { P: dayTarget.P_total || 0, H: dayTarget.H_total || 0, G: dayTarget.G_total || 0 };
        const estado = juzgarElDia(servido, objetivo, { hayAlgo });
        // El que se pasa tiene arriba su propia tarjeta naranja, así que la banda calla.
        // Y `null` del juez es «no hay con qué medirlo»: también calla.
        return (estado === 'se_pasa' || estado === null) ? null : estado;
    })();

    // CON EL DÍA LISTO, LAS COMIDAS NO PIDEN CUADRARSE (José Luis Huertas, 01-10; Francisco).
    // Cada comida se juzga contra su propio objetivo, y una que se desvía salía en «faltan» o
    // «sobran» con su botón de Cuadrar mientras la cabecera decía «✓ Día listo»: el cliente
    // leía que no había terminado. El día manda, así que con el total cuadrado esas comidas se
    // enseñan neutras. SOLO es lo que se ve: cuadrar, volcar y `getDayStatus` siguen mirando
    // `getMealStatus`, el estado de verdad.
    const getMealStatusParaVer = (mealKey) => {
        const s = getMealStatus(mealKey);
        return estadoDelDia === 'listo' && (s === 'falta' || s === 'sobra') ? 'neutra' : s;
    };

    // Lo que le falta al día, dicho corto y solo lo que falta: «6,6 g de proteína y 4,5 de
    // grasa». La frase larga de `desajusteDelDia` dice también lo que sobra, que aquí no
    // viene a cuento.
    const loQueFaltaAlDia = loQueFaltaDelDia(
        { P: dayMacros.P, H: dayMacros.H, G: comidasG },
        { P: dayTarget.P_total || 0, H: dayTarget.H_total || 0, G: dayTarget.G_total || 0 },
    );

    // Latest savable snapshot for auto-save (read in the [currentDate] cleanup). Mirrors the
    // manual saveDiet payload. Updated every render so the cleanup sees the data of the date
    // being left (state hasn't reloaded the new date yet when the cleanup fires).
    autoSaveRef.current = {
        tipo_dia: tipoDia,
        num_comidas: numComidas,
        momento_entreno: momentoEntreno,
        opcion_peri: opcionPeri,
        comidas: comidasDelReparto(),
        macros_snapshot: distribution?.resumen,
        distribution_targets: distribTargetsOverlay || null,
        is_cuadrado: getDayStatus() === 'cuadrado',
        comida_volcada: volcadoMeal,
    };

    // Calma macrosParaVolcar(e): the volcar action is offered on meal `e` ONLY when `e` is the
    // SINGLE regular meal still not cuadrada and no volcado is active (comidasNoValidas.length
    // == 1 && comidasNoValidas[0] == e && !comidaConMacrosVolcadas). Peri meals don't count.
    //
    // OTRA VEZ COMO EN CALMA (Francisco, 28-09): se ofrece aunque volcar cambie poco o nada.
    // El 21-09 se escondía cuando lo que quedaba del día se separaba del objetivo normal menos
    // del margen, y se decía «no hace falta volcar nada». Marco Antonio, que viene de Calma, lo
    // buscó dos días seguidos (27 y 28-09): con las tres primeras dentro de margen, lo que le
    // faltaba (3,5 g de proteína y 3,7 de hidratos) acabó cuadrándolo «Cuadrar el día» moviendo
    // comidas que ya se había comido. El volcado lo mete en la última sin tocar las demás.
    const volcado = (() => {
        const nada = { destino: null };
        if (activeVolcado || singleMeal) return nada; // Calma: volcar disabled in single-meal mode
        const regulars = getMealOrder().filter(k => !['Intra', 'Post'].includes(k));
        const noValidas = regulars.filter(k => getMealStatus(k) !== 'cuadrada');
        const candidata = noValidas.length === 1 ? noValidas[0] : null;
        return candidata ? { destino: candidata } : nada;
    })();
    const volcarTargetMeal = volcado.destino;

    // Meal info
    const mealInfo = {
        C1: { name: singleMeal ? 'Comida única' : 'Comida 1', shortName: 'C1', emoji: singleMeal ? '🍽️' : '🌅' },
        C2: { name: 'Comida 2', shortName: 'C2', emoji: '☀️' },
        C3: { name: numComidas === 3 ? 'Comida 3' : 'Comida 3', shortName: 'C3', emoji: numComidas === 3 ? '🌙' : '🌤️' },
        C4: { name: 'Comida 4', shortName: 'C4', emoji: '🌙' },
        // Sin el «-entreno»: en 390 px salía «INTRA-ENT...» cortado (recorrido móvil
        // del 23-08) y el icono del rayo ya dice de qué va.
        Intra: { name: 'Intra', shortName: 'Intra', emoji: '⚡' },
        Post: { name: 'Post', shortName: 'Post', emoji: '💪' }
    };

    // ── El día vacío (doc 21-08, tarea 6.1) ──────────────────────────────────
    // «Vacío» es SOLO ninguna comida con alimentos: un día migrado con 1-2 comidas
    // montadas sigue yendo a la parrilla de siempre. Y si la carga falló, la pantalla
    // vacía sería mentira (el día puede existir en el servidor), así que tampoco.
    const montadasDelDia = Object.entries(mealsData || {})
        .filter(([, m]) => (m?.alimentos || []).length > 0).map(([k]) => k);
    const diaVacio = montadasDelDia.length === 0;
    const mostrarDiaVacio = diaVacio && !cargaFallida && diaEnCreacion !== currentDate;
    // EL AVISO DE APLICAR UNA FAVORITA DICE EL NÚMERO (doc 3-09, detalle 1): «tus 3 comidas»
    // y no «esas comidas», que es lo que hace parar. El intra y el post no cuentan como
    // comidas (regla del 31-08), así que van aparte para poder nombrarlos por su nombre.
    const comidasDelDia = montadasDelDia.filter(k => /^C\d+$/.test(k)).length;
    const periDelDia = montadasDelDia.filter(k => k === 'Intra' || k === 'Post');
    // Los días que se ofrecen repetir: montados de verdad y que no sean el día abierto.
    const recientesMontados = (recentDiets || []).filter(d =>
        d.fecha !== currentDate
        && Object.values(d.comidas || {}).some(m => (m?.alimentos || []).length > 0));
    // «Hoy · jueves, 21 de agosto», o el día que sea si no es hoy.
    const tituloDiaVacio = (() => {
        const [y, m, d] = (currentDate || hoyISO()).split('-').map(Number);
        const largo = new Date(y, m - 1, d).toLocaleDateString('es-ES', {
            weekday: 'long', day: 'numeric', month: 'long',
            ...(y !== new Date().getFullYear() ? { year: 'numeric' } : {}),
        });
        return currentDate === hoyISO() ? `Hoy · ${largo}` : largo.charAt(0).toUpperCase() + largo.slice(1);
    })();

    // ===== COMPONENTS =====

    // Progress Bar Component
    // ===== LOADING STATE =====
    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand border-t-transparent" />
                    <p className="text-muted-foreground text-sm">Cargando...</p>
                </div>
            </div>
        );
    }

    // ===== SHOW PREFERENCES SETUP IF NEEDED =====
    if (preferencesLoading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand border-t-transparent" />
            </div>
        );
    }
    
    if (showPreferencesSetup) {
        return (
            <PreferencesSetup
                api={api}
                initialPreferences={userPreferences}
                initialAvoidedCategories={avoidedCategories}
                initialAvoidedKeywords={avoidedKeywords}
                onSave={handlePreferencesSaved}
                onCancel={() => setShowPreferencesSetup(false)}
                isEditMode={userPreferences.length > 0}
            />
        );
    }

    // ===== SIN REPARTO DE MACROS (bug 0·0·0) =====
    // Sin distribución, cada comida tendría objetivo 0P·0H·0G: "todo sobra" y el
    // autoajuste excluye todo. Mejor una pantalla clara con el porqué y la salida.
    if (!distribution && distribError) {
        const sinMacros = distribError === 'No tienes macros asignados';
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-4">
                <div className="surface p-6 max-w-md w-full text-center" data-testid="distrib-error">
                    <span className="text-4xl mb-3 block">🎯</span>
                    <h2 className="font-heading font-bold text-2xl text-foreground mb-2">
                        {sinMacros ? 'Aún no tienes macros asignados' : 'No se pudo cargar el reparto del día'}
                    </h2>
                    <p className="text-sm text-muted-foreground mb-5">
                        {sinMacros
                            ? 'Sin macros no podemos repartir objetivos entre tus comidas. Calcúlalos en un minuto con la calculadora, o pídeselos a tu entrenador.'
                            /* Aquí se pegaba `distribError` entre paréntesis, que es el mensaje
                               de la excepción: «Request failed with status code 500», «Network
                               Error». Al cliente no le dice nada y no puede hacer nada con ello.
                               Sigue yendo entero a la consola, que es donde sirve. */
                            : 'Sin el reparto, las comidas no tienen objetivo. Inténtalo de nuevo en un momento.'}
                    </p>
                    <div className="flex flex-col gap-2">
                        {sinMacros ? (
                            <button className="btn-brand w-full py-2.5" data-testid="distrib-error-cta"
                                onClick={() => navigate('/dashboard/macro-calculator')}>
                                Calcular mis macros
                            </button>
                        ) : (
                            <button className="btn-brand w-full py-2.5" onClick={() => loadDistribution()}>
                                Reintentar
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ===== MAIN RENDER =====
    const mealCardProps = {
        mealInfo, mealsData, expandedMeals, setExpandedMeals, getMealTarget, calculateMealMacros,
        getMealStatus: getMealStatusParaVer, loadMenuOptions, setBuildMealModal, openRepeatModal, removeFood, moveFoodUp,
        updateFoodQuantity, updateFoodQuantityDirect, editingQuantity, setEditingQuantity,
        getQuantityIncrement, clearMeal, getFoodEmoji, formatFoodQuantity, setMealMode,
        esPorUnidad, pesoUnidad, abrirFavoritasDeComida, crearPeri, creandoPeri,
        // Lo que lleva el día de cada familia calibrada, para el contador de la línea del
        // alimento (`ContadorFamilia`). Va en los props comunes porque el contador es el
        // mismo en todas las comidas: el tramo lo decide el día entero.
        acumFamilias,
    };
    // Con «No quiero intra», la tarjeta gris va donde iba el intra: justo antes del post, o
    // si hoy no hay post, detrás de la comida después de la que entrena.
    const conIntraRechazado = (mealKey, tarjeta) => {
        if (!intraRechazado) return tarjeta;
        const orden = getMealOrder();
        const antesDe = orden.includes('Post') ? 'Post' : null;
        if (antesDe === mealKey) return <React.Fragment key={mealKey + '-con-intra'}>{tarjetaIntraRechazado}{tarjeta}</React.Fragment>;
        if (!antesDe) {
            const detras = Number(momentoEntreno) > 0 ? `C${momentoEntreno}` : null;
            if (detras === mealKey || (!detras && mealKey === orden[0])) {
                return <React.Fragment key={mealKey + '-con-intra'}>{detras ? <>{tarjeta}{tarjetaIntraRechazado}</> : <>{tarjetaIntraRechazado}{tarjeta}</>}</React.Fragment>;
            }
        }
        return tarjeta;
    };
    const renderMealCard = (mealKey, forceExpanded, denso = false) => conIntraRechazado(mealKey,
        <MealCard
            key={mealKey + (forceExpanded ? '-d' : '-m')}
            forceExpanded={forceExpanded}
            denso={denso}
            mealKey={mealKey}
            // Los suplementos que le tocan con esta comida (maqueta de la parte 6: «+ Creatina»
            // debajo de la 3). El mismo dato y la misma regla que en Inicio: lib/suplementosDelDia.
            suplementos={supPorComida[mealKey]}
            // Y desde ahí se llega a Suplementos (punto 190): es el único sitio de esta
            // pantalla por donde se entra.
            irASuplementos={() => navigate('/dashboard/supplements')}
            // LA VUELTA ATRÁS, EN LA TARJETA (Jesús, 6-09). Solo mientras la comida siga
            // como la dejó el cambio: si el cliente ya ha tocado algo, el enlace se cae solo
            // y no se lleva por delante lo que acaba de poner. Ver `deshacerComida`.
            deshacer={(() => {
                const d = deshacerComida[mealKey];
                if (!d || huellaConCantidades(mealsData[mealKey]) !== d.huella) return null;
                return {
                    texto: d.texto,
                    onDeshacer: () => {
                        setMealsData((prev) => ({ ...prev, [mealKey]: d.antes }));
                        setDeshacerComida((prev) => {
                            const { [mealKey]: _fuera, ...resto } = prev;
                            return resto;
                        });
                    },
                };
            })()}
            {...mealCardProps}
            isLocked={isMealLocked(mealKey)}
            canVolcar={mealKey === volcarTargetMeal}
            onVolcar={handleVolcarToMeal}
            // En el intra y el post no hay botón «Cuadrar» (Francisco, 08-08-2026):
            // el peri se monta con «Construir» y con el sugeridor, y ahí el botón no
            // pinta nada. Sin `onCuadrar`, MealCard no lo dibuja.
            //
            // El backend SÍ sabe cuadrar el peri, y eso se queda: `refit-diet` es lo
            // que ajusta una dieta favorita al aplicarla y al pasarla de entreno a
            // descanso, y hasta el 08-08 el peri de esas favoritas se copiaba tal cual,
            // sin ajustar a los macros del día. Quitar el botón es una decisión de
            // pantalla; que el peri se ajuste al aplicar una favorita no lo es.
            onCuadrar={['Intra', 'Post'].includes(mealKey) ? undefined : cuadrarComida}
            mealMode={mealsData[mealKey]?.modo === 'manual' ? 'manual' : 'auto'}
            noQuieroPeri={mealKey === 'Intra' ? noQuieroPeri : null}
            sugerirPost={mealKey === 'Post' ? () => setPostSugeridosAbierto(true) : null}
        />
    );

    // Aviso de guardado. Solo aparece cuando hay algo que contar: mientras guarda, cuando acaba
    // y, sobre todo, si ha fallado (antes fallaba en silencio y el dia se perdia sin avisar).
    const renderEstadoGuardado = () => {
        // Manda sobre el resto: si el día no está calibrado, los macros que se ven no son los
        // buenos, y decir "Guardado" tan tranquilo sería peor que no decir nada.
        if (calibracionFallida) {
            return (
                <button onClick={() => { setCalibracionFallida(false); setCalibracionIntento(n => n + 1); }}
                    title="Los macros no incluyen el acumulado del día. Toca para volver a intentarlo."
                    data-testid="calibracion-error"
                    className="flex items-center gap-1.5 text-xs font-semibold text-amber-500 hover:underline">
                    <AlertCircle className="w-3.5 h-3.5" /> Macros sin calibrar. Reintentar
                </button>
            );
        }
        if (cargaFallida && guardadoEstado !== 'guardado') {
            return (
                <button onClick={reintentarCarga} title="Vuelve a pedir el día al servidor"
                    className="flex items-center gap-1.5 text-xs font-semibold text-amber-500 hover:underline">
                    <AlertCircle className="w-3.5 h-3.5" /> Sin conexión con el servidor. Reintentar
                </button>
            );
        }
        if (guardadoEstado === 'idle') return null;
        // «GUARDADO» NO PUEDE SALIR EN UN DÍA VACÍO (punto 205 del 28-08). Salía en verde,
        // arriba del todo y con su ✓, en un día en el que el cliente no había puesto nada:
        // «es la primera línea que lee, y es mentira». Y el verde, en esta app, significa
        // resuelto (punto 77). Mientras no haya nada que guardar, ahí no va nada.
        //
        // Los avisos de FALLO sí se quedan (los de arriba y el de abajo): esos no dicen que
        // algo esté hecho, dicen que algo salió mal, y callarlos sería el fallo en silencio
        // que ya costó un día perdido.
        if (diaVacio && (guardadoEstado === 'guardado' || guardadoEstado === 'guardando')) return null;
        if (guardadoEstado === 'error') {
            return (
                <button onClick={flushGuardado}
                    className="flex items-center gap-1.5 text-xs font-semibold text-red-400 hover:underline">
                    <AlertCircle className="w-3.5 h-3.5" /> No se ha podido guardar. Reintentar
                </button>
            );
        }
        return (
            <span data-testid="estado-guardado"
                className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {guardadoEstado === 'guardando'
                    ? <><div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" /> Guardando...</>
                    : <><Check className="w-3.5 h-3.5 text-green-500" /> Guardado</>}
            </span>
        );
    };

    // AQUI ESTABA `renderActions`: PDF, Copiar y Favoritas repetidos ABAJO DEL TODO,
    // detras de las comidas, porque en el movil no cabian arriba. Con el «···» ya estan
    // arriba en los dos tamaños (punto 119), asi que esto era un tercer sitio para los
    // mismos botones -- y el peor: nadie baja hasta el final a buscar el PDF.

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px] mx-auto pb-24 lg:pb-10 animate-fade-in" data-testid="nutrition-page">
            {/* Primero los gustos (el gate de preferencias, mas arriba), luego como esta repartido
                su dia, y solo despues el tutorial de la pantalla. */}
            {primeraDieta ? (
                <PrimeraDieta
                    momentoEntreno={momentoEntreno}
                    numComidas={numComidas}
                    // La configuración del día está siempre a la vista en esta misma pantalla, así
                    // que "cambiarla" es cerrar esto (y el tutorial) para que la vea sin estorbos.
                    onIrAConfig={() => { cerrarPrimeraDieta(); dismissIntro(); }}
                    // UNA BIENVENIDA, NO DOS ENCADENADAS.
                    // Al cerrar esta aparecía el tutorial de la pantalla: dos pantallas completas
                    // antes de ver la primera dieta, contando lo mismo por partes. Ahora cerrar
                    // esta da por visto también aquel.
                    // No se pierde: el tutorial sigue entero en «Repetir recorrido guiado», en Mi
                    // perfil. Es el argumento de Jesús (11-08): como se puede volver a ver, no
                    // hace falta contarlo todo la primera vez, con la dieta esperando detrás.
                    onListo={() => { cerrarPrimeraDieta(); dismissIntro(); }}
                />
            ) : showIntro && <NutritionIntro onClose={dismissIntro} />}
            {/* Con flex-wrap: en un móvil estrecho «Imprimir dieta» y el «···» bajan debajo del
                título en vez de pisar «Este día te lo creó…» (26-09, a 290 px). */}
            <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-4">
                <div className="min-w-0 flex-1 basis-[9rem]">
                    {/* EL TÍTULO «NUTRICIÓN» NO SALE EN EL TELÉFONO: en la barra de abajo ya
                        está marcada «Nutrición» en naranja, así que a 48 px lo dice por
                        tercera vez. En escritorio se queda, que ahí no hay barra inferior.
                        «Plan nutricional» y el estado de guardado se quedan siempre. */}
                    {/* «NUTRICIÓN», NO «PLAN NUTRICIONAL» (maqueta de la parte 6). Se llama
                        igual que en el menú, en la barra de abajo y en la ruta; «Plan
                        nutricional» era el único sitio de la app donde tenía otro nombre. */}
                    <p className="caption text-brand mb-1">Nutrición</p>
                    <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase text-foreground leading-none hidden lg:block">Nutrición</h1>
                    <div className="mt-1 h-4">{renderEstadoGuardado()}</div>
                    {/* Este día se lo creó su entrenador (punto 4.11). «Creó» y no «montó»: en
                        toda la app se dice crear (punto 7 del 23-08; ficha 139). */}
                    {loMontoSuCoach && (
                        <p className="mt-1 text-xs text-brand font-semibold flex items-center gap-1.5"
                            data-testid="dieta-la-monto-el-coach">
                            <UserCheck className="w-3.5 h-3.5" />
                            Este día te lo creó {loMontoSuCoach.por || 'tu entrenador'}
                            {loMontoSuCoach.cuando ? ` el ${fechaDeEdicion(loMontoSuCoach.cuando)}` : ''}
                        </p>
                    )}
                </div>
                {/* DOS BOTONES, E IGUALES EN LOS DOS SITIOS (punto 119). Eran cinco en el
                    ordenador y tres en el móvil, y los dos que faltaban eran justo PDF y
                    Copiar: los que se usan. Reaparecían abajo del todo, detrás de las
                    comidas, donde nadie baja a buscarlos. Ahora PDF fuera y el resto en el
                    «···», sin `hidden sm:` ni `lg:hidden` que cambien la botonera según el
                    ancho de la ventana. */}
                <div className="flex items-center gap-2">
                    {/* «VER DIETA», SIN LA PALABRA PDF (Jesús, ficha 05 del 5-09-2026, que
                        cierra lo que ya dijo el 3: «que sea como ver dieta, quitar la palabra
                        en PDF»). Esta mañana se quitó el «PDF» y se puso «Descargar», y él
                        vuelve sobre ello: no quiere el formato NI la maniobra, quiere lo que
                        va a hacer con el botón, que es ver su dieta. El icono se queda. */}
                    {/* «IMPRIMIR DIETA» (Francisco, 21-09): «ver dieta, que descarga el PDF,
                        que no se llame así». Lo que el botón hace es darte la dieta en papel
                        -- un PDF que se guarda o se imprime --, y «ver» prometía otra cosa:
                        una pantalla. Manda lo más reciente. */}
                    <button onClick={exportPdf} disabled={exportingPdf} data-testid="export-pdf-btn"
                        className="inline-flex items-center gap-2 surface px-3.5 py-2 text-sm font-semibold text-muted-foreground hover:text-brand transition-colors whitespace-nowrap shrink-0" title="Imprimir la dieta">
                        {exportingPdf ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <FileDown size={16} />} Imprimir dieta
                    </button>
                    <MenuDeLaPantalla opciones={[
                        { id: 'copiar', texto: 'Copiar a otro día', icono: Copy, al: () => setCopyModalOpen(true) },
                        { id: 'favoritas', texto: 'Dietas favoritas', icono: Star,
                            al: () => { loadDietFavorites(); setFavoritesModalOpen(true); } },
                        { id: 'preferencias', texto: 'Preferencias alimentarias', icono: SlidersHorizontal,
                            al: () => setShowPreferencesSetup(true) },
                        // Cómo ver las comidas: se elige una vez y estaba a la vista y
                        // permanente encima de las comidas, que es lo que se viene a ver.
                        { id: 'vista', texto: 'Cómo ver las comidas', icono: Settings,
                            detalle: (VISTAS_DE_COMIDAS.find(v => v.id === vistaComidas) || {}).nombre,
                            al: () => setAjustesVistaAbierto(true) },
                        // PUNTO 113: el resumen de la configuración «se queda, dentro del
                        // ···. Es corto, es cierto y además es un botón».
                        { id: 'config', texto: 'Cambiar la configuración del día', icono: SlidersHorizontal,
                            detalle: resumenDeLaConfig({ numComidas, tipoDia, momentoEntreno, opcionPeri, sinPeri }),
                            al: () => setConfigExpanded(true) },
                    ]} />
                </div>
            </header>

            {ajustesVistaAbierto && (
                <div className="surface p-4 mb-4" data-testid="ajustes-vista">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="caption">Cómo ver las comidas</p>
                        <VistaComidasSelector vista={vistaComidas} onCambiar={cambiarVistaComidas} />
                        <button onClick={() => setAjustesVistaAbierto(false)}
                            className="rounded-xl font-bold text-sm px-5 py-2.5 bg-brand text-white hover:bg-brand/85 transition-colors">
                            Listo
                        </button>
                    </div>
                </div>
            )}

            {/* EL AVISO DEL DÍA LEJANO («Este día queda muy atrás de hoy y no tiene un plan
                propio...», QA 15-08) SE QUITÓ: Jesús, 27-09, «fui atrás un día y salió este
                mensaje en amarillo.. bórralo». */}

            {/* Cabecera del día: fecha, tipo de día, configuración resumida y macros */}
                <DayHeader
                    // El estado del día va calculado desde aquí: es el único sitio donde se
                    // decide, y de aquí sale también el de Inicio (Jesús, 9-09).
                    estadoDelDia={estadoDelDia}
                    loQueFaltaAlDia={loQueFaltaAlDia}
                    onCrearDia={() => setDiaEnCreacion(currentDate)}
                    onTerminarDia={handleRecuadrarDia}
                    terminando={recuadrando}
                    currentDate={currentDate}
                    formatDate={formatDate}
                    changeDate={changeDate}
                    setCalendarOpen={setCalendarOpen}
                    handleSetTipoDia={handleSetTipoDia}
                    diaSinMarcar={diaSinMarcar}
                    numComidas={numComidas}
                    setNumComidas={handleSetNumComidas}
                    momentoEntreno={momentoEntreno}
                    setMomentoEntreno={handleSetMomentoEntreno}
                    opcionPeri={opcionPeri}
                    setOpcionPeri={handleSetOpcionPeri}
                    sinPeri={sinPeri}
                    singleMeal={singleMeal}
                    configExpanded={configExpanded}
                    setConfigExpanded={setConfigExpanded}
                    tipoDia={tipoDia}
                    summaryExpanded={summaryExpanded}
                    setSummaryExpanded={setSummaryExpanded}
                    dayMacros={dayMacros}
                    dayTarget={dayTarget}
                    servedPeriP={servedPeriP}
                    servedPeriH={servedPeriH}
                    servedPeriG={servedPeriG}
                    totalPeriP={totalPeriP}
                    totalPeriH={totalPeriH}
                    mealOrder={getMealOrder()}
                    mealInfo={mealInfo}
                    calculateMealMacros={calculateMealMacros}
                    getMealStatus={getMealStatusParaVer}
                />

                {/* EL DÍA VACÍO EN LUGAR DE LA PARRILLA (doc 21-08, tarea 6.1): una
                    pregunta con tres salidas. Los macros del día siguen arriba, en la
                    cabecera, para que sepa a qué está montando. «Crear el día» abre la
                    parrilla de siempre; las otras dos salidas la rellenan y la parrilla
                    aparece sola en cuanto hay comida. */}
                {mostrarDiaVacio ? (
                <div className="mt-4 lg:mt-6">
                    <DiaVacio
                        titulo={tituloDiaVacio}
                        tipoDia={tipoDia}
                        numFavoritas={dietFavorites.length}
                        onCrear={() => setDiaEnCreacion(currentDate)}
                        onVerFavoritas={() => { loadDietFavorites(); setFavoritesModalOpen(true); }}
                        recientes={recientesMontados}
                        cargandoRecientes={cargandoRecientes}
                        onRepetir={repetirDiaReciente}
                        formatDate={formatDate}
                    />
                </div>
                ) : (
                <>
                {/* En el teléfono, sin la raya que separaba los macros del día de las
                    comidas: son la misma cosa contada dos veces -- lo que te queda arriba,
                    de dónde va a salir debajo -- y la raya las presentaba como dos secciones
                    ajenas. En escritorio se queda. */}
                <div className="my-4 lg:my-6 lg:border-t lg:border-border" />

                {/* ── Comidas: selector en columna + detalle ── */}
                <div data-testid="nutrition-meals">
                    {/* LA LÍNEA «ESTE DÍA SE GUARDA EN CUANTO PONGAS EL PRIMER ALIMENTO...» SE QUITÓ
                        (Jesús, 27-09: «ese mensaje sobra»). La regla sigue igual: un día sin un
                        solo alimento no se guarda (punto 4 del 9-09). */}
                    {/* «TE PASAS» con salida (punto 26 del 23-08): si el día ya creado se
                        pasa del objetivo -- lo típico tras un cambio de macros --, se
                        ofrece recuadrarlo, no solo se le riñe. */}
                    {/* NI EN UN DÍA PASADO (Jesús, 27-09 22:13: «yo en el pasado simplemente
                        quiero consultar la dieta, no quiero que me digan nada... un día pasado
                        es un día pasado, no me tiene que saltar ningún aviso»). Sus macros ya
                        son los de esa fecha (el reparto se pide con `fecha`). */}
                    {/* NI CON EL DÍA LISTO (Jesús, 28-09 00:07, captura del martes 29 en dev:
                        «esto me sale mal esta pantalla»). Con 11,6 g de proteína de más y lo
                        demás dentro, la banda decía «✓ Día listo · No te falta nada» y este
                        cartel, debajo, que el día no cuadraba: dos veredictos. Manda el juez
                        de la banda (`lib/estadoDelDia`): la proteína de más no descuadra. */}
                    {/* Y SE PUEDE CERRAR (Jesús, 28-09, 22:21): «tiene que aparecer arriba una cruz
                        de cerrar la ventana... me cambio al día anterior, vuelvo al día actual y
                        me vuelve a salir». Cerrado se queda cerrado para ese día en esta sesión;
                        vuelve si cambian los macros del día (es otro descuadre). Ver
                        `lib/avisosCerrados`. */}
                    {!activeVolcado && !loading && currentDate >= hoyISO() && estadoDelDia !== 'listo'
                        && getDayStatus() === 'sobra'
                        && !estaCerrado(uid, 'no-cuadran', currentDate, huellaDelObjetivo(dayTarget))
                        && Object.values(mealsData).some(c => (c?.alimentos || []).length > 0) && (
                        <div className="surface relative p-4 pr-11 mb-4 flex flex-wrap items-center justify-between gap-3 border-amber-500/30"
                            data-testid="banner-recuadrar">
                            <button type="button" aria-label="Cerrar aviso"
                                onClick={() => cerrarAvisoDelDia('no-cuadran', huellaDelObjetivo(dayTarget))}
                                className="absolute top-2 right-2 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                data-testid="cerrar-banner-recuadrar">
                                <X className="w-4 h-4" />
                            </button>
                            {/* Si no caben, el botón baja debajo del texto (26-09: a 290 px el texto se
                                quedaba en una columna de una palabra por línea). */}
                            {/* LAS PALABRAS SON LAS DE LA MAQUETA DE LA PARTE 6, que es la que
                                manda (Francisco, 27-08).
                                En el vídeo de esa misma mañana, leyendo esta frase en voz alta,
                                dijo «sería, de tus macros ACTUALES» y «pondríamos recuadrar el
                                día. No. Pondríamos REAJUSTAR», y así se dejó unas horas. La
                                parte 6 la dibuja con «de ahora», «te reajustamos» y el botón en
                                «Cuadrar el día», y el documento va por delante del vídeo.
                                Si algún día se vuelve a lo del vídeo, es aquí y son tres
                                palabras. */}
                            {/* Y EL TITULAR DICE LO QUE PASA, NO SIEMPRE LO MISMO (revisión de
                                Nutrición del 1-09, menor 2). El estado «sobra» se enciende con
                                que UN macro se pase, así que el 19 de agosto salía «se pasa de
                                tus macros» con 199 g de proteína y 200 de hidratos DE MENOS y
                                solo 7 de grasa de más: justo lo contrario de lo que pasaba.
                                Cuando además falta algo, se dice que no cuadra y se ponen los
                                números. La maqueta de Jesús dibuja el caso de pasarse y ahí no
                                se toca ni una palabra; esto es el caso que su maqueta no
                                contemplaba. */}
                            <div className="min-w-0 flex-1 basis-[13rem]">
                                {/* EL TÍTULO DICE QUÉ PASA, NO DE QUIÉN ES LA CULPA (Jesús,
                                    «El día que no cuadra», 9-09). Su frase: «"Este día se
                                    pasa" suena a que el cliente hizo algo mal. No hizo nada:
                                    le cambiaron los macros». Así que el sujeto deja de ser
                                    el día y pasan a serlo los macros, que son los que se han
                                    movido.

                                    Y ES EL MISMO PARA LOS DOS CASOS. Antes había uno para
                                    «se pasa» y otro para «no cuadra», y esa distinción era
                                    de la app, no del cliente: por debajo es lo mismo, sus
                                    macros ya no son los de este día. Los números concretos
                                    siguen saliendo en la línea de abajo. */}
                                <p className="font-bold text-foreground">
                                    Los macros de este día no cuadran con los que tienes ahora
                                </p>
                                {/* SIN LA PROMESA DE «SIN QUITARTE NADA» (ficha 44 del parte
                                    de Gonzalo). El mensaje de después ya dice la verdad
                                    cuando el motor no llega («sobra 34 g de hidratos aunque
                                    lo bajemos todo al mínimo»), pero esta línea, que es la
                                    que se lee ANTES de pulsar, seguía prometiendo lo que a
                                    veces no se puede cumplir. Se dice lo que hacemos y lo
                                    que pasa si no se puede. */}
                                {/* Y SE LE QUITA EL MIEDO ANTES DE QUE LO TENGA (mismo
                                    documento). Su frase: «lo primero que piensa cualquiera
                                    al ver un botón que ajusta su comida es "¿me va a borrar
                                    la dieta?". Ahora se le contesta sin que lo pregunte».
                                    Va en el cuerpo y no en el aviso de después, porque esto
                                    se lee ANTES de pulsar, que es cuando da el miedo. */}
                                {/* «DE FORMA AUTOMÁTICA», NO «SOLAS» (Francisco, 16-09). Es la
                                    misma palabra con la que la app cuenta lo que hace ella
                                    en el resto de sitios -- «la media la hace la app de
                                    forma automática» --, y «solas» suena a que pasa sin que
                                    nadie lo mande. */}
                                {/* SOLO ESTA FRASE (Jesús, reunión del 25-09, por escrito: «ese mensaje es
                                    muy largo; que ponga esto: Los macros de este día no cuadran con los
                                    que tienes ahora. Puedes cuadrar el día y las cantidades se ajustan
                                    de forma automática. (EL RESTO FUERA)»). Se van la promesa de no
                                    borrar nada y los números de «Ahora mismo…». */}
                                <p className="text-xs text-muted-foreground">
                                    Puedes cuadrar el día y las cantidades se ajustan de forma automática.
                                </p>
                            </div>
                            <button
                                className="shrink-0 rounded-xl font-bold text-sm px-4 py-2 border border-brand text-brand hover:bg-brand hover:text-white transition-colors disabled:opacity-50"
                                onClick={handleRecuadrarDia} disabled={recuadrando}
                                data-testid="boton-recuadrar-dia">
                                {recuadrando ? 'Cuadrando...' : 'Cuadrar el día'}
                            </button>
                        </div>
                    )}
                    {/* «CUADRA ESTAS COMIDAS» (Jesús, reunión del 25-09): lo que dice «Cuadrar el
                        día» cuando hay comidas fuera de su margen, con un botón para ir a cada
                        una. Se van de la lista según las cuadra. */}
                    {!activeVolcado && comidasACuadrar?.fecha === currentDate
                        && !estaCerrado(uid, 'cuadra-estas-comidas', currentDate) && (() => {
                        const quedan = comidasACuadrar.comidas.filter((k) => getMealOrder().includes(k) && comidaFueraDeMargen(k));
                        if (!quedan.length) return null;
                        return (
                            <div className="surface relative p-4 pr-11 mb-4 border-brand/40" data-testid="cuadra-estas-comidas">
                                {/* Con su cruz, como el de arriba (Jesús, 28-09). Vuelve a salir
                                    si se pulsa otra vez «Cuadrar el día». */}
                                <button type="button" aria-label="Cerrar aviso"
                                    onClick={() => cerrarAvisoDelDia('cuadra-estas-comidas')}
                                    className="absolute top-2 right-2 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                    data-testid="cerrar-cuadra-estas-comidas">
                                    <X className="w-4 h-4" />
                                </button>
                                <p className="font-bold text-foreground">
                                    Para cuadrar el día, cuadra {quedan.length === 1 ? 'esta comida' : 'estas comidas'}
                                </p>
                                <p className="text-xs text-muted-foreground mb-3">
                                    Entra en {quedan.length === 1 ? 'ella' : 'cada una'} y pulsa «Cuadrar».
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {quedan.map((k) => (
                                        <button key={k} onClick={() => irAComida(k)}
                                            className="rounded-xl font-bold text-sm px-4 py-2 border border-brand text-brand hover:bg-brand hover:text-white transition-colors"
                                            data-testid={`ir-a-comida-${k}`}>
                                            Ir a {mealInfo?.[k]?.name || k}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}
                    {/* EL «DESHACER» DEL DÍA, DE UNA SOLA VEZ (Francisco, 16-09). Va aquí,
                        donde se pulsó el botón, y no repartido por las tarjetas: un gesto,
                        una vuelta atrás. Se cae solo en cuanto el día deja de ser el que
                        dejó el cuadre, igual que el de cada comida, para no pisar lo que el
                        cliente haya puesto después. Y no lleva cuenta atrás: el aviso que
                        se iba solo a los ocho segundos es justo lo que Jesús no quería. */}
                    {deshacerElDia && cantidadesDelDia(mealsData) === deshacerElDia.huella && (
                        <div className="flex items-center justify-between gap-3 rounded-xl border border-border
                                        bg-muted/30 px-3.5 py-2.5 my-3" data-testid="deshacer-el-dia">
                            <p className="text-xs text-muted-foreground min-w-0">
                                {deshacerElDia.cuantas === 1
                                    ? 'Cuadré las cantidades de una comida.'
                                    : `Cuadré las cantidades de ${deshacerElDia.cuantas} comidas.`}
                            </p>
                            <button type="button" data-testid="boton-deshacer-el-dia"
                                onClick={() => {
                                    setMealsData(deshacerElDia.antes);
                                    setDeshacerElDia(null);
                                }}
                                className="shrink-0 text-xs font-bold text-brand hover:underline">
                                Deshacer
                            </button>
                        </div>
                    )}
                    {/* Volcado de macros banner (ancho completo) */}
                    {activeVolcado && (
                        /* Sin `truncate` (punto 12 del 23-08: se leía «Volcados en…»,
                           cortado) y con el botón en «Deshacer» (punto 10). */
                        <div className="surface p-4 mb-4 flex items-center justify-between gap-3 border-brand/30">
                            <div className="min-w-0">
                                <p className="font-bold text-foreground">Los macros que te quedaban están volcados en {mealInfo[activeVolcado]?.name}</p>
                                <p className="text-xs text-muted-foreground">Las demás comidas quedan bloqueadas hasta deshacerlo.</p>
                            </div>
                            <button
                                className="shrink-0 rounded-xl font-bold text-sm px-4 py-2 border border-brand text-brand hover:bg-brand hover:text-white transition-colors"
                                onClick={handleEliminarVolcado}
                            >
                                Deshacer volcado
                            </button>
                        </div>
                    )}

                    {/* Cabecera de sección: el título y, a la derecha, cómo quiere verlas.
                        El switch de macros vive aquí porque solo cambia lo que pone en
                        la lista de ingredientes; ni los totales ni el reparto se mueven. */}
                    {/* «COMIDAS DEL DÍA» TAMBIÉN EN EL TELÉFONO (maqueta de la parte 6). Estaba
                        escondido en `lg`: se quitó del móvil porque «debajo vienen las comidas
                        con su nombre», pero encima de ellas hay tres números grandes, el pie de
                        «lo que llevas creado» y, algunos días, el aviso de que te pasas. Sin el
                        rótulo, la lista empieza sin que nada diga que empieza.
                        Los dos conmutadores sí se quedan sólo en escritorio: en el móvil viven
                        en la tuerca de arriba y aquí competirían con el rótulo por el ancho. */}
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-2.5">
                        <p className="caption">Comidas del día</p>
                        <span className="hidden lg:inline-flex">
                            <VistaComidasSelector vista={vistaComidas} onCambiar={cambiarVistaComidas} />
                        </span>
                    </div>

                    {vistaComidas === 'actual' && (
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 xl:gap-8 items-start">
                            {/* Selector de comidas (columna) - desktop lg+ */}
                            <aside className="hidden lg:block lg:col-span-4 xl:col-span-3 lg:sticky lg:top-6 self-start space-y-2" data-testid="meal-selector">
                                {getMealOrder().map(mealKey => (
                                    <React.Fragment key={mealKey}>
                                    {/* «Hoy sin intra», también en la columna del ordenador. */}
                                    {intraRechazado && mealKey === 'Post' && tarjetaIntraRechazado}
                                    <MealSelectorItem
                                        mealKey={mealKey}
                                        mealInfo={mealInfo}
                                        getMealTarget={getMealTarget}
                                        calculateMealMacros={calculateMealMacros}
                                        getMealStatus={getMealStatusParaVer}
                                        isLocked={isMealLocked(mealKey)}
                                        selected={selectedMeal === mealKey}
                                        onSelect={() => setSelectedMeal(mealKey)}
                                        numAlimentos={(mealsData[mealKey]?.alimentos || []).length}
                                    />
                                    </React.Fragment>
                                ))}
                                {intraRechazado && !getMealOrder().includes('Post') && tarjetaIntraRechazado}
                            </aside>

                            {/* Detalle (desktop) + acordeón (móvil) */}
                            <main className="lg:col-span-8 xl:col-span-9 min-w-0 space-y-5">
                                {/* Desktop: detalle de la comida seleccionada */}
                                <div className="hidden lg:block" data-testid="meal-detail">
                                    {getMealOrder().includes(selectedMeal) && renderMealCard(selectedMeal, true)}
                                </div>

                                {/* Móvil/tablet (<lg): acordeón de comidas en una sola columna */}
                                {/* Las comidas, pegadas: es una lista que se va tachando, no
                                    cinco tarjetas sueltas. El aire entre ellas sumaba casi
                                    una pantalla de móvil sin decir nada. */}
                                <div className="lg:hidden space-y-1" data-testid="meals-accordion">
                                    {getMealOrder().map(mealKey => renderMealCard(mealKey, false))}
                                </div>


                                {/* Acciones (móvil <sm: tras las comidas; en sm+ van en la tarjeta de config) */}
                                <div className="sm:hidden">
                                </div>
                            </main>
                        </div>
                    )}

                    {/* Pestañas: las comidas arriba y la abierta a todo el ancho. Las pestañas
                        se desplazan en horizontal cuando no caben (móvil, o día con peri). */}
                    {vistaComidas === 'pestanas' && (
                        <div className="space-y-4">
                            <div className="overflow-x-auto border-b border-border" role="tablist" data-testid="meal-tabs">
                                <div className="flex gap-1 min-w-min">
                                    {getMealOrder().map(mealKey => (
                                        <MealTab
                                            key={mealKey}
                                            mealKey={mealKey}
                                            mealInfo={mealInfo}
                                            getMealStatus={getMealStatusParaVer}
                                            isLocked={isMealLocked(mealKey)}
                                            selected={selectedMeal === mealKey}
                                            onSelect={() => setSelectedMeal(mealKey)}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div data-testid="meal-detail">
                                {getMealOrder().includes(selectedMeal) && renderMealCard(selectedMeal, true)}
                            </div>
                        </div>
                    )}

                    {/* Todo seguido: el día entero abierto, como la dieta de Calma. Sin
                        seleccionar nada, se edita cualquier comida donde está. */}
                    {/* EN PANTALLA ANCHA, EN DOS COLUMNAS (Jesus, 12-08-2026: «aprovechar el
                        ancho para ver las cuatro comidas a la vez»). En una sola columna, un
                        dia de cuatro comidas son dos pantallas y media de scroll en un monitor
                        donde sobra la mitad del ancho. A partir de xl caben las cuatro sin
                        moverse; por debajo se queda como estaba, que es donde tiene sentido. */}
                    {vistaComidas === 'continua' && (
                        <div data-testid="meals-continua">
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
                                {getMealOrder().map(mealKey => (
                                    <div key={mealKey} className="min-w-0">{renderMealCard(mealKey, true, true)}</div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* LOS EXTRAS, EN LAS TRES VISTAS (Francisco, 28-09). Vivían dentro de la
                        vista «actual», y con «Pestañas» o «Todo seguido» no salían. */}
                    <div className="mt-6">
                                {/* Extras del día (bloque 6.3): debajo de las comidas y del
                                    peri. No cuentan en ningún sitio (punto 28 del doc del
                                    24-08): ni en «Llevas» del Inicio ni en la Dieta.
                                    El componente habla axios (api.get/post/delete): se le
                                    adapta el fetch de esta página.

                                    `origen="nutricion"`: el bloque se monta en dos sitios
                                    (Inicio y aquí) y el componente NO trae valor por
                                    defecto a propósito, para que uno no se apunte como el
                                    otro. Aquí faltaba y todo lo apuntado desde Nutrición se
                                    guardaba con origen vacío: la mitad de los extras sin
                                    procedencia y sin forma de saber por dónde los apunta la
                                    gente. Si este bloque se monta en un tercer sitio, ese
                                    sitio pone su origen (y lo acepta `_ORIGENES_EXTRA` en
                                    backend/routes/diets.py). */}
                                {/* Y NO EN UN DÍA QUE TODAVÍA NO EXISTE (Jesús, vídeo del
                                    20-09): «si yo estoy creando un día desde cero, esto de
                                    extras del día no debería salirme aquí».
                                    Tiene razón: un extra es lo que te comes FUERA de la dieta,
                                    y sin dieta no hay fuera. Se calla mientras no haya nada
                                    creado, y vuelve en cuanto lo hay.
                                    Con una salvedad: si ese día ya tiene extras apuntados --
                                    se pueden apuntar desde Inicio sin haber montado la dieta --
                                    el bloque sigue saliendo, que esconderlos sería perderlos de
                                    vista. */}
                                {(hayComidaCreada || (extrasDia || []).length > 0) && (
                                <ExtrasDelDia
                                    api={{
                                        get: (url, cfg) => api(`/api${url}?${new URLSearchParams(cfg?.params || {})}`).then((data) => ({ data })),
                                        post: (url, body) => api(`/api${url}`, { method: 'POST', body: JSON.stringify(body || {}) }).then((data) => ({ data })),
                                        delete: (url) => api(`/api${url}`, { method: 'DELETE' }).then((data) => ({ data })),
                                    }}
                                    fecha={currentDate}
                                    origen="nutricion"
                                    // PLEGADO AQUÍ, Y SOLO AQUÍ (Jesús, 6-09-2026): «cuando abro
                                    // Nutrición, aquí quiero que extras salga con el más, no
                                    // abierto. Abierto solo si ya tiene algo». Vuelve la prop que
                                    // se había quitado el 5-09, ahora con `false` por defecto: en
                                    // Inicio, en el panel del día y en la pregunta de la noche el
                                    // campo sigue abierto, que es donde se apunta sobre la marcha.
                                    // Aquí es lo último de una pantalla larga y la mayoría de los
                                    // días no se usa, que es el motivo que ya traía la maqueta de
                                    // la parte 6. Ver el comentario de `plegable` en el propio
                                    // componente: esta decisión ha ido y venido tres veces.
                                    plegable
                                    // Y CON EL MISMO BOTÓN QUE INICIO (Francisco, 28-09: «el botón
                                    // de apuntar extra del inicio también debía ir en Nutrición»):
                                    // cerrado es «+ Apuntar un extra», en naranja, sin el rótulo.
                                    compacto
                                    extras={extrasDia}
                                    onAnadido={(extra) => setExtrasDia((prev) => [...prev, extra])}
                                    onQuitado={(id) => setExtrasDia((prev) => prev.filter((e) => e.id !== id))}
                                />
                                )}
                    </div>
                </div>
                </>
                )}

            {/* AQUÍ SE MONTABA UN BUSCADOR DE ALIMENTOS QUE NADIE PODÍA ABRIR.
                `addFoodModal` se inicializaba cerrado y se cerraba en dos sitios, pero en toda
                la aplicación no había una sola línea que lo pusiera en abierto: estaba escrito,
                montado y muerto. Salió al hacer el catálogo de pantallas del 10-08, intentando
                retratarlo.
                No se le ha puesto una puerta porque lo que hacía ya existe y mejor: el modal
                «Lo hago yo» busca alimentos y los añade a la comida, con categorías y
                preparaciones. Un segundo camino a lo mismo solo añade ruido.
                Se va con él su `useEffect` de búsqueda y sus cuatro estados. El fichero
                SearchFoodModal.jsx SE QUEDA: de él salen `FOOD_FAVORITES_UI`, que usa
                BuildMealModal, y `FoodFilterBar`, que usa la ficha del cliente. */}

            {/* "Sugiéreme un menú": biblioteca REAL por cercanía, sin reescalar */}
            <LibraryMenusModal
                open={menuOptionsModal.open}
                mealKey={menuOptionsModal.mealKey}
                onClose={() => setMenuOptionsModal({ open: false, mealKey: null })}
                mealInfo={mealInfo}
                target={menuOptionsModal.mealKey ? getMealTarget(menuOptionsModal.mealKey) : null}
                api={api}
                dayConfig={{
                    fecha: currentDate,
                    tipo_dia: tipoDia,
                    num_comidas: numComidas,
                    momento_entreno: momentoEntreno,
                    opcion_peri: opcionPeri,
                }}
                onApply={applyLibraryMenu}
            />

            {/* Build Meal Modal */}
            <BuildMealModal 
                open={buildMealModal.open}
                mealKey={buildMealModal.mealKey}
                mode={buildMealModal.mode || 'normal'}
                onClose={() => setBuildMealModal({ open: false, mealKey: null })}
                getMealTarget={getMealTarget}
                mealInfo={mealInfo}
                api={api}
                tipoDia={tipoDia}
                mealsData={mealsData}
                // Las comidas del reparto de hoy: la ventana calibra el día con ESTAS y no
                // con todo `mealsData`, que guarda de más (Francisco, 13-09-2026).
                mealOrder={getMealOrder()}
                setMealsData={setMealsData}
                setMealMode={setMealMode}
                getFoodEmoji={getFoodEmoji}
                userPreferences={userPreferences}
                avoidedCategories={avoidedCategories}
                // Lo que lleva el día de las familias calibradas: sin esto la ventana
                // calculaba con el motor de antes de la calibración y sus números cambiaban
                // al guardar (punto 135 del 26-08).
                acumFamilias={acumFamilias}
                onGuardado={alGuardarComida}
            />

            <PostSugeridos open={postSugeridosAbierto} onClose={() => setPostSugeridosAbierto(false)}
                api={api} objetivo={getMealTarget('Post')}
                onUsar={(alimentos, titulo) => {
                    // «Usar» solo lo pone en la comida. La pregunta de dejarlo como favorito
                    // no sale aquí: «primero tengo que usarlo, ver cómo está, y luego ya veo»
                    // (Jesús, 27-09). Sale cuando lo guarda desde su hoja (onGuardado).
                    setPostSugeridosAbierto(false);
                    const fav = profile?.peri_favorito?.post;
                    const esElPuesto = !!fav && firmaDe(fav.alimentos) === firmaDe(alimentos);
                    setMealsData((prev) => ({ ...prev, Post: { ...(prev?.Post || {}), alimentos, puesto_solo: esElPuesto } }));
                    toast.success(`«${titulo}» puesto en el post`);
                }} />

            {preguntaPeriFav && (
                <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
                    role="dialog" aria-modal="true" aria-labelledby="pregunta-peri-fav" data-testid="pregunta-peri-favorito">
                    <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl p-5">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-600 dark:text-emerald-400 mb-1">
                            {preguntaPeriFav.tipo === 'nuevo'
                                ? (preguntaPeriFav.peri === 'post' ? 'Post guardado' : 'Intra guardado')
                                : (preguntaPeriFav.peri === 'post' ? 'Has cambiado el post' : 'Has cambiado el intra')}
                        </p>
                        <h2 id="pregunta-peri-fav" className="font-heading text-xl font-bold text-foreground leading-tight mb-2">
                            {preguntaPeriFav.tipo === 'nuevo'
                                ? `¿Dejamos este ${preguntaPeriFav.peri === 'post' ? 'postentreno' : 'intraentreno'} como favorito para que te salga siempre?`
                                : '¿Lo dejamos así a partir de ahora?'}
                        </h2>
                        <p className="text-sm text-muted-foreground mb-4">
                            {preguntaPeriFav.tipo === 'nuevo'
                                ? 'Si tus macros cambian en el futuro, lo ajustamos sin problema.'
                                : (() => {
                                    const ids = (l) => new Set((l || []).map((a) => a.alimento_id));
                                    const nuevos = ids(preguntaPeriFav.alimentos);
                                    const viejos = ids(preguntaPeriFav.antes);
                                    const nombres = (l, fuera) => (l || []).filter((a) => !fuera.has(a.alimento_id))
                                        .map((a) => a.nombre).join(', ');
                                    const quitados = nombres(preguntaPeriFav.antes, nuevos);
                                    const puestos = nombres(preguntaPeriFav.alimentos, viejos);
                                    return [quitados && `Antes tenías ${quitados}.`, puestos && `Ahora pondrás ${puestos}.`]
                                        .filter(Boolean).join(' ') || 'Cambian las cantidades.';
                                })()}
                        </p>
                        <div className="grid gap-2">
                            <button type="button" onClick={() => contestarPeriFav(true)} data-testid="peri-fav-si"
                                className="w-full py-3 rounded-xl bg-brand text-white font-bold text-sm hover:opacity-90">
                                {preguntaPeriFav.tipo === 'nuevo' ? 'Sí, déjalo puesto' : 'Sí, a partir de ahora'}
                            </button>
                            <button type="button" onClick={() => contestarPeriFav(false)} data-testid="peri-fav-no"
                                className="w-full py-3 rounded-xl border border-border text-foreground font-bold text-sm hover:bg-muted">
                                {preguntaPeriFav.tipo === 'nuevo' ? 'Solo para hoy' : 'Solo por hoy'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Repeat Meal Modal */}
            <RepeatMealModal
                open={repeatMealModal.open}
                mealKey={repeatMealModal.mealKey}
                onClose={() => {
                    setRepeatMealModal({ open: false, mealKey: null });
                    setSelectedDietForRepeat(null);
                }}
                recentDiets={recentDiets}
                mealInfo={mealInfo}
                formatDate={formatDate}
                /* El día viaja como argumento, no por el estado: leerlo del estado en el
                   mismo tick devolvía el del intento anterior (punto 4.3). */
                onCopyMeal={(sourceMealKey, sourceDiet, opciones) => copyMealFromDay(sourceMealKey, sourceDiet, opciones)}
            />

            {/* Copy Diet Modal */}
            <CopyDietModal
                open={copyModalOpen}
                onClose={() => setCopyModalOpen(false)}
                copyDate={copyDate}
                setCopyDate={setCopyDate}
                onCopy={copyDiet}
                currentDateFormatted={formatDate(currentDate)}
            />

            {/* Dietas favoritas */}
            <FavoritesModal
                open={favoritesModalOpen}
                onClose={() => setFavoritesModalOpen(false)}
                favorites={dietFavorites}
                onSave={saveDietFavorite}
                onApply={(fav, opciones) => applyDietFavorite(fav, { ...(opciones || {}), talCual: true })}
                onDelete={deleteDietFavorite}
                onRename={renombrarDietFavorite}
                tipoDia={tipoDia}
                // Para no dejar guardar un día sin comidas como favorita.
                diaVacio={diaVacio}
                // Y para que el aviso de antes de aplicar diga cuántas comidas se pierden.
                comidasDelDia={comidasDelDia}
                periDelDia={periDelDia}
            />

            {/* «USAR EN UNA COMIDA»: en cuál se pone el menú del ranking. */}
            {menuParaUsar && (
                <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
                    role="dialog" aria-modal="true" aria-labelledby="usar-menu-titulo"
                    onClick={(e) => { if (e.target === e.currentTarget) setMenuParaUsar(null); }}
                    data-testid="usar-menu-en">
                    <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl p-5">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand mb-1">Usar en una comida</p>
                        <h2 id="usar-menu-titulo" className="font-heading text-xl font-bold text-foreground leading-tight mb-1">
                            {menuParaUsar.titulo}
                        </h2>
                        <p className="text-sm text-muted-foreground mb-4">
                            ¿En qué comida lo pones? Se cuadra a los macros de esa comida.
                        </p>
                        <div className="space-y-2">
                            {getMealOrder().filter((k) => !['Intra', 'Post'].includes(k)).map((k) => {
                                const n = (mealsData[k]?.alimentos || []).length;
                                return (
                                    <button key={k} type="button" onClick={() => usarMenuEn(k)}
                                        data-testid={`usar-menu-en-${k}`}
                                        className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-border hover:border-brand hover:bg-brand/5 text-left">
                                        <span className="font-semibold text-foreground whitespace-nowrap">{mealInfo?.[k]?.name || k}</span>
                                        <span className="text-xs text-muted-foreground">
                                            {n ? `lleva ${n} ${n === 1 ? 'alimento' : 'alimentos'}, se sustituye` : 'vacía'}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        <button type="button" onClick={() => setMenuParaUsar(null)}
                            className="w-full mt-3 py-2.5 text-sm font-semibold text-muted-foreground hover:text-foreground">
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            <FavoritasDeComida
                open={favComidaModal.open}
                onClose={() => setFavComidaModal({ open: false, mealKey: null })}
                mealKey={favComidaModal.mealKey}
                mealLabel={mealInfo?.[favComidaModal.mealKey]?.name || 'esta comida'}
                favorites={comidaFavoritas}
                titulo={favComidaModal.mealKey === 'Post' ? 'Postentrenos favoritos' : 'Comidas favoritas'}
                fijas={favComidaModal.mealKey === 'Post' && (profile?.peri_favorito?.post?.alimentos || []).length
                    ? [{ id: 'peri-favorito-post', name: 'Tu postentreno de siempre', fija: true,
                         comida_origen: 'Post', alimentos: profile.peri_favorito.post.alimentos }]
                    : []}
                comidaLlena={((mealsData[favComidaModal.mealKey]?.alimentos) || []).length > 0}
                onSave={guardarComidaFavorita}
                onApply={aplicarComidaFavorita}
                onDelete={borrarComidaFavorita}
            />

            {/* Diet Calendar Modal */}
            <DietCalendar
                open={calendarOpen}
                onClose={() => setCalendarOpen(false)}
                onSelectDate={(date) => setCurrentDate(date)}
                api={api}
                // El día que se está mirando, para que el calendario lo marque y se abra en
                // su mes: el calendario se abre para moverse, y hay que saber de dónde.
                abierto={currentDate}
            />
        </div>
    );
};

export default NutritionPage;
